import { STRAPI_URL, flattenStrapiEntry, strapiAuthHeaders, parseErrorMessage } from './strapi';
import type { StrapiManagedUserRow } from './strapiUsers';
import {
  letterCountBySamplingPlanFromLetters,
  pickCanonicalSamplingPlans,
} from '../sampling/samplingPlanCanonical';
import {
  airGroupFromSamplingPlan,
  buildJenisByQsiFromPlans,
  buildJenisByQsiFromSampleLines,
  buildQsiUpdatedAtByDocIdFromPlans,
  buildQsiUpdatedAtByDocIdFromSampleLines,
  effectiveBidangLabelFromSamplingPlan,
  mergeJenisIndexes,
  mergeUpdatedAtIndexes,
} from '../sampling/samplingPlanBidang';
import {
  normalizeDynamicRow,
  strapiCreateDynamicEntity,
  strapiDeleteDynamicEntity,
  strapiListDynamicEntityPage,
  type DynamicEntityRow,
} from './strapiDynamicEntity';

export type StrapiCustomerRow = {
  documentId: string;
  customer_number: string;
  name: string;
  address?: string | null;
  alamat_lhu?: string | null;
  no_telp_finance?: string | null;
  phone?: string | null;
  email?: string | null;
  active_status?: 'active' | 'inactive' | 'prospect' | 'blocked' | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
  office_phone?: string | null;
  contact_name?: string | null;
  mobile_wa?: string | null;
  notes?: string | null;
  customer_type?: string | null;
  title_prefix?: 'MR' | 'MRS' | 'CV' | 'PT' | 'LAINNYA' | null;
  npwp?: string | null;
  fax?: string | null;
  job_title?: string | null;
  work_email?: string | null;
  discount?: number | null;
  audit_created_by?: string | null;
  audit_updated_by?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type StrapiQuotationUserStub = {
  documentId: string;
  /** Strapi users-permissions numeric id (untuk mencocokkan session user). */
  id?: number | null;
  username: string;
  email: string;
};

export type StrapiQuotationOtherCostRow = {
  documentId: string;
  description: string;
  quantity: number;
  cost_price: number | null;
  quotation_group?: { documentId: string } | null;
};

export type CompanyBranchJobCode = 'GL' | 'GLJ' | 'GLM';

export type StrapiCompanyBranchRow = {
  documentId: string;
  name: string;
  address: string | null;
  job_code: CompanyBranchJobCode;
};

export function formatCompanyBranchLabel(
  branch: StrapiCompanyBranchRow | null | undefined,
): string {
  if (!branch?.name?.trim()) return '—';
  const name = branch.name.trim();
  const code = branch.job_code?.trim();
  return code ? `${name} (${code})` : name;
}

function normalizeCompanyBranchEntry(raw: Record<string, unknown>): StrapiCompanyBranchRow {
  const src = flattenStrapiEntry(raw);
  const jc = String(src.job_code ?? 'GL');
  const job_code: CompanyBranchJobCode =
    jc === 'GLJ' || jc === 'GLM' ? jc : 'GL';
  return {
    documentId: String(src.documentId ?? raw.documentId ?? ''),
    name: String(src.name ?? ''),
    address: (src.address as string) ?? null,
    job_code,
  };
}

/** Customer fields needed to show/edit alamat + PIC on sampling WO surfaces. */
export type StrapiCustomerAddressMini = {
  documentId: string;
  customer_number: string;
  name: string;
  address?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
  contact_name?: string | null;
  job_title?: string | null;
  email?: string | null;
  work_email?: string | null;
};

export type StrapiQuotationGroupRow = {
  documentId: string;
  recurring_type?: 'weekly' | 'monthly' | 'yearly' | null;
  recurring_interval?: number | null;
  recurring_count_override?: number | null;
  recurring_start_date?: string | null;
  /** Pelanggan grup sampel (penawaran konsultan). Lengkap, untuk cek kelengkapan di grid. */
  customer?: StrapiCustomerRow | null;
  quotation_recurring_events?: StrapiQuotationRecurringEventRow[];
};

export type StrapiQuotationRecurringEventRow = {
  documentId: string;
  event_index: number;
  scheduled_date: string;
  sampling_schedule?: {
    documentId?: string;
    work_order?: { documentId?: string; job_number?: string | null } | null;
  } | null;
};

export type StrapiQuotationRow = {
  documentId: string;
  quotation_number: string;
  preorder: boolean;
  is_contract: boolean;
  is_consultant_quote: boolean;
  contract_period_start_date?: string | null;
  contract_period_end_date?: string | null;
  /** @deprecated Legacy; dipakai hanya untuk resolve end date data lama. */
  contract_period_long?: number | null;
  /** @deprecated Legacy; dipakai hanya untuk resolve end date data lama. */
  contract_period_type?: 'week' | 'month' | 'year' | null;
  sample_pickup_method: 'sampling' | 'diantar';
  discount_amount: number;
  is_ppn: boolean;
  assignee_user?: StrapiQuotationUserStub | null;
  approved_by_user?: StrapiQuotationUserStub | null;
  progress_status?: string | null;
  company_branch?: StrapiCompanyBranchRow | null;
  work_order_created?: boolean;
  sampling_plans_ready?: boolean;
  /** Ringkas status penjadwalan sampling (populate list quotation). */
  sampling_schedules?: Array<{
    documentId: string;
    progress_status?: string | null;
    work_order?: { documentId?: string; job_number?: string | null } | null;
  }>;
  /** Ringkas sampling plan + WO (populate list quotation, kontrak/konsultan). */
  sampling_plans?: Array<{
    documentId: string;
    quotation_group?: { documentId?: string } | null;
    work_order?: {
      documentId?: string;
      job_number?: string | null;
      laboratory_testing_status?: string | null;
    } | null;
  }>;
  remark_edit?: string | null;
  remark_edit_review?: string | null;
  /** Parallel to main `progress_status`: none | pending | approved | rejected. */
  edit_request_status?: 'none' | 'pending' | 'approved' | 'rejected' | null;
  remark_approval?: string | null;
  request_date?: string | null;
  spv_approved_date?: string | null;
  manajer_approved_date?: string | null;
  customer_approved_date?: string | null;
  quotation_date?: string | null;
  delivery_date?: string | null;
  receipt_date?: string | null;
  payment_date?: string | null;
  term_of_payment?: boolean;
  term_of_payment_date?: string | null;
  term_of_payment_note?: string | null;
  createdAt?: string;
  updatedAt?: string;
  /** Relasi pelanggan lengkap — dipakai grid untuk cek kelengkapan data. */
  customer?: StrapiCustomerRow | null;
  /** Quotation-bound sample lines (when populated via `strapiGetQuotation`). */
  sample_informations?: StrapiSampleInformationRow[];
  /**
   * Which REST collection persists these lines (`strapiGetQuotation` prefers `quotation_sample_informations`).
   * Bundle-created quotations use `quotation-sample-informations`; legacy lines use `sample-informations`.
   */
  quotation_sample_lines_api?: 'quotation-sample-informations' | 'sample-informations';
  /** Biaya lain (when populated via `strapiGetQuotation`). */
  quotation_other_costs?: StrapiQuotationOtherCostRow[];
  /** Consultant/contract groups (populated via `strapiGetQuotation`). */
  quotation_groups?: StrapiQuotationGroupRow[];
};

type StrapiListMeta = {
  pagination?: { page?: number; pageSize?: number; pageCount?: number; total?: number };
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function customerAddressMiniFromRow(c: StrapiCustomerRow): StrapiCustomerAddressMini {
  return {
    documentId: c.documentId,
    customer_number: c.customer_number,
    name: c.name,
    address: c.address,
    city: c.city,
    province: c.province,
    postal_code: c.postal_code,
    contact_name: c.contact_name,
    job_title: c.job_title,
    email: c.email,
    work_email: c.work_email,
  };
}

function normalizeCustomerEntry(raw: Record<string, unknown>): StrapiCustomerRow {
  const src = flattenStrapiEntry(raw);
  return {
    documentId: String(src.documentId ?? raw.documentId ?? ''),
    customer_number: String(src.customer_number ?? ''),
    name: String(src.name ?? ''),
    address: (src.address as string) ?? null,
    alamat_lhu: (src.alamat_lhu as string) ?? null,
    no_telp_finance: (src.no_telp_finance as string) ?? null,
    phone: (src.phone as string) ?? null,
    email: (src.email as string) ?? null,
    active_status: (src.active_status as StrapiCustomerRow['active_status']) ?? null,
    city: (src.city as string) ?? null,
    province: (src.province as string) ?? null,
    postal_code: (src.postal_code as string) ?? null,
    office_phone: (src.office_phone as string) ?? null,
    contact_name: (src.contact_name as string) ?? null,
    mobile_wa: (src.mobile_wa as string) ?? null,
    notes: (src.notes as string) ?? null,
    customer_type: (src.customer_type as string) ?? null,
    title_prefix: (src.title_prefix as StrapiCustomerRow['title_prefix']) ?? null,
    npwp: (src.npwp as string) ?? null,
    fax: (src.fax as string) ?? null,
    job_title: (src.job_title as string) ?? null,
    work_email: (src.work_email as string) ?? null,
    discount: src.discount != null ? Number(src.discount) : null,
    audit_created_by: (src.audit_created_by as string) ?? null,
    audit_updated_by: (src.audit_updated_by as string) ?? null,
    createdAt: (src.createdAt as string) ?? (src.created_at as string) ?? undefined,
    updatedAt: (src.updatedAt as string) ?? (src.updated_at as string) ?? undefined,
  };
}

function unwrapPopulateOne(v: unknown): Record<string, unknown> | null {
  if (!v) return null;
  if (typeof v === 'object' && v !== null && 'data' in (v as object)) {
    const d = (v as { data: unknown }).data;
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      return d as Record<string, unknown>;
    }
  }
  if (typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
  return null;
}

function unwrapPopulateMany(v: unknown): Record<string, unknown>[] {
  if (!v) return [];
  if (Array.isArray(v)) {
    return v.filter((x) => x && typeof x === 'object') as Record<string, unknown>[];
  }
  if (typeof v === 'object' && v !== null && 'data' in v) {
    const d = (v as { data: unknown }).data;
    if (Array.isArray(d)) {
      return d.filter((x) => x && typeof x === 'object') as Record<string, unknown>[];
    }
  }
  return [];
}

function normalizeQuotationUserStub(raw: Record<string, unknown> | null): StrapiQuotationUserStub | null {
  if (!raw) return null;
  const src = flattenStrapiEntry(raw);
  const documentId = String(src.documentId ?? raw.documentId ?? '').trim();
  const rawId = src.id ?? raw.id;
  const numericId =
    rawId != null && Number.isFinite(Number(rawId)) ? Number(rawId) : null;
  const username = String(src.username ?? '');
  const email = String(src.email ?? '');
  if (!documentId && numericId == null && !username && !email) return null;
  return {
    documentId: documentId || (numericId != null ? String(numericId) : ''),
    id: numericId,
    username,
    email,
  };
}

function normalizeQuotationOtherCostEntry(raw: Record<string, unknown>): StrapiQuotationOtherCostRow {
  const src = flattenStrapiEntry(raw);
  const groupRaw = unwrapPopulateOne(src.quotation_group);
  const groupDocId = groupRaw ? String((groupRaw as Record<string, unknown>).documentId ?? '').trim() : '';
  return {
    documentId: String(src.documentId ?? raw.documentId ?? ''),
    description: String(src.description ?? ''),
    quantity: src.quantity != null && Number.isFinite(Number(src.quantity)) ? Number(src.quantity) : 1,
    cost_price: src.cost_price != null && Number.isFinite(Number(src.cost_price)) ? Number(src.cost_price) : null,
    quotation_group: groupDocId ? { documentId: groupDocId } : null,
  };
}

/** Prefer quotation bundle lines on `quotation_sample_informations`; otherwise legacy `sample_informations`. */
function unwrapQuotationSampleLinesRaw(src: Record<string, unknown>): Record<string, unknown>[] {
  const q = unwrapPopulateMany(src.quotation_sample_informations);
  if (q.length) return q as Record<string, unknown>[];
  return unwrapPopulateMany(src.sample_informations) as Record<string, unknown>[];
}

function quotationSampleLinesRestCollection(
  src: Record<string, unknown>,
): 'quotation-sample-informations' | 'sample-informations' | undefined {
  if (unwrapPopulateMany(src.quotation_sample_informations).length) return 'quotation-sample-informations';
  if (unwrapPopulateMany(src.sample_informations).length) return 'sample-informations';
  return undefined;
}

function normalizeQuotationGroupEntry(raw: Record<string, unknown> | null): StrapiQuotationGroupRow | null {
  if (!raw) return null;
  const src = flattenStrapiEntry(raw);
  const documentId = String(src.documentId ?? raw.documentId ?? '').trim();
  if (!documentId) return null;
  const rt = src.recurring_type as string | undefined;
  const recurring_type =
    rt === 'weekly' || rt === 'monthly' || rt === 'yearly' ? rt : null;
  const recurring_interval =
    src.recurring_interval != null && Number.isFinite(Number(src.recurring_interval))
      ? Math.max(1, Number(src.recurring_interval))
      : null;
  const recurring_count_override =
    src.recurring_count_override != null && Number.isFinite(Number(src.recurring_count_override))
      ? Math.max(1, Number(src.recurring_count_override))
      : null;
  const recurring_start_date =
    typeof src.recurring_start_date === 'string' && src.recurring_start_date.trim()
      ? src.recurring_start_date
      : null;
  const eventsRaw = unwrapPopulateMany(src.quotation_recurring_events);
  const quotation_recurring_events: StrapiQuotationRecurringEventRow[] = [];
  for (const ev of eventsRaw) {
    const e = flattenStrapiEntry(ev);
    const documentId = String(e.documentId ?? ev.documentId ?? '').trim();
    if (!documentId) continue;
    const event_index = Number(e.event_index);
    const scheduled_date = String(e.scheduled_date ?? '').trim();
    if (!Number.isFinite(event_index) || !scheduled_date) continue;
    const schedule = unwrapPopulateOne(e.sampling_schedule);
    const scheduleWo = schedule ? unwrapPopulateOne(schedule.work_order) : null;
    quotation_recurring_events.push({
      documentId,
      event_index,
      scheduled_date,
      sampling_schedule: schedule
        ? {
            documentId: String(schedule.documentId ?? '').trim() || undefined,
            work_order: scheduleWo
              ? {
                  documentId: String(scheduleWo.documentId ?? '').trim() || undefined,
                  job_number: String(scheduleWo.job_number ?? '').trim() || null,
                }
              : null,
          }
        : null,
    });
  }
  quotation_recurring_events.sort((a, b) => a.event_index - b.event_index);
  const custRaw = unwrapPopulateOne(src.customer);
  const customer: StrapiQuotationGroupRow['customer'] = custRaw
    ? normalizeCustomerEntry(custRaw)
    : null;
  return {
    documentId,
    recurring_type,
    recurring_interval,
    recurring_count_override,
    recurring_start_date,
    customer,
    ...(quotation_recurring_events.length ? { quotation_recurring_events } : {}),
  };
}

function normalizeQuotationEntry(raw: Record<string, unknown>): StrapiQuotationRow {
  const src = flattenStrapiEntry(raw);
  const custRaw = unwrapPopulateOne(src.customer);
  const customer: StrapiQuotationRow['customer'] = custRaw ? normalizeCustomerEntry(custRaw) : null;
  const pickup = src.sample_pickup_method as string | undefined;
  const sample_pickup_method: StrapiQuotationRow['sample_pickup_method'] =
    pickup === 'diantar' ? 'diantar' : 'sampling';
  return {
    documentId: String(src.documentId ?? raw.documentId ?? ''),
    quotation_number: String(src.quotation_number ?? ''),
    preorder: Boolean(src.preorder),
    is_contract: Boolean(src.is_contract),
    is_consultant_quote: Boolean(src.is_consultant_quote),
    contract_period_start_date: (src.contract_period_start_date as string) ?? null,
    contract_period_end_date: (src.contract_period_end_date as string) ?? null,
    contract_period_long:
      src.contract_period_long != null && Number.isFinite(Number(src.contract_period_long))
        ? Number(src.contract_period_long)
        : null,
    contract_period_type: (() => {
      const t = src.contract_period_type as string | undefined;
      return t === 'week' || t === 'month' || t === 'year' ? t : null;
    })(),
    sample_pickup_method,
    discount_amount:
      src.discount_amount != null && Number.isFinite(Number(src.discount_amount))
        ? Math.max(0, Math.round(Number(src.discount_amount)))
        : 0,
    is_ppn: Boolean(src.is_ppn),
    assignee_user: normalizeQuotationUserStub(unwrapPopulateOne(src.assignee_user)),
    approved_by_user: normalizeQuotationUserStub(unwrapPopulateOne(src.approved_by_user)),
    progress_status: (src.progress_status as string) ?? null,
    company_branch: (() => {
      const br = unwrapPopulateOne(src.company_branch);
      return br ? normalizeCompanyBranchEntry(br) : null;
    })(),
    work_order_created: Boolean(src.work_order_created),
    sampling_plans_ready: Boolean(src.sampling_plans_ready),
    sampling_schedules: (() => {
      const rows = unwrapPopulateMany(src.sampling_schedules);
      if (!rows.length) return undefined;
      return rows
        .map((r) => {
          const wo = unwrapPopulateOne(r.work_order);
          return {
            documentId: String(r.documentId ?? '').trim(),
            progress_status: (r.progress_status as string) ?? null,
            work_order: wo
              ? {
                  documentId: String(wo.documentId ?? '').trim() || undefined,
                  job_number: String(wo.job_number ?? '').trim() || null,
                }
              : null,
          };
        })
        .filter((r) => r.documentId);
    })(),
    sampling_plans: (() => {
      const rows = unwrapPopulateMany(src.sampling_plans);
      if (!rows.length) return undefined;
      return rows
        .map((r) => {
          const wo = unwrapPopulateOne(r.work_order);
          const group = unwrapPopulateOne(r.quotation_group);
          const labRows = wo ? unwrapPopulateMany(wo.laboratory_results) : [];
          const labStatuses = labRows
            .map((lab) => String(lab.testing_status ?? '').trim().toLowerCase())
            .filter(Boolean);
          const laboratoryTestingStatus =
            labStatuses.length > 0 && labStatuses.every((status) => status === 'cetak' || status === 'lhu_rilis')
              ? 'cetak'
              : labStatuses[0] ?? null;
          return {
            documentId: String(r.documentId ?? '').trim(),
            quotation_group: group?.documentId ? { documentId: String(group.documentId).trim() } : null,
            work_order: wo
              ? {
                  documentId: String(wo.documentId ?? '').trim() || undefined,
                  job_number: String(wo.job_number ?? '').trim() || null,
                  laboratory_testing_status: laboratoryTestingStatus,
                }
              : null,
          };
        })
        .filter((r) => r.documentId);
    })(),
    remark_edit: (src.remark_edit as string) ?? null,
    remark_edit_review: (src.remark_edit_review as string) ?? null,
    edit_request_status: (() => {
      const raw = String(src.edit_request_status ?? 'none').trim();
      if (raw === 'pending' || raw === 'approved' || raw === 'rejected' || raw === 'none') {
        return raw;
      }
      return 'none';
    })(),
    remark_approval: (src.remark_approval as string) ?? null,
    request_date: (src.request_date as string) ?? null,
    spv_approved_date: (src.spv_approved_date as string) ?? null,
    manajer_approved_date: (src.manajer_approved_date as string) ?? null,
    customer_approved_date: (src.customer_approved_date as string) ?? null,
    quotation_date: (src.quotation_date as string) ?? null,
    delivery_date: (src.delivery_date as string) ?? null,
    receipt_date: (src.receipt_date as string) ?? null,
    payment_date: (src.payment_date as string) ?? null,
    term_of_payment: Boolean(src.term_of_payment),
    term_of_payment_date: (src.term_of_payment_date as string) ?? null,
    term_of_payment_note: (src.term_of_payment_note as string) ?? null,
    createdAt: (src.createdAt as string) ?? (src.created_at as string) ?? undefined,
    updatedAt: (src.updatedAt as string) ?? (src.updated_at as string) ?? undefined,
    customer,
    sample_informations: (() => {
      const si = unwrapQuotationSampleLinesRaw(src);
      if (!si.length) return undefined;
      return si.map((r) => normalizeSampleInformationEntry(r as Record<string, unknown>));
    })(),
    quotation_sample_lines_api: quotationSampleLinesRestCollection(src),
    quotation_other_costs: (() => {
      const xs = unwrapPopulateMany(src.quotation_other_costs);
      if (!xs.length) return undefined;
      return xs.map((r) => normalizeQuotationOtherCostEntry(r as Record<string, unknown>));
    })(),
    quotation_groups: (() => {
      const xs = unwrapPopulateMany(src.quotation_groups);
      if (!xs.length) return undefined;
      return xs
        .map((r) => normalizeQuotationGroupEntry(r as Record<string, unknown>))
        .filter((g): g is StrapiQuotationGroupRow => g != null);
    })(),
  };
}

function parseListBody(
  res: Response,
  body: unknown,
): { rows: Record<string, unknown>[]; meta: StrapiListMeta } {
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  const b = asRecord(body);
  if (!b) {
    return { rows: [], meta: {} };
  }
  if (Array.isArray(b)) {
    return { rows: b as Record<string, unknown>[], meta: {} };
  }
  if (Array.isArray(b.data)) {
    return { rows: b.data as Record<string, unknown>[], meta: (b.meta as StrapiListMeta) || {} };
  }
  return { rows: [], meta: {} };
}

export type CustomersListResult = { rows: StrapiCustomerRow[]; total: number };
export type QuotationsListResult = { rows: StrapiQuotationRow[]; total: number };

/** `GET /api/customers`: Strapi `$and`; empty strings = no filter. Telepon mencocokkan salah satu field. */
export type CustomerListFilters = {
  customerNumber: string;
  name: string;
  email: string;
  phone: string;
  /** '' = semua status */
  status: string;
};

export const emptyCustomerListFilters = Object.freeze<CustomerListFilters>({
  customerNumber: '',
  name: '',
  email: '',
  phone: '',
  status: '',
});

function appendCustomerListFilters(params: URLSearchParams, f: CustomerListFilters): number {
  const nz = (s: string) => s.trim();
  let i = 0;
  if (nz(f.customerNumber)) {
    params.append(`filters[$and][${i}][customer_number][$containsi]`, nz(f.customerNumber));
    i += 1;
  }
  if (nz(f.name)) {
    params.append(`filters[$and][${i}][name][$containsi]`, nz(f.name));
    i += 1;
  }
  if (nz(f.email)) {
    params.append(`filters[$and][${i}][email][$containsi]`, nz(f.email));
    i += 1;
  }
  if (nz(f.phone)) {
    const q = nz(f.phone);
    params.append(`filters[$and][${i}][$or][0][phone][$containsi]`, q);
    params.append(`filters[$and][${i}][$or][1][mobile_wa][$containsi]`, q);
    params.append(`filters[$and][${i}][$or][2][office_phone][$containsi]`, q);
    i += 1;
  }
  if (nz(f.status)) {
    params.append(`filters[$and][${i}][active_status][$eq]`, nz(f.status));
    i += 1;
  }
  return i;
}

/**
 * PIC marketing: customer yang punya quotation dengan `assignee_user` = user yang login.
 * Dipanggil hanya untuk user type itu; pemanggil lain mengosongkan `picAssignee`.
 */
export function appendCustomersPicAssigneeFilter(
  params: URLSearchParams,
  startIndex: number,
  picAssignee?: QuotationsListPicAssignee | null,
): number {
  if (!quotationsListPicAssigneeReady(picAssignee)) return startIndex;
  const docId = picAssignee?.documentId?.trim() ?? '';
  const id = picAssignee?.id;
  const hasId = id != null && Number.isFinite(id);
  const i = startIndex;
  if (docId && hasId) {
    params.append(
      `filters[$and][${i}][$or][0][quotations][assignee_user][documentId][$eq]`,
      docId,
    );
    params.append(`filters[$and][${i}][$or][1][quotations][assignee_user][id][$eq]`, String(id));
    return i + 1;
  }
  if (docId) {
    params.append(`filters[$and][${i}][quotations][assignee_user][documentId][$eq]`, docId);
    return i + 1;
  }
  params.append(`filters[$and][${i}][quotations][assignee_user][id][$eq]`, String(id));
  return i + 1;
}

export async function strapiListCustomers(
  jwt: string,
  options: {
    filters?: CustomerListFilters;
    /** Strapi pagination (1-based). */
    page?: number;
    pageSize?: number;
    /** Hanya diisi untuk PIC marketing: batasi ke customer pada quotation yang di-assign. */
    picAssignee?: QuotationsListPicAssignee | null;
  } = {},
): Promise<CustomersListResult> {
  const filters = options.filters ?? emptyCustomerListFilters;
  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? 200;
  const params = new URLSearchParams({
    'pagination[pageSize]': String(pageSize),
    'pagination[page]': String(page),
    sort: 'customer_number:asc',
  });
  const nextFilterIndex = appendCustomerListFilters(params, filters);
  appendCustomersPicAssigneeFilter(params, nextFilterIndex, options.picAssignee);
  const res = await fetch(`${STRAPI_URL}/api/customers?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows, meta } = parseListBody(res, body);
  const total = meta.pagination?.total ?? rows.length;
  return { rows: rows.map((r) => normalizeCustomerEntry(r)), total };
}

/** Server-side filter: nama atau nomor pelanggan mengandung `q` (tanpa sensitif huruf besar/kecil). */
export async function strapiSearchCustomers(
  jwt: string,
  q: string,
  pageSize = 50,
): Promise<CustomersListResult> {
  const query = q.trim();
  const params = new URLSearchParams({
    'pagination[pageSize]': String(pageSize),
    'pagination[page]': '1',
    sort: 'customer_number:asc',
  });
  if (query.length > 0) {
    params.append('filters[$or][0][name][$containsi]', query);
    params.append('filters[$or][1][customer_number][$containsi]', query);
  }
  const res = await fetch(`${STRAPI_URL}/api/customers?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows, meta } = parseListBody(res, body);
  const total = meta.pagination?.total ?? rows.length;
  return { rows: rows.map((r) => normalizeCustomerEntry(r)), total };
}

export type BakuMutuSearchListResult = { rows: DynamicEntityRow[]; total: number };

/** Pencarian server: `kode` atau `judul` mengandung `q` (case-insensitive). */
export async function strapiSearchBakuMutus(
  jwt: string,
  q: string,
  pageSize = 50,
): Promise<BakuMutuSearchListResult> {
  const query = q.trim();
  const params = new URLSearchParams({
    'pagination[pageSize]': String(pageSize),
    'pagination[page]': '1',
    'fields[0]': 'documentId',
    'fields[1]': 'kode',
    'fields[2]': 'nama_baku_mutu',
    'fields[3]': 'judul',
    sort: 'kode:asc',
  });
  if (query.length > 0) {
    params.append('filters[$or][0][kode][$containsi]', query);
    params.append('filters[$or][1][judul][$containsi]', query);
  }
  const res = await fetch(`${STRAPI_URL}/api/baku-mutus?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows, meta } = parseListBody(res, body);
  const total = meta.pagination?.total ?? rows.length;
  return { rows: rows.map((r) => normalizeDynamicRow(r as Record<string, unknown>)), total };
}

/**
 * Strapi / proxies may occasionally echo a relation-count shape `{ count: n }` where the UI expects a scalar.
 * Coerce to a finite integer or null.
 */
export function coercePositiveIntField(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw) && 'count' in raw) {
    const c = (raw as { count: unknown }).count;
    if (typeof c === 'number' && Number.isFinite(c)) return c;
    if (typeof c === 'string' && c.trim() !== '') {
      const n = Number(c.trim());
      return Number.isFinite(n) ? n : null;
    }
  }
  return null;
}

function scalarStringFromStrapiField(raw: unknown): string | null {
  if (raw == null) return null;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') return null;
  return String(raw);
}

/** Token `documentId` parameter uji (`;` / `,` di UI, atau array / JSON) — relasi M2M `parameter_ujis`. */
export function parseStoredParameterUjiDocumentIds(raw: string | null | undefined | unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    const ids: string[] = [];
    for (const item of raw) {
      if (typeof item === 'string') {
        const t = item.trim();
        if (t) ids.push(t);
        continue;
      }
      if (item && typeof item === 'object') {
        const o = item as Record<string, unknown>;
        const flat = flattenStrapiEntry(o);
        const id = String(flat.documentId ?? o.documentId ?? '').trim();
        if (id) ids.push(id);
      }
    }
    return ids;
  }
  if (typeof raw !== 'string') return [];
  const s = raw.trim();
  if (!s) return [];
  if (s.startsWith('[')) {
    try {
      const parsed = JSON.parse(s) as unknown;
      if (Array.isArray(parsed)) return parseStoredParameterUjiDocumentIds(parsed);
    } catch {
      /* legacy plain string */
    }
  }
  return s
    .split(/[;,]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

/** Heuristik Strapi documentId (cukup untuk membedakan teks bebas lama vs id). */
export function isLikelyStrapiDocumentId(s: string): boolean {
  const t = s.trim();
  if (t.length < 18 || t.length > 40) return false;
  return /^[a-z0-9]+$/i.test(t);
}

/** Ambil nama/simbol untuk sekumpulan `parameter-uji` (beberapa batch jika banyak). */
export async function strapiFetchParameterUjisByDocumentIds(
  jwt: string,
  documentIds: string[],
): Promise<Map<string, { nama_parameter: string | null; simbol: string | null }>> {
  const ids = [...new Set(documentIds.map((x) => x.trim()).filter(Boolean))];
  const out = new Map<string, { nama_parameter: string | null; simbol: string | null }>();
  if (!ids.length) return out;
  const chunkSize = 25;
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    const params = new URLSearchParams({
      'pagination[pageSize]': String(Math.max(chunk.length, 1)),
      'pagination[page]': '1',
      'fields[0]': 'documentId',
      'fields[1]': 'nama_parameter',
      'fields[2]': 'simbol',
    });
    chunk.forEach((id, idx) => {
      params.append(`filters[$or][${idx}][documentId][$eq]`, id);
    });
    const res = await fetch(`${STRAPI_URL}/api/parameter-ujis?${params}`, { headers: strapiAuthHeaders(jwt) });
    const body = (await res.json().catch(() => ({}))) as unknown;
    const { rows } = parseListBody(res, body);
    for (const r of rows) {
      const f = flattenStrapiEntry(r as Record<string, unknown>);
      const did = String(f.documentId ?? '').trim();
      if (!did) continue;
      out.set(did, {
        nama_parameter: (f.nama_parameter as string) ?? null,
        simbol: (f.simbol as string) ?? null,
      });
    }
  }
  return out;
}

function parseCustomerOneResponse(res: Response, body: unknown): StrapiCustomerRow {
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  const b = asRecord(body);
  const data = b?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Invalid Strapi customer response');
  }
  return normalizeCustomerEntry(data as Record<string, unknown>);
}

/** Writable customer fields for POST/PUT (`data` object body). Omit nulls optional. */
export type StrapiCustomerWriteData = Partial<Record<keyof StrapiCustomerRow, unknown>> &
  Pick<StrapiCustomerRow, 'customer_number' | 'name'>;

/** `POST /api/customers`: `customer_number` is assigned by Strapi (`CUS-#####`). */
export type StrapiCustomerCreateData = Omit<
  Partial<Record<keyof StrapiCustomerRow, unknown>>,
  'customer_number'
> &
  Pick<StrapiCustomerRow, 'name'>;

export async function strapiGetCustomer(jwt: string, documentId: string): Promise<StrapiCustomerRow> {
  const res = await fetch(`${STRAPI_URL}/api/customers/${encodeURIComponent(documentId)}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseCustomerOneResponse(res, body);
}

export async function strapiCreateCustomer(
  jwt: string,
  data: StrapiCustomerCreateData,
): Promise<StrapiCustomerRow> {
  const res = await fetch(`${STRAPI_URL}/api/customers`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseCustomerOneResponse(res, body);
}

export async function strapiUpdateCustomer(
  jwt: string,
  documentId: string,
  data: Partial<StrapiCustomerWriteData>,
): Promise<StrapiCustomerRow> {
  const res = await fetch(`${STRAPI_URL}/api/customers/${encodeURIComponent(documentId)}`, {
    method: 'PUT',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseCustomerOneResponse(res, body);
}

export async function strapiDeleteCustomer(jwt: string, documentId: string): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/customers/${encodeURIComponent(documentId)}`, {
    method: 'DELETE',
    headers: strapiAuthHeaders(jwt),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(body, res.status));
  }
}

/** Payload for `POST /api/quotations` (`data`). `quotation_number` defaults server-side when omitted. */
export type StrapiQuotationCreateData = {
  quotation_number?: string;
  customer: { connect: string[] };
  preorder?: boolean;
  is_contract?: boolean;
  is_consultant_quote?: boolean;
  contract_period_start_date?: string | null;
  contract_period_end_date?: string | null;
  sample_pickup_method?: 'sampling' | 'diantar';
  assignee_user?: number | { connect: string[] } | null;
  approved_by_user?: number | { connect: string[] } | null;
  discount_amount?: number | null;
  is_ppn?: boolean;
  progress_status?:
    | 'draft'
    | 'sent'
    | 'submitted'
    | 'approved'
    | 'need_manajer_approval'
    | 'manajer_approved'
    | 'customer_approved'
    | 'need_attention'
    | 'edit_requested'
    | 'customer_detail'
    | 'rejected'
    | 'rejected_by_manajer'
    | 'in_progress'
    | 'completed'
    | 'cancelled'
    | 'paid';
  company_branch?: { connect: string[] } | null;
  work_order_created?: boolean;
  sampling_plans_ready?: boolean;
  remark_edit?: string | null;
  remark_edit_review?: string | null;
  edit_request_status?: 'none' | 'pending' | 'approved' | 'rejected' | null;
  remark_approval?: string | null;
  request_date?: string | null;
  spv_approved_date?: string | null;
  manajer_approved_date?: string | null;
  customer_approved_date?: string | null;
  quotation_date?: string | null;
  delivery_date?: string | null;
  receipt_date?: string | null;
  payment_date?: string | null;
  term_of_payment?: boolean;
  term_of_payment_date?: string | null;
  term_of_payment_note?: string | null;
};

/** Payload for `PUT /api/quotations/:documentId`; omit fields you do not change. */
export type StrapiQuotationUpdateData = Partial<StrapiQuotationCreateData>;

const quotationPopulateCompanyBranchQs =
  '&populate[company_branch][fields][0]=documentId' +
  '&populate[company_branch][fields][1]=name' +
  '&populate[company_branch][fields][2]=address' +
  '&populate[company_branch][fields][3]=job_code';

export type CompanyBranchesListResult = { rows: StrapiCompanyBranchRow[]; total: number };

/** Daftar cabang perusahaan (master). */
export async function strapiListCompanyBranches(
  jwt: string,
  pageSize = 200,
): Promise<CompanyBranchesListResult> {
  const params = new URLSearchParams({
    'pagination[pageSize]': String(pageSize),
    'pagination[page]': '1',
    sort: 'name:asc',
  });
  const res = await fetch(`${STRAPI_URL}/api/company-branches?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows, meta } = parseListBody(res, body);
  const total = meta.pagination?.total ?? rows.length;
  return {
    rows: rows.map((r) => normalizeCompanyBranchEntry(r)),
    total,
  };
}

export type StrapiSamplingPlanCreateData = {
  work_order: { connect: string[] };
  quotation?: { connect: string[] };
  quotation_group?: { connect: string[] };
  quotation_sample_information?: { connect: string[] };
  line_index?: number;
  sample_point_count: number;
  /** Checklist form shared across all jenis in this matrix group (air / non_air). */
  air_group?: 'air' | 'non_air';
  progress_status?: 'draft' | 'submitted' | 'approved';
  bidang_pengujian?: { connect: string[] };
  tujuan_pengambilan?: { connect: string[] };
  pengawetan?: { connect: string[] };
  larutan_penjerap?: { connect: string[] };
  peralatan_pengambilan_contoh?: { connect: string[] };
  peralatan_pengukuran_lapangan?: { connect: string[] };
  peralatan_pendukung?: { connect: string[] };
  peralatan_k3?: { connect: string[] };
  cara_pengambilan_contoh?: { connect: string[] };
  pengendalian_mutu_lapangan?: { connect: string[] };
  pengamanan_transportasi_contoh?: { connect: string[] };
  administrasi_contoh?: { connect: string[] };
  petugas_pengambil_contoh?: number | { connect: string[] };
  disahkan_oleh_manajer_teknis?: number | { connect: string[] };
  metode_sampling?: { connect: string[] };
  sni_methods?: { connect: string[] };
  jumlah_wadah_contoh_uji_air?: string;
  jumlah_wadah_contoh_uji_padatan?: string;
  peta_sketsa_lokasi_files?: File[];
};

export async function strapiCreateSamplingPlan(
  jwt: string,
  data: StrapiSamplingPlanCreateData,
): Promise<DynamicEntityRow> {
  const { peta_sketsa_lokasi_files, ...payloadData } = data;
  const files = peta_sketsa_lokasi_files?.filter(Boolean) ?? [];
  const body =
    files.length > 0
      ? (() => {
          const form = new FormData();
          form.append('data', JSON.stringify(payloadData));
          files.forEach((file) => form.append('files.peta_sketsa_lokasi', file));
          return form;
        })()
      : JSON.stringify({ data: payloadData });
  const res = await fetch(`${STRAPI_URL}/api/sampling-plans`, {
    method: 'POST',
    headers:
      files.length > 0
        ? strapiAuthHeaders(jwt)
        : {
            ...(strapiAuthHeaders(jwt) as Record<string, string>),
            'Content-Type': 'application/json',
          },
    body,
  });
  const parsed = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(parsed, res.status));
  }
  const row = asRecord(parsed)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid Strapi sampling plan create response');
  return normalizeDynamicRow(row);
}

/** Set semua rencana sampling work order ke status approved (pra-survey). */
export async function strapiApproveSamplingPlansForWorkOrder(
  jwt: string,
  workOrderDocumentId: string,
): Promise<{ updated: number }> {
  const plans = await strapiListSamplingPlansForWorkOrder(jwt, workOrderDocumentId);
  if (!plans.length) {
    throw new Error('Tidak ada rencana sampling pada work order ini.');
  }
  let updated = 0;
  for (const plan of plans) {
    const documentId = String(plan.documentId ?? '').trim();
    if (!documentId) continue;
    if (String(plan.progress_status ?? '') === 'approved') continue;
    await strapiUpdateSamplingPlanChecklist(jwt, documentId, { progress_status: 'approved' });
    updated += 1;
  }
  return { updated };
}

export async function strapiListSamplingPlansForWorkOrder(
  jwt: string,
  workOrderDocumentId: string,
): Promise<DynamicEntityRow[]> {
  const params = new URLSearchParams({
    'pagination[pageSize]': '500',
    'pagination[page]': '1',
    sort: 'line_index:asc',
  });
  params.append('filters[work_order][documentId][$eq]', workOrderDocumentId);
  appendSamplingPlanChecklistPopulate(params);
  const res = await fetch(`${STRAPI_URL}/api/sampling-plans?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows } = parseListBody(res, body);
  return rows.map((row) => normalizeDynamicRow(row));
}

function appendReferensiMiniPopulate(params: URLSearchParams, path: string): void {
  const fields = ['documentId', 'kode_kategori', 'kode', 'deskripsi1'] as const;
  fields.forEach((field, index) => {
    params.append(`${path}[fields][${index}]`, field);
  });
}

function appendSamplingPlanChecklistPopulate(params: URLSearchParams): void {
  for (const field of [
    'tujuan_pengambilan',
    'pengawetan',
    'larutan_penjerap',
    'peralatan_pengambilan_contoh',
    'peralatan_pengukuran_lapangan',
    'peralatan_pendukung',
    'peralatan_k3',
    'cara_pengambilan_contoh',
    'pengendalian_mutu_lapangan',
    'pengamanan_transportasi_contoh',
    'administrasi_contoh',
  ] as const) {
    appendReferensiMiniPopulate(params, `populate[${field}]`);
  }

  params.append('populate[bidang_pengujian][fields][0]', 'documentId');
  params.append('populate[bidang_pengujian][fields][1]', 'nama_jenis');
  params.append('populate[bidang_pengujian][fields][2]', 'kode_jenis');
  params.append('populate[bidang_pengujian][populate][grouping][fields][0]', 'perencanaan_sampling');

  for (const field of ['petugas_pengambil_contoh', 'disahkan_oleh_manajer_teknis'] as const) {
    params.append(`populate[${field}][fields][0]`, 'documentId');
    params.append(`populate[${field}][fields][1]`, 'id');
    params.append(`populate[${field}][fields][2]`, 'username');
    params.append(`populate[${field}][fields][3]`, 'email');
  }

  params.append('populate[metode_sampling][fields][0]', 'documentId');
  params.append('populate[metode_sampling][fields][1]', 'kode_sni');
  params.append('populate[metode_sampling][fields][2]', 'satuan');
  params.append('populate[metode_sampling][populate][parameter_uji][fields][0]', 'documentId');
  params.append('populate[metode_sampling][populate][parameter_uji][fields][1]', 'nama_parameter');
  params.append('populate[metode_sampling][populate][parameter_uji][fields][2]', 'simbol');

  params.append('populate[sni_methods][fields][0]', 'documentId');
  params.append('populate[sni_methods][fields][1]', 'kode_sni');
  params.append('populate[sni_methods][fields][2]', 'judul');

  params.append('populate[quotation_sample_information][fields][0]', 'documentId');
  params.append('populate[quotation_sample_information][fields][1]', 'updatedAt');
  params.append('populate[quotation_sample_information][populate][jenis_pengukuran][fields][0]', 'documentId');
  params.append('populate[quotation_sample_information][populate][jenis_pengukuran][fields][1]', 'nama_jenis');
  params.append('populate[quotation_sample_information][populate][jenis_pengukuran][fields][2]', 'kode_jenis');
  params.append(
    'populate[quotation_sample_information][populate][jenis_pengukuran][populate][grouping][fields][0]',
    'perencanaan_sampling',
  );
  params.append(
    'populate[quotation_sample_information][populate][quotation_group][fields][0]',
    'documentId',
  );
  params.append(
    'populate[quotation_sample_information][populate][quotation_group][populate][customer]',
    'true',
  );
  params.append('populate[quotation_group][fields][0]', 'documentId');
  params.append('populate[quotation_group][populate][customer]', 'true');
}

export async function strapiGetSamplingPlan(
  jwt: string,
  documentId: string,
): Promise<DynamicEntityRow> {
  const params = new URLSearchParams();
  params.append('populate[bidang_pengujian][fields][0]', 'documentId');
  params.append('populate[bidang_pengujian][fields][1]', 'nama_jenis');
  params.append('populate[bidang_pengujian][fields][2]', 'kode_jenis');
  params.append('populate[quotation_sample_information][fields][0]', 'documentId');
  params.append('populate[quotation_sample_information][populate][jenis_pengukuran][fields][0]', 'documentId');
  params.append('populate[quotation_sample_information][populate][jenis_pengukuran][fields][1]', 'nama_jenis');
  params.append('populate[quotation_sample_information][populate][jenis_pengukuran][fields][2]', 'kode_jenis');
  const res = await fetch(
    `${STRAPI_URL}/api/sampling-plans/${encodeURIComponent(documentId)}?${params}`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const parsed = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(parsed, res.status));
  const row = asRecord(parsed)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid Strapi sampling plan response');
  return normalizeDynamicRow(row);
}

export async function strapiUpdateSamplingPlanChecklist(
  jwt: string,
  documentId: string,
  data: Partial<StrapiSamplingPlanCreateData>,
): Promise<DynamicEntityRow> {
  const { peta_sketsa_lokasi_files, work_order: _workOrder, ...payloadData } = data;
  const files = peta_sketsa_lokasi_files?.filter(Boolean) ?? [];
  const body =
    files.length > 0
      ? (() => {
          const form = new FormData();
          form.append('data', JSON.stringify(payloadData));
          files.forEach((file) => form.append('files.peta_sketsa_lokasi', file));
          return form;
        })()
      : JSON.stringify({ data: payloadData });
  const res = await fetch(`${STRAPI_URL}/api/sampling-plans/${encodeURIComponent(documentId)}?populate=*`, {
    method: 'PUT',
    headers:
      files.length > 0
        ? strapiAuthHeaders(jwt)
        : {
            ...(strapiAuthHeaders(jwt) as Record<string, string>),
            'Content-Type': 'application/json',
          },
    body,
  });
  const parsed = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(parsed, res.status));
  const row = asRecord(parsed)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid Strapi sampling plan update response');
  return normalizeDynamicRow(row);
}

/** 1 = normal, 2 = trouble */
export type SamplingToolCondition = 1 | 2;

export const SAMPLING_TOOL_CONDITION_LABEL: Record<SamplingToolCondition, string> = {
  1: 'Normal',
  2: 'Trouble',
};

export type StrapiSamplingCheckToolSaveRow = {
  referensiDocumentId: string;
  alatDocumentId?: string;
  initial_condition?: SamplingToolCondition;
  end_condition?: SamplingToolCondition | null;
  description?: string;
};

export async function strapiListSamplingCheckToolsForWorkOrder(
  jwt: string,
  workOrderDocumentId: string,
): Promise<DynamicEntityRow[]> {
  const params = new URLSearchParams({
    'pagination[pageSize]': '500',
    'pagination[page]': '1',
  });
  params.append('filters[work_order][documentId][$eq]', workOrderDocumentId);
  appendReferensiMiniPopulate(params, 'populate[referensi]');
  params.append('populate[alat][fields][0]', 'documentId');
  params.append('populate[alat][fields][1]', 'nama_alat');
  params.append('populate[alat][fields][2]', 'nomor_inventaris_alat');
  const res = await fetch(`${STRAPI_URL}/api/sampling-check-tools?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows } = parseListBody(res, body);
  return rows.map((row) => normalizeDynamicRow(row));
}

export async function strapiReplaceSamplingCheckToolsForWorkOrder(
  jwt: string,
  workOrderDocumentId: string,
  rows: StrapiSamplingCheckToolSaveRow[],
): Promise<void> {
  const existing = await strapiListSamplingCheckToolsForWorkOrder(jwt, workOrderDocumentId);
  await Promise.all(
    existing.map((row) => strapiDeleteDynamicEntity(jwt, 'sampling-check-tools', row.documentId)),
  );
  for (const row of rows) {
    const referensiId = row.referensiDocumentId.trim();
    if (!referensiId) continue;
    const alatId = row.alatDocumentId?.trim();
    await strapiCreateDynamicEntity(jwt, 'sampling-check-tools', {
      work_order: { connect: [workOrderDocumentId] },
      referensi: { connect: [referensiId] },
      ...(alatId ? { alat: { connect: [alatId] } } : {}),
      initial_condition: row.initial_condition ?? 1,
      ...(row.end_condition != null ? { end_condition: row.end_condition } : {}),
      ...(row.description?.trim() ? { description: row.description.trim() } : {}),
    });
  }
}

export async function strapiListToolsForPeralatanKategoriKodes(
  jwt: string,
  kategoriKodes: string[],
): Promise<DynamicEntityRow[]> {
  const kodes = new Set(
    kategoriKodes.map((k) => k.trim().toUpperCase()).filter((k) => k.startsWith('PERALATAN_')),
  );
  if (!kodes.size) return [];
  const result = await strapiListDynamicEntityPage(jwt, 'tools', {
    populate: '*',
    page: 1,
    pageSize: 2000,
  });
  return result.rows.filter((tool) => {
    const kat = tool.kategori_referensi;
    if (!kat || typeof kat !== 'object' || Array.isArray(kat)) return false;
    const kode = String((kat as Record<string, unknown>).kode_kategori ?? '')
      .trim()
      .toUpperCase();
    return kodes.has(kode);
  });
}

export type StrapiSurveyTeamCreateData = {
  work_order?: { connect: string[] };
  sampling_schedule?: { connect: string[] };
  /** Exactly one of `user_idx` or `freelance_user` must be set. */
  user_idx?: number | { connect: string[] };
  freelance_user?: { connect: string[] };
  is_lead: boolean;
  /** Label bebas untuk komposisi petugas (opsional). */
  team_name?: string | null;
  scheduled_date?: string[];
};

export type SamplingScheduleStatus =
  | 'draft'
  | 'scheduling_in_progress'
  | 'rejected'
  | 'approved'
  | 'work_order_created'
  | 'cancelled';

export type SamplingScheduleKind = 'initial' | 'point_reschedule';

export type StrapiSamplingScheduleRow = {
  documentId: string;
  schedule_number?: string | null;
  schedule_type?: 'flat' | 'recurring' | null;
  schedule_kind?: SamplingScheduleKind | null;
  reschedule_point_index?: number | null;
  reschedule_sampling_plan?: { documentId?: string } | null;
  petugas_proposed_at?: string | null;
  progress_status?: SamplingScheduleStatus | null;
  customer_agreed_start_date?: string | null;
  customer_agreed_end_date?: string | null;
  /** Tanggal kesepakatan eksplisit (`YYYY-MM-DD[]`); sumber kebenaran untuk kartu kosong. */
  customer_agreed_dates?: string[] | null;
  equipment_checklist_completed_at?: string | null;
  admin_workorder_completed_at?: string | null;
  admin_sampling_completed_at?: string | null;
  sampling_received_at?: string | null;
  approved_at?: string | null;
  notes?: string | null;
  transportasi_note?: string | null;
  quotation?: StrapiQuotationRow | null;
  quotation_group?: {
    documentId?: string;
    recurring_type?: string | null;
    recurring_interval?: number | null;
    recurring_count_override?: number | null;
    recurring_start_date?: string | null;
    createdAt?: string | null;
    customer?: StrapiCustomerAddressMini | null;
  } | null;
  quotation_recurring_event?: {
    documentId?: string;
    event_index?: number | null;
  } | null;
  work_order?: { documentId?: string; job_number?: string | null; progress_status?: string | null } | null;
};

/** Prefer initial schedule; otherwise first row. Keeps singular `sampling_schedule` consumers working. */
export function primarySamplingSchedule(
  schedules: StrapiSamplingScheduleRow[] | null | undefined,
  fallback?: StrapiSamplingScheduleRow | null,
): StrapiSamplingScheduleRow | null {
  if (schedules?.length) {
    const initial = schedules.find((s) => (s.schedule_kind ?? 'initial') === 'initial');
    return initial ?? schedules[0] ?? null;
  }
  return fallback ?? null;
}

export type SamplingSchedulesListFilters = {
  status?: string;
  quotationRefs?: string;
  customer?: string;
};

function normalizeSamplingScheduleRow(raw: Record<string, unknown>): StrapiSamplingScheduleRow {
  const qRaw = raw.quotation;
  const quotation =
    qRaw && typeof qRaw === 'object' && !Array.isArray(qRaw)
      ? normalizeQuotationEntry(qRaw as Record<string, unknown>)
      : null;
  const woRaw = raw.work_order;
  const work_order =
    woRaw && typeof woRaw === 'object' && !Array.isArray(woRaw)
      ? {
          documentId: String((woRaw as Record<string, unknown>).documentId ?? ''),
          job_number: (woRaw as Record<string, unknown>).job_number as string | null | undefined,
          progress_status: (woRaw as Record<string, unknown>).progress_status as string | null | undefined,
        }
      : null;
  const qgRaw = unwrapPopulateOne(raw.quotation_group);
  const quotation_group = qgRaw
    ? (() => {
        const g = flattenStrapiEntry(qgRaw);
        const custRaw = unwrapPopulateOne(g.customer);
        const customer = custRaw ? customerAddressMiniFromRow(normalizeCustomerEntry(custRaw)) : null;
        return {
          documentId: String(g.documentId ?? qgRaw.documentId ?? ''),
          recurring_type: g.recurring_type != null ? String(g.recurring_type) : null,
          recurring_interval:
            g.recurring_interval != null && Number.isFinite(Number(g.recurring_interval))
              ? Number(g.recurring_interval)
              : null,
          recurring_count_override:
            g.recurring_count_override != null && Number.isFinite(Number(g.recurring_count_override))
              ? Number(g.recurring_count_override)
              : null,
          recurring_start_date:
            g.recurring_start_date != null ? String(g.recurring_start_date) : null,
          createdAt: g.createdAt != null ? String(g.createdAt) : null,
          customer,
        };
      })()
    : null;
  const eventRaw = unwrapPopulateOne(raw.quotation_recurring_event);
  const quotation_recurring_event = eventRaw
    ? (() => {
        const event = flattenStrapiEntry(eventRaw);
        const eventIndex = Number(event.event_index);
        return {
          documentId: String(event.documentId ?? eventRaw.documentId ?? ''),
          event_index: Number.isFinite(eventIndex) ? eventIndex : null,
        };
      })()
    : null;
  const planRaw = unwrapPopulateOne(raw.reschedule_sampling_plan);
  const reschedule_sampling_plan = planRaw
    ? { documentId: String(planRaw.documentId ?? '').trim() || undefined }
    : null;
  return {
    documentId: String(raw.documentId ?? ''),
    schedule_number: raw.schedule_number != null ? String(raw.schedule_number) : null,
    schedule_type: raw.schedule_type as StrapiSamplingScheduleRow['schedule_type'],
    schedule_kind: (raw.schedule_kind as SamplingScheduleKind | null | undefined) ?? 'initial',
    reschedule_point_index:
      raw.reschedule_point_index != null && Number.isFinite(Number(raw.reschedule_point_index))
        ? Number(raw.reschedule_point_index)
        : null,
    reschedule_sampling_plan,
    petugas_proposed_at: raw.petugas_proposed_at as string | null | undefined,
    progress_status: raw.progress_status as SamplingScheduleStatus | null | undefined,
    customer_agreed_start_date: raw.customer_agreed_start_date as string | null | undefined,
    customer_agreed_end_date: raw.customer_agreed_end_date as string | null | undefined,
    customer_agreed_dates: Array.isArray(raw.customer_agreed_dates)
      ? (raw.customer_agreed_dates as unknown[])
          .map((item) => String(item ?? '').trim().slice(0, 10))
          .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
      : null,
    equipment_checklist_completed_at: raw.equipment_checklist_completed_at as string | null | undefined,
    admin_workorder_completed_at: raw.admin_workorder_completed_at as string | null | undefined,
    admin_sampling_completed_at: raw.admin_sampling_completed_at as string | null | undefined,
    sampling_received_at: raw.sampling_received_at as string | null | undefined,
    approved_at: raw.approved_at as string | null | undefined,
    notes: raw.notes as string | null | undefined,
    transportasi_note: raw.transportasi_note as string | null | undefined,
    quotation,
    quotation_group,
    quotation_recurring_event,
    work_order,
  };
}

export async function strapiListSamplingSchedules(
  jwt: string,
  filters: SamplingSchedulesListFilters = {},
  options: { page?: number; pageSize?: number } = {},
): Promise<{ rows: StrapiSamplingScheduleRow[]; total: number }> {
  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? 10;
  const params = new URLSearchParams({
    'pagination[pageSize]': String(pageSize),
    'pagination[page]': String(page),
    sort: 'createdAt:desc',
  });
  params.append('populate[quotation][populate][0]', 'customer');
  params.append('populate[quotation][fields][0]', 'documentId');
  params.append('populate[quotation][fields][1]', 'quotation_number');
  params.append('populate[quotation][fields][2]', 'is_contract');
  params.append('populate[quotation][fields][3]', 'is_consultant_quote');
  params.append('populate[quotation][fields][4]', 'contract_period_start_date');
  params.append('populate[quotation][fields][5]', 'contract_period_end_date');
  params.append('populate[quotation_group][fields][0]', 'documentId');
  params.append('populate[quotation_group][fields][1]', 'recurring_type');
  params.append('populate[quotation_group][fields][2]', 'recurring_interval');
  params.append('populate[quotation_group][fields][3]', 'recurring_count_override');
  params.append('populate[quotation_group][fields][4]', 'recurring_start_date');
  params.append('populate[quotation_group][fields][5]', 'createdAt');
  params.append('populate[quotation_group][populate][customer][fields][0]', 'name');
  params.append('populate[quotation_recurring_event][fields][0]', 'documentId');
  params.append('populate[quotation_recurring_event][fields][1]', 'event_index');
  params.append('populate[work_order][fields][0]', 'job_number');
  params.append('populate[work_order][fields][1]', 'documentId');
  params.append('populate[work_order][fields][2]', 'progress_status');
  params.append('populate[reschedule_sampling_plan][fields][0]', 'documentId');
  const status = filters.status?.trim();
  if (status && status !== 'ALL') {
    params.append('filters[progress_status][$eq]', status);
  }
  const qRef = filters.quotationRefs?.trim();
  if (qRef) {
    params.append('filters[quotation][quotation_number][$containsi]', qRef);
  }
  const customer = filters.customer?.trim();
  if (customer) {
    params.append('filters[quotation][customer][name][$containsi]', customer);
  }
  const res = await fetch(`${STRAPI_URL}/api/sampling-schedules?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows, meta } = parseListBody(res, body);
  const total = meta.pagination?.total ?? rows.length;
  return {
    rows: rows.map((row) => normalizeSamplingScheduleRow(row as Record<string, unknown>)),
    total,
  };
}

/** Semua baris penjadwalan untuk kalender overview (paginate penuh). */
export async function strapiListAllSamplingSchedulesForCalendar(
  jwt: string,
): Promise<StrapiSamplingScheduleRow[]> {
  const pageSize = 500;
  const allRows: StrapiSamplingScheduleRow[] = [];
  let page = 1;
  let pageCount = 1;

  while (page <= pageCount) {
    const { rows, total } = await strapiListSamplingSchedules(jwt, {}, { page, pageSize });
    allRows.push(...rows);
    pageCount = Math.max(1, Math.ceil(total / pageSize));
    page += 1;
    if (page > 50) break;
  }

  return allRows;
}

export async function strapiGetSamplingSchedule(
  jwt: string,
  documentId: string,
): Promise<StrapiSamplingScheduleRow> {
  const params = new URLSearchParams();
  params.append('populate[quotation][fields][0]', 'documentId');
  params.append('populate[quotation][fields][1]', 'quotation_number');
  params.append('populate[quotation][fields][2]', 'is_contract');
  params.append('populate[quotation][fields][3]', 'is_consultant_quote');
  params.append('populate[quotation][fields][4]', 'contract_period_start_date');
  params.append('populate[quotation][fields][5]', 'contract_period_end_date');
  params.append('populate[quotation][populate][customer][fields][0]', 'documentId');
  params.append('populate[quotation][populate][customer][fields][1]', 'name');
  params.append('populate[quotation][populate][customer][fields][2]', 'address');
  params.append('populate[quotation][populate][customer][fields][3]', 'city');
  params.append('populate[quotation][populate][customer][fields][4]', 'province');
  params.append('populate[quotation_group][fields][0]', 'documentId');
  params.append('populate[quotation_group][fields][1]', 'recurring_type');
  params.append('populate[quotation_group][fields][2]', 'recurring_interval');
  params.append('populate[quotation_group][fields][3]', 'recurring_count_override');
  params.append('populate[quotation_group][fields][4]', 'recurring_start_date');
  params.append('populate[quotation_group][fields][5]', 'createdAt');
  params.append('populate[quotation_group][populate][customer][fields][0]', 'name');
  params.append('populate[quotation_recurring_event][fields][0]', 'documentId');
  params.append('populate[quotation_recurring_event][fields][1]', 'event_index');
  params.append('populate[work_order][fields][0]', 'job_number');
  params.append('populate[work_order][fields][1]', 'documentId');
  params.append('populate[work_order][fields][2]', 'progress_status');
  const res = await fetch(
    `${STRAPI_URL}/api/sampling-schedules/${encodeURIComponent(documentId)}?${params}`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!data) throw new Error('Invalid sampling schedule response');
  return normalizeSamplingScheduleRow(data);
}

/** Penjadwalan aktif terbaru untuk quotation (untuk navigasi langsung ke detail). */
export async function strapiFindSamplingScheduleForQuotation(
  jwt: string,
  quotationDocumentId: string,
): Promise<StrapiSamplingScheduleRow | null> {
  const qid = quotationDocumentId.trim();
  if (!qid) return null;
  const params = new URLSearchParams({
    'pagination[pageSize]': '1',
    'pagination[page]': '1',
    sort: 'createdAt:desc',
  });
  params.append('filters[quotation][documentId][$eq]', qid);
  params.append('filters[progress_status][$notIn][0]', 'cancelled');
  params.append('populate[quotation][populate][0]', 'customer');
  params.append('populate[work_order][fields][0]', 'job_number');
  params.append('populate[work_order][fields][1]', 'documentId');
  const res = await fetch(`${STRAPI_URL}/api/sampling-schedules?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows } = parseListBody(res, body);
  const first = rows[0] as Record<string, unknown> | undefined;
  return first ? normalizeSamplingScheduleRow(first) : null;
}

export async function strapiUpdateSamplingSchedule(
  jwt: string,
  documentId: string,
  data: Record<string, unknown>,
): Promise<StrapiSamplingScheduleRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/sampling-schedules/${encodeURIComponent(documentId)}?populate=*`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid sampling schedule update response');
  return normalizeSamplingScheduleRow(row);
}

/** Hapus penjadwalan (cascade tim survey / checklist / putuskan plan). */
export async function strapiDeleteSamplingSchedule(
  jwt: string,
  documentId: string,
): Promise<void> {
  const res = await fetch(
    `${STRAPI_URL}/api/sampling-schedules/${encodeURIComponent(documentId)}/remove`,
    {
      method: 'DELETE',
      headers: strapiAuthHeaders(jwt),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
}

export async function strapiCompleteScheduleEquipmentChecklist(
  jwt: string,
  documentId: string,
): Promise<StrapiSamplingScheduleRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/sampling-schedules/${encodeURIComponent(documentId)}/complete-equipment-checklist`,
    { method: 'POST', headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid complete checklist response');
  return normalizeSamplingScheduleRow(row);
}

export async function strapiCompleteScheduleAdminWorkorder(
  jwt: string,
  documentId: string,
): Promise<StrapiSamplingScheduleRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/sampling-schedules/${encodeURIComponent(documentId)}/complete-admin-workorder`,
    { method: 'POST', headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid complete admin workorder response');
  return normalizeSamplingScheduleRow(row);
}

export async function strapiCompleteScheduleAdminSampling(
  jwt: string,
  documentId: string,
): Promise<StrapiSamplingScheduleRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/sampling-schedules/${encodeURIComponent(documentId)}/complete-admin-sampling`,
    { method: 'POST', headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid complete admin sampling response');
  return normalizeSamplingScheduleRow(row);
}

export async function strapiApproveSamplingSchedule(
  jwt: string,
  documentId: string,
): Promise<StrapiSamplingScheduleRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/sampling-schedules/${encodeURIComponent(documentId)}/approve`,
    { method: 'POST', headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid approve sampling schedule response');
  return normalizeSamplingScheduleRow(row);
}

export async function strapiEnsureSamplingScheduleForQuotation(
  jwt: string,
  quotationDocumentId: string,
  quotationGroupDocumentId?: string,
  quotationRecurringEventDocumentId?: string,
): Promise<StrapiSamplingScheduleRow> {
  const groupId = String(quotationGroupDocumentId ?? '').trim();
  const eventId = String(quotationRecurringEventDocumentId ?? '').trim();
  const res = await fetch(`${STRAPI_URL}/api/sampling-schedules/ensure-for-quotation`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      data: {
        quotation_document_id: quotationDocumentId,
        ...(groupId ? { quotation_group_document_id: groupId } : {}),
        ...(eventId ? { quotation_recurring_event_document_id: eventId } : {}),
      },
    }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid ensure schedule response');
  return normalizeSamplingScheduleRow(row);
}

export async function strapiConnectSamplingPlanToSchedule(
  jwt: string,
  scheduleDocumentId: string,
  planDocumentId: string,
): Promise<StrapiSamplingScheduleRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/sampling-schedules/${encodeURIComponent(scheduleDocumentId)}/connect-plan`,
    {
      method: 'POST',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: { plan_document_id: planDocumentId } }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid connect plan response');
  return normalizeSamplingScheduleRow(row);
}

export async function strapiListSamplingPlansForQuotation(
  jwt: string,
  quotationDocumentId: string,
): Promise<DynamicEntityRow[]> {
  const params = new URLSearchParams({
    'pagination[pageSize]': '500',
    'pagination[page]': '1',
    sort: 'line_index:asc',
  });
  params.append('filters[quotation][documentId][$eq]', quotationDocumentId);
  params.append('populate[sampling_schedule][fields][0]', 'documentId');
  params.append('populate[sampling_schedule][fields][1]', 'progress_status');
  params.append('populate[work_order][fields][0]', 'documentId');
  params.append('populate[work_order][fields][1]', 'job_number');
  params.append('populate[quotation_group][fields][0]', 'documentId');
  params.append('populate[quotation_group][fields][1]', 'recurring_type');
  params.append('populate[quotation_group][fields][2]', 'recurring_interval');
  params.append('populate[quotation_group][fields][3]', 'recurring_count_override');
  params.append('populate[quotation_group][fields][4]', 'recurring_start_date');
  params.append('populate[quotation_group][fields][5]', 'createdAt');
  params.append('populate[quotation_group][populate][customer][fields][0]', 'documentId');
  params.append('populate[quotation_group][populate][customer][fields][1]', 'name');
  params.append('populate[quotation_group][populate][quotation_recurring_events][fields][0]', 'documentId');
  params.append('populate[quotation_group][populate][quotation_recurring_events][fields][1]', 'event_index');
  params.append('populate[quotation_group][populate][quotation_recurring_events][fields][2]', 'scheduled_date');
  params.append(
    'populate[quotation_group][populate][quotation_recurring_events][populate][sampling_schedule][fields][0]',
    'documentId',
  );
  params.append(
    'populate[quotation_group][populate][quotation_recurring_events][populate][sampling_schedule][populate][work_order][fields][0]',
    'documentId',
  );
  params.append(
    'populate[quotation_group][populate][quotation_recurring_events][populate][sampling_schedule][populate][work_order][fields][1]',
    'job_number',
  );
  params.append('populate[quotation_sample_information][fields][0]', 'documentId');
  params.append('populate[quotation_sample_information][fields][1]', 'sample_point_count');
  params.append('populate[quotation_sample_information][fields][2]', 'harga');
  params.append('populate[quotation_sample_information][populate][jenis_pengukuran][fields][0]', 'documentId');
  params.append('populate[quotation_sample_information][populate][jenis_pengukuran][fields][1]', 'nama_jenis');
  params.append('populate[quotation_sample_information][populate][jenis_pengukuran][fields][2]', 'kode_jenis');
  params.append('populate[quotation_sample_information][populate][baku_mutu][fields][0]', 'documentId');
  params.append('populate[quotation_sample_information][populate][baku_mutu][fields][1]', 'nama_baku_mutu');
  params.append('populate[quotation_sample_information][populate][parameter_ujis][fields][0]', 'documentId');
  params.append('populate[quotation_sample_information][populate][parameter_ujis][fields][1]', 'nama_parameter');
  params.append('populate[quotation_sample_information][populate][parameter_ujis][fields][2]', 'simbol');
  params.append('populate[quotation_sample_information][populate][quotation_group][fields][0]', 'documentId');
  params.append('populate[quotation_sample_information][populate][quotation_group][fields][1]', 'recurring_type');
  params.append('populate[quotation_sample_information][populate][quotation_group][fields][2]', 'recurring_interval');
  params.append('populate[quotation_sample_information][populate][quotation_group][fields][3]', 'recurring_count_override');
  params.append('populate[quotation_sample_information][populate][quotation_group][fields][4]', 'recurring_start_date');
  params.append('populate[quotation_sample_information][populate][quotation_group][fields][5]', 'createdAt');
  params.append(
    'populate[quotation_sample_information][populate][quotation_group][populate][customer][fields][0]',
    'documentId',
  );
  params.append(
    'populate[quotation_sample_information][populate][quotation_group][populate][customer][fields][1]',
    'name',
  );
  params.append(
    'populate[quotation_sample_information][populate][quotation_group][populate][quotation_recurring_events][fields][0]',
    'documentId',
  );
  params.append(
    'populate[quotation_sample_information][populate][quotation_group][populate][quotation_recurring_events][fields][1]',
    'event_index',
  );
  params.append(
    'populate[quotation_sample_information][populate][quotation_group][populate][quotation_recurring_events][fields][2]',
    'scheduled_date',
  );
  params.append(
    'populate[quotation_sample_information][populate][quotation_group][populate][quotation_recurring_events][populate][sampling_schedule][fields][0]',
    'documentId',
  );
  params.append(
    'populate[quotation_sample_information][populate][quotation_group][populate][quotation_recurring_events][populate][sampling_schedule][populate][work_order][fields][0]',
    'documentId',
  );
  params.append(
    'populate[quotation_sample_information][populate][quotation_group][populate][quotation_recurring_events][populate][sampling_schedule][populate][work_order][fields][1]',
    'job_number',
  );
  const res = await fetch(`${STRAPI_URL}/api/sampling-plans?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows } = parseListBody(res, body);
  return rows.map((row) => normalizeDynamicRow(row));
}

export async function strapiPromoteScheduleToWorkOrder(
  jwt: string,
  scheduleDocumentId: string,
  workOrder: Record<string, unknown>,
): Promise<{ documentId: string; job_number?: string | null }> {
  const res = await fetch(
    `${STRAPI_URL}/api/sampling-schedules/${encodeURIComponent(scheduleDocumentId)}/promote-to-work-order`,
    {
      method: 'POST',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: { work_order: workOrder } }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid promote response');
  return {
    documentId: String(row.documentId ?? ''),
    job_number: row.job_number as string | null | undefined,
  };
}

export async function strapiListSurveyTeamsForSamplingSchedule(
  jwt: string,
  scheduleDocumentId: string,
): Promise<DynamicEntityRow[]> {
  return strapiFetchSurveyTeamsPaged(jwt, (params) => {
    params.append('filters[sampling_schedule][documentId][$eq]', scheduleDocumentId);
  }, 'createdAt:asc');
}

/**
 * Tim survey penjadwalan: relasi langsung ke schedule, fallback ke work order terkait.
 *
 * Set `allowWorkOrderFallback: false` for `point_reschedule` schedules — they share a WO with
 * the initial schedule, and WO fallback would surface (and risk deleting) the original teams.
 */
export async function strapiResolveSurveyTeamsForSamplingSchedule(
  jwt: string,
  scheduleDocumentId: string,
  workOrderDocumentId?: string | null,
  options?: { allowWorkOrderFallback?: boolean },
): Promise<DynamicEntityRow[]> {
  const bySchedule = await strapiListSurveyTeamsForSamplingSchedule(jwt, scheduleDocumentId);
  if (bySchedule.length > 0) return bySchedule;
  if (options?.allowWorkOrderFallback === false) return [];
  const woDocId = workOrderDocumentId?.trim();
  if (!woDocId) return [];
  return strapiListSurveyTeamsForWorkOrder(jwt, woDocId);
}

function appendSurveyTeamCalendarPopulate(params: URLSearchParams): void {
  params.append('populate[user_idx][fields][0]', 'id');
  params.append('populate[user_idx][fields][1]', 'documentId');
  params.append('populate[user_idx][fields][2]', 'username');
  params.append('populate[user_idx][fields][3]', 'email');
  params.append('populate[freelance_user][fields][0]', 'documentId');
  params.append('populate[freelance_user][fields][1]', 'name');
  params.append('populate[freelance_user][fields][2]', 'phone_number');
  params.append('populate[freelance_user][fields][3]', 'active_status');
  params.append('populate[sampling_schedule][fields][0]', 'documentId');
  params.append('populate[sampling_schedule][fields][1]', 'schedule_number');
  params.append('populate[sampling_schedule][fields][2]', 'notes');
  params.append('populate[sampling_schedule][fields][3]', 'progress_status');
  params.append('populate[sampling_schedule][fields][4]', 'sampling_received_at');
  params.append('populate[sampling_schedule][fields][5]', 'transportasi_note');
  params.append('populate[sampling_schedule][populate][work_order][fields][0]', 'documentId');
  params.append('populate[sampling_schedule][populate][work_order][fields][1]', 'job_number');
  params.append('populate[sampling_schedule][populate][quotation_group][fields][0]', 'documentId');
  params.append('populate[sampling_schedule][populate][quotation_group][fields][1]', 'recurring_type');
  params.append('populate[sampling_schedule][populate][quotation_group][fields][2]', 'recurring_interval');
  params.append('populate[sampling_schedule][populate][quotation_group][fields][3]', 'recurring_count_override');
  params.append('populate[sampling_schedule][populate][quotation_group][fields][4]', 'recurring_start_date');
  params.append('populate[sampling_schedule][populate][quotation_group][fields][5]', 'createdAt');
  params.append(
    'populate[sampling_schedule][populate][quotation_group][populate][customer][fields][0]',
    'name',
  );
  params.append('populate[sampling_schedule][populate][quotation_recurring_event][fields][0]', 'documentId');
  params.append('populate[sampling_schedule][populate][quotation_recurring_event][fields][1]', 'event_index');
  params.append('populate[sampling_schedule][populate][sampling_plans][fields][0]', 'documentId');
  params.append(
    'populate[sampling_schedule][populate][sampling_plans][populate][quotation_group][fields][0]',
    'documentId',
  );
  params.append(
    'populate[sampling_schedule][populate][sampling_plans][populate][quotation_group][fields][1]',
    'recurring_type',
  );
  params.append(
    'populate[sampling_schedule][populate][sampling_plans][populate][quotation_group][fields][2]',
    'recurring_interval',
  );
  params.append(
    'populate[sampling_schedule][populate][sampling_plans][populate][quotation_group][fields][3]',
    'recurring_count_override',
  );
  params.append(
    'populate[sampling_schedule][populate][sampling_plans][populate][quotation_group][fields][4]',
    'recurring_start_date',
  );
  params.append(
    'populate[sampling_schedule][populate][sampling_plans][populate][quotation_group][fields][5]',
    'createdAt',
  );
  params.append(
    'populate[sampling_schedule][populate][sampling_plans][populate][quotation_group][populate][customer][fields][0]',
    'name',
  );
  params.append(
    'populate[sampling_schedule][populate][sampling_plans][populate][quotation_sample_information][fields][0]',
    'documentId',
  );
  params.append(
    'populate[sampling_schedule][populate][sampling_plans][populate][quotation_sample_information][populate][jenis_pengukuran][fields][0]',
    'documentId',
  );
  params.append(
    'populate[sampling_schedule][populate][sampling_plans][populate][quotation_sample_information][populate][jenis_pengukuran][fields][1]',
    'nama_jenis',
  );
  params.append(
    'populate[sampling_schedule][populate][sampling_plans][populate][quotation_sample_information][populate][jenis_pengukuran][fields][2]',
    'kode_jenis',
  );
  params.append('populate[sampling_schedule][populate][quotation][fields][0]', 'documentId');
  params.append('populate[sampling_schedule][populate][quotation][fields][1]', 'quotation_number');
  params.append('populate[sampling_schedule][populate][quotation][fields][2]', 'is_contract');
  params.append('populate[sampling_schedule][populate][quotation][fields][3]', 'is_consultant_quote');
  params.append('populate[sampling_schedule][populate][quotation][fields][4]', 'contract_period_start_date');
  params.append('populate[sampling_schedule][populate][quotation][fields][5]', 'contract_period_end_date');
  params.append('populate[sampling_schedule][populate][quotation][populate][customer][fields][0]', 'name');
  params.append('populate[sampling_schedule][populate][quotation][populate][customer][fields][1]', 'address');
  params.append('populate[sampling_schedule][populate][quotation][populate][customer][fields][2]', 'city');
  params.append('populate[sampling_schedule][populate][quotation][populate][customer][fields][3]', 'province');
  params.append('populate[work_order][fields][0]', 'documentId');
  params.append('populate[work_order][fields][1]', 'job_number');
  params.append('populate[work_order][populate][sampling_schedules][fields][0]', 'documentId');
  params.append('populate[work_order][populate][sampling_schedules][fields][1]', 'schedule_number');
  params.append('populate[work_order][populate][sampling_schedules][fields][2]', 'notes');
  params.append('populate[work_order][populate][sampling_schedules][fields][3]', 'progress_status');
  params.append('populate[work_order][populate][sampling_schedules][fields][4]', 'schedule_kind');
  params.append('populate[work_order][populate][sampling_schedules][fields][5]', 'sampling_received_at');
  params.append('populate[work_order][populate][sampling_schedules][fields][6]', 'transportasi_note');
  params.append('populate[work_order][populate][sampling_schedules][populate][quotation][fields][0]', 'documentId');
  params.append('populate[work_order][populate][sampling_schedules][populate][quotation][fields][1]', 'quotation_number');
  params.append('populate[work_order][populate][sampling_schedules][populate][quotation][fields][2]', 'is_contract');
  params.append('populate[work_order][populate][sampling_schedules][populate][quotation][fields][3]', 'is_consultant_quote');
  params.append('populate[work_order][populate][sampling_schedules][populate][quotation][fields][4]', 'contract_period_start_date');
  params.append('populate[work_order][populate][sampling_schedules][populate][quotation][fields][5]', 'contract_period_end_date');
  params.append('populate[work_order][populate][sampling_schedules][populate][quotation][populate][customer][fields][0]', 'name');
  params.append('populate[work_order][populate][sampling_schedules][populate][quotation][populate][customer][fields][1]', 'address');
  params.append('populate[work_order][populate][sampling_schedules][populate][quotation][populate][customer][fields][2]', 'city');
  params.append('populate[work_order][populate][sampling_schedules][populate][quotation][populate][customer][fields][3]', 'province');
  params.append(
    'populate[work_order][populate][sampling_schedules][populate][quotation_recurring_event][fields][0]',
    'documentId',
  );
  params.append(
    'populate[work_order][populate][sampling_schedules][populate][quotation_recurring_event][fields][1]',
    'event_index',
  );
}

async function strapiFetchSurveyTeamsPaged(
  jwt: string,
  applyFilters: (params: URLSearchParams) => void,
  sort = 'createdAt:asc',
): Promise<DynamicEntityRow[]> {
  const pageSize = 500;
  const allRows: Record<string, unknown>[] = [];
  let page = 1;
  let pageCount = 1;

  while (page <= pageCount) {
    const params = new URLSearchParams({
      'pagination[pageSize]': String(pageSize),
      'pagination[page]': String(page),
      sort,
    });
    appendSurveyTeamCalendarPopulate(params);
    applyFilters(params);
    const res = await fetch(`${STRAPI_URL}/api/survey-teams?${params}`, {
      headers: strapiAuthHeaders(jwt),
    });
    const body = (await res.json().catch(() => ({}))) as unknown;
    const { rows, meta } = parseListBody(res, body);
    allRows.push(...rows);
    pageCount = meta.pagination?.pageCount ?? page;
    page += 1;
    if (page > 50) break;
  }

  return allRows.map((row) => normalizeDynamicRow(row));
}

export async function strapiListSamplingPlansForSchedule(
  jwt: string,
  scheduleDocumentId: string,
): Promise<DynamicEntityRow[]> {
  const params = new URLSearchParams({
    'pagination[pageSize]': '500',
    'pagination[page]': '1',
    sort: 'line_index:asc',
    populate: '*',
  });
  params.append('filters[sampling_schedule][documentId][$eq]', scheduleDocumentId);
  const res = await fetch(`${STRAPI_URL}/api/sampling-plans?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows } = parseListBody(res, body);
  return rows.map((row) => normalizeDynamicRow(row));
}

export async function strapiListSamplingCheckToolsForSchedule(
  jwt: string,
  scheduleDocumentId: string,
): Promise<DynamicEntityRow[]> {
  const params = new URLSearchParams({
    'pagination[pageSize]': '500',
    'pagination[page]': '1',
    populate: '*',
  });
  params.append('filters[sampling_schedule][documentId][$eq]', scheduleDocumentId);
  const res = await fetch(`${STRAPI_URL}/api/sampling-check-tools?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows } = parseListBody(res, body);
  return rows.map((row) => normalizeDynamicRow(row));
}

export async function strapiReplaceSamplingCheckToolsForSchedule(
  jwt: string,
  scheduleDocumentId: string,
  rows: StrapiSamplingCheckToolSaveRow[],
): Promise<void> {
  const existing = await strapiListSamplingCheckToolsForSchedule(jwt, scheduleDocumentId);
  await Promise.all(
    existing.map((row) => strapiDeleteDynamicEntity(jwt, 'sampling-check-tools', row.documentId)),
  );
  for (const row of rows) {
    const referensiId = row.referensiDocumentId.trim();
    if (!referensiId) continue;
    const alatId = row.alatDocumentId?.trim();
    await strapiCreateDynamicEntity(jwt, 'sampling-check-tools', {
      sampling_schedule: { connect: [scheduleDocumentId] },
      referensi: { connect: [referensiId] },
      ...(alatId ? { alat: { connect: [alatId] } } : {}),
      initial_condition: row.initial_condition ?? 1,
      ...(row.end_condition != null ? { end_condition: row.end_condition } : {}),
      ...(row.description ? { description: row.description } : {}),
    });
  }
}

export type StrapiSurveyTeamsPresenceCreateData = {
  survey_team: { connect: string[] };
  is_presence: boolean;
  picture?: string | null;
  longitude?: number | null;
  latitude?: number | null;
};

export type SurveyTeamScheduledSlot = {
  /** Nilai mentah tersimpan di JSON `scheduled_date`. */
  raw: string;
  /** Tanggal `YYYY-MM-DD`. */
  date: string;
  /** Label jam `HH:mm` (kosong jika hanya tanggal). */
  timeLabel: string;
};

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function isoDateFromDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Normalisasi slot jadwal dari field JSON `scheduled_date` (tanggal atau datetime ISO). */
export function parseSurveyTeamScheduledSlots(raw: unknown): SurveyTeamScheduledSlot[] {
  let list: unknown = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      list = JSON.parse(trimmed) as unknown;
    } catch {
      list = [trimmed];
    }
  }
  if (!Array.isArray(list)) {
    if (list == null || list === '') return [];
    list = [list];
  }
  const items = list as unknown[];
  const out: SurveyTeamScheduledSlot[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const text = String(item ?? '').trim();
    if (!text || seen.has(text)) continue;
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      seen.add(text);
      out.push({ raw: text, date: text, timeLabel: '' });
      continue;
    }
    const d = new Date(text);
    if (Number.isNaN(d.getTime())) continue;
    seen.add(text);
    out.push({
      raw: text,
      date: isoDateFromDate(d),
      timeLabel: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
    });
  }
  return out.sort((a, b) => a.raw.localeCompare(b.raw));
}

/** True jika nilai slot mentah merujuk ke tanggal kalender `YYYY-MM-DD`. */
export function surveyTeamSlotMatchesDate(raw: string, date: string): boolean {
  const target = String(date ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(target)) return false;
  const text = String(raw ?? '').trim();
  if (!text) return false;
  if (text === target || text.slice(0, 10) === target) return true;
  const parsed = parseSurveyTeamScheduledSlots([text])[0];
  return parsed?.date === target;
}

/** Hapus semua slot pada tanggal kalender tertentu; kembalikan raw tersisa. */
export function filterSurveyTeamSlotsRemovingDate(raw: unknown, date: string): string[] {
  return parseSurveyTeamScheduledSlots(raw)
    .filter((slot) => !surveyTeamSlotMatchesDate(slot.raw, date))
    .map((slot) => slot.raw);
}

/** Normalisasi array tanggal ISO `YYYY-MM-DD` dari field JSON `scheduled_date`. */
export function parseSurveyTeamScheduledDates(raw: unknown): string[] {
  const dates = parseSurveyTeamScheduledSlots(raw).map((s) => s.date);
  return [...new Set(dates)].sort();
}

export function surveyTeamRowScheduledOnDate(row: DynamicEntityRow, date: string): boolean {
  return parseSurveyTeamScheduledSlots(row.scheduled_date).some((s) => s.date === date);
}

export function surveyTeamSlotsOnDate(row: DynamicEntityRow, date: string): SurveyTeamScheduledSlot[] {
  return parseSurveyTeamScheduledSlots(row.scheduled_date).filter((s) => s.date === date);
}

/** Gabungkan tanggal + jam lokal menjadi ISO datetime untuk disimpan. */
export function buildSurveyTeamSlotIso(date: string, timeHm: string): string {
  const t = timeHm.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(t)) {
    throw new Error('Format tanggal/jam tidak valid.');
  }
  const d = new Date(`${date}T${t}:00`);
  if (Number.isNaN(d.getTime())) throw new Error('Tanggal/jam tidak valid.');
  return d.toISOString();
}

export function surveyTeamMemberDisplayLabel(team: DynamicEntityRow): string {
  const u = team.user_idx;
  if (u && typeof u === 'object' && !Array.isArray(u)) {
    const row = u as Record<string, unknown>;
    const username = String(row.username ?? '').trim();
    const email = String(row.email ?? '').trim();
    if (username || email) {
      return [username, email ? `(${email})` : ''].filter(Boolean).join(' ');
    }
  }
  const freelance = team.freelance_user;
  if (freelance && typeof freelance === 'object' && !Array.isArray(freelance)) {
    const row = freelance as Record<string, unknown>;
    const name = String(row.name ?? '').trim();
    if (name) return `${name} (freelance)`;
    const docId = String(row.documentId ?? '').trim();
    return docId ? `Freelance #${docId}` : 'Freelance';
  }
  return '—';
}

export async function strapiListSurveyTeamsForWorkOrder(
  jwt: string,
  workOrderDocumentId: string,
): Promise<DynamicEntityRow[]> {
  return strapiFetchSurveyTeamsPaged(jwt, (params) => {
    params.append('filters[work_order][documentId][$eq]', workOrderDocumentId);
  }, 'createdAt:asc');
}

/** Tim survey untuk WO: dari relasi work order, fallback ke penjadwalan sampling. */
export async function strapiResolveSurveyTeamsForWorkOrder(
  jwt: string,
  workOrderDocumentId: string,
  samplingScheduleDocumentId?: string | null,
): Promise<DynamicEntityRow[]> {
  const byWorkOrder = await strapiListSurveyTeamsForWorkOrder(jwt, workOrderDocumentId);
  if (byWorkOrder.length > 0) return byWorkOrder;
  const scheduleId = samplingScheduleDocumentId?.trim();
  if (!scheduleId) return [];
  return strapiListSurveyTeamsForSamplingSchedule(jwt, scheduleId);
}

export type SurveyTeamsScheduleListOptions = {
  userNumericId?: number | null;
};

/** Daftar tim survey lintas work order untuk halaman Jadwal Survey. */
export async function strapiListSurveyTeamsForSchedule(
  jwt: string,
  options: SurveyTeamsScheduleListOptions = {},
): Promise<DynamicEntityRow[]> {
  const pageSize = 500;
  const allRows: Record<string, unknown>[] = [];
  let page = 1;
  let pageCount = 1;

  while (page <= pageCount) {
    const params = new URLSearchParams({
      'pagination[pageSize]': String(pageSize),
      'pagination[page]': String(page),
      sort: 'createdAt:desc',
    });
    appendSurveyTeamCalendarPopulate(params);
    if (options.userNumericId != null && Number.isFinite(options.userNumericId)) {
      params.append('filters[user_idx][id][$eq]', String(options.userNumericId));
    }
    const res = await fetch(`${STRAPI_URL}/api/survey-teams?${params}`, {
      headers: strapiAuthHeaders(jwt),
    });
    const body = (await res.json().catch(() => ({}))) as unknown;
    const { rows, meta } = parseListBody(res, body);
    allRows.push(...rows);
    pageCount = meta.pagination?.pageCount ?? page;
    page += 1;
    if (page > 50) break;
  }

  return allRows.map((row) => normalizeDynamicRow(row));
}

export async function strapiListSurveyTeamSchedules(
  jwt: string,
  userNumericIds: number[],
): Promise<DynamicEntityRow[]> {
  const ids = [...new Set(userNumericIds.filter((id) => Number.isFinite(id)))];
  if (!ids.length) return [];

  const params = new URLSearchParams({
    'pagination[pageSize]': '500',
    'pagination[page]': '1',
    sort: 'createdAt:asc',
    populate: '*',
  });
  ids.forEach((id, idx) => {
    params.append(`filters[$or][${idx}][user_idx][id][$eq]`, String(id));
  });
  const res = await fetch(`${STRAPI_URL}/api/survey-teams?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows } = parseListBody(res, body);
  return rows.map((row) => normalizeDynamicRow(row));
}

export async function strapiCreateSurveyTeam(
  jwt: string,
  data: StrapiSurveyTeamCreateData,
): Promise<DynamicEntityRow> {
  const res = await fetch(`${STRAPI_URL}/api/survey-teams?populate=*`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid Strapi survey team create response');
  return normalizeDynamicRow(row);
}

export async function strapiUpdateSurveyTeam(
  jwt: string,
  documentId: string,
  data: Partial<Pick<StrapiSurveyTeamCreateData, 'is_lead' | 'scheduled_date' | 'team_name'>>,
): Promise<DynamicEntityRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/survey-teams/${encodeURIComponent(documentId)}?populate=*`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid Strapi survey team update response');
  return normalizeDynamicRow(row);
}

/**
 * Hapus satu tanggal dari `scheduled_date` semua tim milik jadwal (dan tim WO legacy
 * yang tidak terikat jadwal lain). Update in-place — tidak recreate roster.
 */
export async function strapiRemoveScheduledDateFromSurveyTeams(opts: {
  jwt: string;
  scheduleDocumentId: string;
  date: string;
  workOrderDocumentId?: string | null;
}): Promise<{
  remainingDates: string[];
  deletedTeamCount: number;
  updatedTeamCount: number;
}> {
  const scheduleDocumentId = String(opts.scheduleDocumentId ?? '').trim();
  const date = String(opts.date ?? '').trim();
  if (!scheduleDocumentId) throw new Error('ID penjadwalan tidak valid.');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error('Tanggal tidak valid.');

  const bySchedule = await strapiListSurveyTeamsForSamplingSchedule(
    opts.jwt,
    scheduleDocumentId,
  );
  const teams: DynamicEntityRow[] = [...bySchedule];
  const seen = new Set(
    bySchedule.map((row) => String(row.documentId ?? '').trim()).filter(Boolean),
  );

  const woDocId = String(opts.workOrderDocumentId ?? '').trim();
  if (woDocId) {
    const byWo = await strapiListSurveyTeamsForWorkOrder(opts.jwt, woDocId);
    for (const row of byWo) {
      const docId = String(row.documentId ?? '').trim();
      if (!docId || seen.has(docId)) continue;
      const scheduleRel = row.sampling_schedule as { documentId?: string } | null | undefined;
      const linkedScheduleId = String(scheduleRel?.documentId ?? '').trim();
      // Tim WO yang sudah terikat jadwal lain jangan disentuh.
      if (linkedScheduleId && linkedScheduleId !== scheduleDocumentId) continue;
      teams.push(row);
      seen.add(docId);
    }
  }

  let deletedTeamCount = 0;
  let updatedTeamCount = 0;
  const remainingDates = new Set<string>();

  for (const team of teams) {
    const docId = String(team.documentId ?? '').trim();
    if (!docId) continue;
    const before = parseSurveyTeamScheduledSlots(team.scheduled_date);
    const remainingRaw = filterSurveyTeamSlotsRemovingDate(team.scheduled_date, date);
    if (remainingRaw.length === before.length) {
      for (const slot of before) remainingDates.add(slot.date);
      continue;
    }
    if (remainingRaw.length === 0) {
      await strapiDeleteSurveyTeam(opts.jwt, docId);
      deletedTeamCount += 1;
      continue;
    }
    await strapiUpdateSurveyTeam(opts.jwt, docId, { scheduled_date: remainingRaw });
    updatedTeamCount += 1;
    for (const slot of parseSurveyTeamScheduledSlots(remainingRaw)) {
      remainingDates.add(slot.date);
    }
  }

  return {
    remainingDates: [...remainingDates].sort(),
    deletedTeamCount,
    updatedTeamCount,
  };
}

export async function strapiDeleteSurveyTeam(jwt: string, documentId: string): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/survey-teams/${encodeURIComponent(documentId)}`, {
    method: 'DELETE',
    headers: strapiAuthHeaders(jwt),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(body, res.status));
  }
}

function parseQuotationOneResponse(res: Response, body: unknown): StrapiQuotationRow {
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  const b = asRecord(body);
  const data = b?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Invalid Strapi quotation response');
  }
  return normalizeQuotationEntry(data as Record<string, unknown>);
}

export async function strapiCreateQuotation(
  jwt: string,
  data: StrapiQuotationCreateData,
): Promise<StrapiQuotationRow> {
  const res = await fetch(`${STRAPI_URL}/api/quotations?populate[customer]=true`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      data: {
        ...data,
      },
    }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseQuotationOneResponse(res, body);
}

export async function strapiGetQuotation(jwt: string, documentId: string): Promise<StrapiQuotationRow> {
  /** Strapi 5: jangan gabungkan `populate=customer` dengan `populate[relation][…]` — gunakan satu bentuk bertingkat supaya customer tetap ikut dimuat. */
  const qs =
    'populate[customer]=true&populate[sample_informations][sort][0]=createdAt:asc' +
    '&populate[sample_informations][fields][0]=documentId&populate[sample_informations][fields][1]=sample_point_count&populate[sample_informations][fields][2]=harga' +
    '&populate[sample_informations][populate][parameter_ujis][fields][0]=documentId' +
    '&populate[sample_informations][populate][parameter_ujis][fields][1]=nama_parameter' +
    '&populate[sample_informations][populate][parameter_ujis][fields][2]=simbol' +
    '&populate[sample_informations][populate][jenis_pengukuran][fields][0]=documentId' +
    '&populate[sample_informations][populate][jenis_pengukuran][fields][1]=nama_jenis' +
    '&populate[sample_informations][populate][jenis_pengukuran][fields][2]=kode_jenis' +
    '&populate[sample_informations][populate][jenis_pengukuran][sort][0]=nama_jenis:asc' +
    jenisPengukuranGroupingPopulateQs('populate[sample_informations][populate][jenis_pengukuran]') +
    '&populate[sample_informations][populate][baku_mutu][fields][0]=documentId' +
    '&populate[sample_informations][populate][baku_mutu][fields][1]=kode' +
    '&populate[sample_informations][populate][baku_mutu][fields][2]=nama_baku_mutu' +
    '&populate[sample_informations][populate][baku_mutu][fields][3]=judul' +
    '&populate[sample_informations][populate][baku_mutu][sort][0]=nama_baku_mutu:asc' +
    '&populate[quotation_sample_informations][sort][0]=createdAt:asc' +
    '&populate[quotation_sample_informations][fields][0]=documentId&populate[quotation_sample_informations][fields][1]=sample_point_count&populate[quotation_sample_informations][fields][2]=harga' +
    '&populate[quotation_sample_informations][populate][parameter_ujis][fields][0]=documentId' +
    '&populate[quotation_sample_informations][populate][parameter_ujis][fields][1]=nama_parameter' +
    '&populate[quotation_sample_informations][populate][parameter_ujis][fields][2]=simbol' +
    '&populate[quotation_sample_informations][populate][jenis_pengukuran][fields][0]=documentId' +
    '&populate[quotation_sample_informations][populate][jenis_pengukuran][fields][1]=nama_jenis' +
    '&populate[quotation_sample_informations][populate][jenis_pengukuran][fields][2]=kode_jenis' +
    '&populate[quotation_sample_informations][populate][jenis_pengukuran][sort][0]=nama_jenis:asc' +
    jenisPengukuranGroupingPopulateQs(
      'populate[quotation_sample_informations][populate][jenis_pengukuran]',
    ) +
    '&populate[quotation_sample_informations][populate][baku_mutu][fields][0]=documentId' +
    '&populate[quotation_sample_informations][populate][baku_mutu][fields][1]=kode' +
    '&populate[quotation_sample_informations][populate][baku_mutu][fields][2]=nama_baku_mutu' +
    '&populate[quotation_sample_informations][populate][baku_mutu][fields][3]=judul' +
    '&populate[quotation_sample_informations][populate][baku_mutu][sort][0]=nama_baku_mutu:asc' +
    '&populate[quotation_sample_informations][populate][quotation_group][fields][0]=documentId' +
    '&populate[quotation_sample_informations][populate][quotation_group][fields][1]=recurring_type' +
    '&populate[quotation_sample_informations][populate][quotation_group][fields][2]=recurring_interval' +
    '&populate[quotation_sample_informations][populate][quotation_group][fields][3]=recurring_count_override' +
    '&populate[quotation_sample_informations][populate][quotation_group][fields][4]=recurring_start_date' +
    '&populate[quotation_sample_informations][populate][quotation_group][populate][customer][fields][0]=documentId' +
    '&populate[quotation_sample_informations][populate][quotation_group][populate][customer][fields][1]=customer_number' +
    '&populate[quotation_sample_informations][populate][quotation_group][populate][customer][fields][2]=name' +
    '&populate[quotation_sample_informations][populate][quotation_group][populate][quotation_recurring_events][sort][0]=event_index:asc' +
    '&populate[quotation_sample_informations][populate][quotation_group][populate][quotation_recurring_events][fields][0]=documentId' +
    '&populate[quotation_sample_informations][populate][quotation_group][populate][quotation_recurring_events][fields][1]=event_index' +
    '&populate[quotation_sample_informations][populate][quotation_group][populate][quotation_recurring_events][fields][2]=scheduled_date' +
    '&populate[assignee_user][fields][0]=documentId&populate[assignee_user][fields][1]=id&populate[assignee_user][fields][2]=username&populate[assignee_user][fields][3]=email' +
    '&populate[approved_by_user][fields][0]=documentId&populate[approved_by_user][fields][1]=id&populate[approved_by_user][fields][2]=username&populate[approved_by_user][fields][3]=email' +
    quotationPopulateCompanyBranchQs +
    '&populate[quotation_other_costs][sort][0]=createdAt:asc' +
    '&populate[quotation_other_costs][fields][0]=documentId&populate[quotation_other_costs][fields][1]=description' +
    '&populate[quotation_other_costs][fields][2]=quantity&populate[quotation_other_costs][fields][3]=cost_price' +
    '&populate[quotation_other_costs][populate][quotation_group][fields][0]=documentId' +
    '&populate[quotation_groups][sort][0]=createdAt:asc' +
    '&populate[quotation_groups][fields][0]=documentId' +
    '&populate[quotation_groups][fields][1]=recurring_type' +
    '&populate[quotation_groups][fields][2]=recurring_interval' +
    '&populate[quotation_groups][fields][3]=recurring_count_override' +
    '&populate[quotation_groups][fields][4]=recurring_start_date' +
    '&populate[quotation_groups][populate][customer]=true' +
    '&populate[quotation_groups][populate][quotation_recurring_events][sort][0]=event_index:asc' +
    '&populate[quotation_groups][populate][quotation_recurring_events][fields][0]=documentId' +
    '&populate[quotation_groups][populate][quotation_recurring_events][fields][1]=event_index' +
    '&populate[quotation_groups][populate][quotation_recurring_events][fields][2]=scheduled_date' +
    '&populate[quotation_groups][populate][quotation_recurring_events][populate][sampling_schedule][fields][0]=documentId' +
    '&populate[quotation_groups][populate][quotation_recurring_events][populate][sampling_schedule][populate][work_order][fields][0]=documentId' +
    '&populate[quotation_groups][populate][quotation_recurring_events][populate][sampling_schedule][populate][work_order][fields][1]=job_number';
  const res = await fetch(
    `${STRAPI_URL}/api/quotations/${encodeURIComponent(documentId)}?${qs}`,
    {
      headers: strapiAuthHeaders(jwt),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseQuotationOneResponse(res, body);
}

export type QuotationBundleSampleLine = {
  jenis_pengukuran: string;
  parameter_uji: string;
  baku_mutu: string;
  sample_point_count: number;
  harga?: number | null;
};

export type QuotationBundleOtherCostLine = {
  description: string;
  quantity: number;
  cost_price: number;
};

export type QuotationBundleSampleGroup = {
  customer?: string;
  recurring_type?: 'weekly' | 'monthly' | 'yearly';
  recurring_interval?: number;
  recurring_count_override?: number | null;
  recurring_start_date?: string | null;
  sample_lines: QuotationBundleSampleLine[];
  other_costs?: QuotationBundleOtherCostLine[];
};

/** Satu panggilan: `POST /api/quotations/bundle` — quotation + baris sampel (`quotation_sample_information`) + biaya lain. */
export type StrapiQuotationCreateBundleRequest = {
  quotation: StrapiQuotationCreateData;
  sample_lines?: QuotationBundleSampleLine[];
  sample_groups?: QuotationBundleSampleGroup[];
  other_costs?: QuotationBundleOtherCostLine[];
};

export async function strapiCreateQuotationBundle(
  jwt: string,
  req: StrapiQuotationCreateBundleRequest,
): Promise<StrapiQuotationRow> {
  const res = await fetch(`${STRAPI_URL}/api/quotations/bundle`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(req),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseQuotationOneResponse(res, body);
}

/** Copy existing quotation into a new draft quotation. */
export async function strapiCopyQuotation(
  jwt: string,
  sourceDocumentId: string,
): Promise<StrapiQuotationRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/quotations/${encodeURIComponent(sourceDocumentId)}/copy`,
    {
      method: 'POST',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseQuotationOneResponse(res, body);
}

export async function strapiUpdateQuotation(
  jwt: string,
  documentId: string,
  data: StrapiQuotationUpdateData,
): Promise<StrapiQuotationRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/quotations/${encodeURIComponent(documentId)}?populate[customer]=true`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseQuotationOneResponse(res, body);
}

/** Poll & buka PDF quotation yang sudah di-generate saat SPV approve (bukan saat klik). */
export type QuotationPdfJobStatus = 'queued' | 'processing' | 'completed' | 'failed';

export type QuotationPdfJobResponse = {
  jobId: string;
  documentId: string;
  status: QuotationPdfJobStatus;
  quotationNumber?: string;
  error?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
};

const PDF_JOB_POLL_MS = 2000;
const PDF_JOB_MAX_WAIT_MS = 10 * 60 * 1000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function fetchLatestQuotationPdfJob(
  jwt: string,
  documentId: string,
): Promise<QuotationPdfJobResponse> {
  const res = await fetch(
    `${STRAPI_URL}/api/quotations/${encodeURIComponent(documentId)}/pdf/jobs/latest`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  const job = asRecord(body)?.data as QuotationPdfJobResponse | undefined;
  if (!job?.jobId?.trim()) {
    throw new Error('Respons status PDF tidak valid.');
  }
  return job;
}

async function fetchQuotationPdfJobById(
  jwt: string,
  jobId: string,
): Promise<QuotationPdfJobResponse> {
  const res = await fetch(
    `${STRAPI_URL}/api/quotations/pdf/jobs/${encodeURIComponent(jobId)}`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  const job = asRecord(body)?.data as QuotationPdfJobResponse | undefined;
  if (!job?.status) {
    throw new Error('Respons status PDF tidak valid.');
  }
  return job;
}

/** Generate ulang PDF quotation (antre + poll sampai selesai). */
export async function strapiRegenerateQuotationPdf(
  jwt: string,
  documentId: string,
  opts?: { onStatus?: (status: QuotationPdfJobStatus) => void },
): Promise<QuotationPdfJobResponse> {
  const res = await fetch(
    `${STRAPI_URL}/api/quotations/${encodeURIComponent(documentId)}/pdf/regenerate`,
    {
      method: 'POST',
      headers: strapiAuthHeaders(jwt),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  let job = asRecord(body)?.data as QuotationPdfJobResponse | undefined;
  if (!job?.jobId?.trim()) {
    throw new Error('Respons regenerate PDF tidak valid.');
  }
  opts?.onStatus?.(job.status);

  if (job.status === 'completed') return job;
  if (job.status === 'failed') {
    throw new Error(job.error ?? 'Gagal membuat PDF quotation.');
  }

  const deadline = Date.now() + PDF_JOB_MAX_WAIT_MS;
  while (Date.now() < deadline) {
    await sleep(PDF_JOB_POLL_MS);
    job = await fetchQuotationPdfJobById(jwt, job.jobId);
    opts?.onStatus?.(job.status);

    if (job.status === 'failed') {
      throw new Error(job.error ?? 'Gagal membuat PDF quotation.');
    }
    if (job.status === 'completed') {
      return job;
    }
  }

  throw new Error('Timeout menunggu PDF quotation selesai diproses.');
}

export type QuotationPdfKind = 'quotation' | 'kup';

async function fetchLatestKupPdfJob(
  jwt: string,
  documentId: string,
): Promise<QuotationPdfJobResponse> {
  const res = await fetch(
    `${STRAPI_URL}/api/quotations/${encodeURIComponent(documentId)}/kup-pdf/jobs/latest`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  const job = asRecord(body)?.data as QuotationPdfJobResponse | undefined;
  if (!job?.jobId?.trim()) {
    throw new Error('Respons status PDF KUP tidak valid.');
  }
  return job;
}

async function fetchKupPdfJobById(
  jwt: string,
  jobId: string,
): Promise<QuotationPdfJobResponse> {
  const res = await fetch(
    `${STRAPI_URL}/api/quotations/kup-pdf/jobs/${encodeURIComponent(jobId)}`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  const job = asRecord(body)?.data as QuotationPdfJobResponse | undefined;
  if (!job?.status) {
    throw new Error('Respons status PDF KUP tidak valid.');
  }
  return job;
}

async function downloadKupPdfBlob(
  jwt: string,
  documentId: string,
  quotationNumber?: string,
): Promise<QuotationPdfFetchResult> {
  const pdfRes = await fetch(
    `${STRAPI_URL}/api/quotations/${encodeURIComponent(documentId)}/kup-pdf/download`,
    { headers: strapiAuthHeaders(jwt) },
  );
  if (!pdfRes.ok) {
    const errBody = (await pdfRes.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, pdfRes.status));
  }
  const blob = await pdfRes.blob();
  return { blob, quotationNumber };
}

export async function strapiRegenerateKupPdf(
  jwt: string,
  documentId: string,
  opts?: { onStatus?: (status: QuotationPdfJobStatus) => void },
): Promise<QuotationPdfJobResponse> {
  const res = await fetch(
    `${STRAPI_URL}/api/quotations/${encodeURIComponent(documentId)}/kup-pdf/regenerate`,
    {
      method: 'POST',
      headers: strapiAuthHeaders(jwt),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  let job = asRecord(body)?.data as QuotationPdfJobResponse | undefined;
  if (!job?.jobId?.trim()) {
    throw new Error('Respons regenerate PDF KUP tidak valid.');
  }
  opts?.onStatus?.(job.status);

  if (job.status === 'completed') return job;
  if (job.status === 'failed') {
    throw new Error(job.error ?? 'Gagal membuat PDF KUP.');
  }

  const deadline = Date.now() + PDF_JOB_MAX_WAIT_MS;
  while (Date.now() < deadline) {
    await sleep(PDF_JOB_POLL_MS);
    job = await fetchKupPdfJobById(jwt, job.jobId);
    opts?.onStatus?.(job.status);

    if (job.status === 'failed') {
      throw new Error(job.error ?? 'Gagal membuat PDF KUP.');
    }
    if (job.status === 'completed') {
      return job;
    }
  }

  throw new Error('Timeout menunggu PDF KUP selesai diproses.');
}

export async function strapiFetchKupPdf(
  jwt: string,
  documentId: string,
  opts?: { onStatus?: (status: QuotationPdfJobStatus) => void },
): Promise<QuotationPdfFetchResult> {
  let job = await fetchLatestKupPdfJob(jwt, documentId);
  opts?.onStatus?.(job.status);

  if (job.status === 'completed') {
    return downloadKupPdfBlob(jwt, documentId, job.quotationNumber);
  }

  if (job.status === 'failed') {
    throw new Error(job.error ?? 'Gagal membuat PDF KUP.');
  }

  const deadline = Date.now() + PDF_JOB_MAX_WAIT_MS;
  while (Date.now() < deadline) {
    await sleep(PDF_JOB_POLL_MS);
    job = await fetchKupPdfJobById(jwt, job.jobId);
    opts?.onStatus?.(job.status);

    if (job.status === 'failed') {
      throw new Error(job.error ?? 'Gagal membuat PDF KUP.');
    }
    if (job.status === 'completed') {
      return downloadKupPdfBlob(jwt, documentId, job.quotationNumber);
    }
  }

  throw new Error('Timeout menunggu PDF KUP selesai diproses.');
}

export type QuotationPdfFetchResult = {
  blob: Blob;
  quotationNumber?: string;
};

async function downloadQuotationPdfBlob(
  jwt: string,
  documentId: string,
  quotationNumber?: string,
): Promise<QuotationPdfFetchResult> {
  const pdfRes = await fetch(
    `${STRAPI_URL}/api/quotations/${encodeURIComponent(documentId)}/pdf/download`,
    { headers: strapiAuthHeaders(jwt) },
  );
  if (!pdfRes.ok) {
    const errBody = (await pdfRes.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, pdfRes.status));
  }
  const blob = await pdfRes.blob();
  return { blob, quotationNumber };
}

export async function strapiFetchQuotationPdf(
  jwt: string,
  documentId: string,
  opts?: { onStatus?: (status: QuotationPdfJobStatus) => void },
): Promise<QuotationPdfFetchResult> {
  let job = await fetchLatestQuotationPdfJob(jwt, documentId);
  opts?.onStatus?.(job.status);

  if (job.status === 'completed') {
    return downloadQuotationPdfBlob(jwt, documentId, job.quotationNumber);
  }

  if (job.status === 'failed') {
    throw new Error(job.error ?? 'Gagal membuat PDF quotation.');
  }

  const deadline = Date.now() + PDF_JOB_MAX_WAIT_MS;
  while (Date.now() < deadline) {
    await sleep(PDF_JOB_POLL_MS);
    job = await fetchQuotationPdfJobById(jwt, job.jobId);
    opts?.onStatus?.(job.status);

    if (job.status === 'failed') {
      throw new Error(job.error ?? 'Gagal membuat PDF quotation.');
    }
    if (job.status === 'completed') {
      return downloadQuotationPdfBlob(jwt, documentId, job.quotationNumber);
    }
  }

  throw new Error('Timeout menunggu PDF quotation selesai diproses.');
}

/** Buka preview HTML quotation di tab browser baru (untuk development styling). */
export async function strapiOpenQuotationPdfHtmlPreview(jwt: string, documentId: string): Promise<void> {
  const res = await fetch(
    `${STRAPI_URL}/api/quotations/${encodeURIComponent(documentId)}/pdf/preview-html`,
    { headers: strapiAuthHeaders(jwt) },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
  const html = await res.text();
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const tab = window.open(url, '_blank', 'noopener,noreferrer');
  if (!tab) {
    URL.revokeObjectURL(url);
    throw new Error('Pop-up diblokir browser. Izinkan pop-up untuk Custom PDF preview.');
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export type StrapiQuotationOtherCostWrite = {
  quotation: { connect: string[] };
  quotation_group?: { connect: string[] };
  description: string;
  quantity: number;
  cost_price: number;
};

export type StrapiQuotationOtherCostUpdateData = Partial<
  Pick<StrapiQuotationOtherCostWrite, 'description' | 'quantity' | 'cost_price' | 'quotation_group'>
>;

function parseQuotationOtherCostOneResponse(res: Response, body: unknown): StrapiQuotationOtherCostRow {
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  const b = asRecord(body);
  const data = b?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Invalid Strapi quotation-other-cost response');
  }
  return normalizeQuotationOtherCostEntry(data as Record<string, unknown>);
}

export async function strapiCreateQuotationOtherCost(
  jwt: string,
  data: StrapiQuotationOtherCostWrite,
): Promise<StrapiQuotationOtherCostRow> {
  const res = await fetch(`${STRAPI_URL}/api/quotation-other-costs`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseQuotationOtherCostOneResponse(res, body);
}

export async function strapiUpdateQuotationOtherCost(
  jwt: string,
  documentId: string,
  data: StrapiQuotationOtherCostUpdateData,
): Promise<StrapiQuotationOtherCostRow> {
  const res = await fetch(`${STRAPI_URL}/api/quotation-other-costs/${encodeURIComponent(documentId)}`, {
    method: 'PUT',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseQuotationOtherCostOneResponse(res, body);
}

export async function strapiDeleteQuotationOtherCost(jwt: string, documentId: string): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/quotation-other-costs/${encodeURIComponent(documentId)}`, {
    method: 'DELETE',
    headers: strapiAuthHeaders(jwt),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(body, res.status));
  }
}



/** `GET /api/quotations` list filters (`$and` + nested `$or` where noted). */
export type QuotationsListFilters = {
  quotationNumber: string;
  /** Substring nama atau nomor pelanggan (OR). */
  customer: string;
  /** Substring username atau email user penanggung jawab (OR). */
  assigneeContains: string;
  /** Substring username atau email user persetujuan (OR). */
  approverContains: string;
  status: string;
  /** '' = semua; kontrak = `is_contract`; non kontrak = bukan kontrak */
  contract: '' | 'contract' | 'non_contract';
  /** '' = semua; yes/no → `is_consultant_quote` */
  consultantQuote: '' | 'yes' | 'no';
};

export const emptyQuotationsListFilters = Object.freeze<QuotationsListFilters>({
  quotationNumber: '',
  customer: '',
  assigneeContains: '',
  approverContains: '',
  status: '',
  contract: '',
  consultantQuote: '',
});

/** Server sort for quotation list (`sort` query param). `field` matches DataGrid column `field`. */
export type QuotationsListSort = {
  field: string;
  direction: 'asc' | 'desc';
};

export const defaultQuotationsListSort = Object.freeze<QuotationsListSort>({
  field: 'updatedAt',
  direction: 'desc',
});

const QUOTATION_LIST_SORT_STRAPI_FIELD: Record<string, string> = {
  quotation_number: 'quotation_number',
  request_date: 'request_date',
  customer: 'customer.name',
  progress_status: 'progress_status',
  is_contract: 'is_contract',
  is_consultant_quote: 'is_consultant_quote',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
};

export function quotationsListSortToStrapiParam(sort: QuotationsListSort): string {
  const strapiField = QUOTATION_LIST_SORT_STRAPI_FIELD[sort.field] ?? sort.field;
  return `${strapiField}:${sort.direction}`;
}

export type QuotationsListVisibility = 'all' | 'marketing_pic' | 'marketing_spv' | 'admin_workorder';

/** PIC marketing: list di-scope ke `assignee_user` user yang login. */
export type QuotationsListPicAssignee = {
  documentId?: string | null;
  id?: number | null;
};

export const ADMIN_WORKORDER_QUOTATION_LIST_STATUS = 'customer_approved' as const;

export function adminWorkorderQuotationsListFilters(): QuotationsListFilters {
  return {
    ...emptyQuotationsListFilters,
    status: ADMIN_WORKORDER_QUOTATION_LIST_STATUS,
  };
}

export function quotationsListPicAssigneeReady(
  pic?: QuotationsListPicAssignee | null,
): boolean {
  const docId = pic?.documentId?.trim() ?? '';
  const id = pic?.id;
  return Boolean(docId) || (id != null && Number.isFinite(id));
}

export function quotationsListPicAssigneeKey(
  pic?: QuotationsListPicAssignee | null,
): string {
  const docId = pic?.documentId?.trim() ?? '';
  const id = pic?.id;
  return `${docId}:${id != null && Number.isFinite(id) ? String(id) : ''}`;
}

export function appendQuotationsRoleVisibility(
  params: URLSearchParams,
  visibility: QuotationsListVisibility | undefined,
  startIndex: number,
  picAssignee?: QuotationsListPicAssignee,
): number {
  let i = startIndex;
  if (visibility === 'marketing_pic') {
    const docId = picAssignee?.documentId?.trim() ?? '';
    const id = picAssignee?.id;
    const hasId = id != null && Number.isFinite(id);
    if (docId && hasId) {
      params.append(`filters[$and][${i}][$or][0][assignee_user][documentId][$eq]`, docId);
      params.append(`filters[$and][${i}][$or][1][assignee_user][id][$eq]`, String(id));
      i += 1;
    } else if (docId) {
      params.append(`filters[$and][${i}][assignee_user][documentId][$eq]`, docId);
      i += 1;
    } else if (hasId) {
      params.append(`filters[$and][${i}][assignee_user][id][$eq]`, String(id));
      i += 1;
    }
  }
  if (visibility === 'marketing_spv') {
    params.append(`filters[$and][${i}][progress_status][$ne]`, 'draft');
    i += 1;
  }
  if (visibility === 'admin_workorder') {
    params.append(`filters[$and][${i}][progress_status][$eq]`, ADMIN_WORKORDER_QUOTATION_LIST_STATUS);
    i += 1;
  }
  return i;
}

function appendQuotationsListFilters(params: URLSearchParams, f: QuotationsListFilters): number {
  const nz = (s: string) => s.trim();
  let i = 0;
  if (nz(f.quotationNumber)) {
    params.append(`filters[$and][${i}][quotation_number][$containsi]`, nz(f.quotationNumber));
    i += 1;
  }
  if (nz(f.customer)) {
    const q = nz(f.customer);
    params.append(`filters[$and][${i}][$or][0][customer][name][$containsi]`, q);
    params.append(`filters[$and][${i}][$or][1][customer][customer_number][$containsi]`, q);
    i += 1;
  }
  if (nz(f.assigneeContains)) {
    const q = nz(f.assigneeContains);
    params.append(`filters[$and][${i}][$or][0][assignee_user][username][$containsi]`, q);
    params.append(`filters[$and][${i}][$or][1][assignee_user][email][$containsi]`, q);
    i += 1;
  }
  if (nz(f.approverContains)) {
    const q = nz(f.approverContains);
    params.append(`filters[$and][${i}][$or][0][approved_by_user][username][$containsi]`, q);
    params.append(`filters[$and][${i}][$or][1][approved_by_user][email][$containsi]`, q);
    i += 1;
  }
  if (nz(f.status)) {
    params.append(`filters[$and][${i}][progress_status][$eq]`, nz(f.status));
    i += 1;
  }
  if (f.contract === 'contract') {
    params.append(`filters[$and][${i}][is_contract][$eq]`, 'true');
    i += 1;
  } else if (f.contract === 'non_contract') {
    params.append(`filters[$and][${i}][is_contract][$eq]`, 'false');
    i += 1;
  }
  if (f.consultantQuote === 'yes') {
    params.append(`filters[$and][${i}][is_consultant_quote][$eq]`, 'true');
    i += 1;
  } else if (f.consultantQuote === 'no') {
    params.append(`filters[$and][${i}][is_consultant_quote][$eq]`, 'false');
    i += 1;
  }
  return i;
}

/** `GET /api/work-orders` list filters. */
export type WorkOrdersListFilters = {
  quotationRefs: string;
  customer: string;
  workOrderDate: string;
  status: string;
  workOrderDocumentId: string;
  /** Hanya WO di mana user ini ada di `survey_teams` (petugas sampling). */
  surveyTeamUserId?: number | null;
};

export const emptyWorkOrdersListFilters = Object.freeze<WorkOrdersListFilters>({
  quotationRefs: '',
  customer: '',
  workOrderDate: '',
  status: '',
  workOrderDocumentId: '',
});

function appendWorkOrdersListFilters(params: URLSearchParams, f: WorkOrdersListFilters): void {
  const nz = (s: string) => s.trim();
  let i = 0;
  if (nz(f.quotationRefs)) {
    const q = nz(f.quotationRefs);
    params.append(`filters[$and][${i}][$or][0][job_number][$containsi]`, q);
    params.append(`filters[$and][${i}][$or][1][quotation][quotation_number][$containsi]`, q);
    params.append(`filters[$and][${i}][$or][2][quotation][documentId][$containsi]`, q);
    i += 1;
  }
  if (nz(f.customer)) {
    const q = nz(f.customer);
    params.append(`filters[$and][${i}][$or][0][quotation][customer][name][$containsi]`, q);
    params.append(`filters[$and][${i}][$or][1][quotation][customer][customer_number][$containsi]`, q);
    params.append(
      `filters[$and][${i}][$or][2][sampling_schedules][quotation_group][customer][name][$containsi]`,
      q,
    );
    params.append(
      `filters[$and][${i}][$or][3][sampling_schedules][quotation_group][customer][customer_number][$containsi]`,
      q,
    );
    i += 1;
  }
  if (nz(f.workOrderDate)) {
    params.append(`filters[$and][${i}][work_order_date][$containsi]`, nz(f.workOrderDate));
    i += 1;
  }
  if (nz(f.status)) {
    params.append(`filters[$and][${i}][progress_status][$eq]`, nz(f.status));
    i += 1;
  }
  if (nz(f.workOrderDocumentId)) {
    params.append(`filters[$and][${i}][documentId][$containsi]`, nz(f.workOrderDocumentId));
    i += 1;
  }
  if (f.surveyTeamUserId != null && Number.isFinite(f.surveyTeamUserId)) {
    params.append(`filters[$and][${i}][survey_teams][user_idx][id][$eq]`, String(f.surveyTeamUserId));
    i += 1;
  }
}

export async function strapiListQuotations(
  jwt: string,
  options: {
    filters?: QuotationsListFilters;
    sort?: QuotationsListSort;
    /** Strapi pagination (1-based). */
    page?: number;
    pageSize?: number;
    visibility?: QuotationsListVisibility;
    picAssignee?: QuotationsListPicAssignee;
  } = {},
): Promise<QuotationsListResult> {
  const filters = options.filters ?? emptyQuotationsListFilters;
  const sort = options.sort ?? defaultQuotationsListSort;
  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? 200;
  const params = new URLSearchParams({
    'pagination[pageSize]': String(pageSize),
    'pagination[page]': String(page),
    sort: quotationsListSortToStrapiParam(sort),
  });
  params.append('populate[customer]', 'true');
  params.append('populate[assignee_user][fields][0]', 'documentId');
  params.append('populate[assignee_user][fields][1]', 'id');
  params.append('populate[assignee_user][fields][2]', 'username');
  params.append('populate[assignee_user][fields][3]', 'email');
  params.append('populate[approved_by_user][fields][0]', 'documentId');
  params.append('populate[approved_by_user][fields][1]', 'id');
  params.append('populate[approved_by_user][fields][2]', 'username');
  params.append('populate[approved_by_user][fields][3]', 'email');
  params.append('populate[company_branch][fields][0]', 'documentId');
  params.append('populate[company_branch][fields][1]', 'name');
  params.append('populate[company_branch][fields][2]', 'address');
  params.append('populate[company_branch][fields][3]', 'job_code');
  params.append('populate[sampling_schedules][fields][0]', 'documentId');
  params.append('populate[sampling_schedules][fields][1]', 'progress_status');
  params.append('populate[sampling_schedules][populate][work_order][fields][0]', 'documentId');
  params.append('populate[sampling_schedules][populate][work_order][fields][1]', 'job_number');
  params.append('populate[sampling_plans][fields][0]', 'documentId');
  params.append('populate[sampling_plans][populate][quotation_group][fields][0]', 'documentId');
  params.append('populate[sampling_plans][populate][work_order][fields][0]', 'documentId');
  params.append('populate[sampling_plans][populate][work_order][fields][1]', 'job_number');
  params.append(
    'populate[sampling_plans][populate][work_order][populate][laboratory_results][fields][0]',
    'testing_status',
  );
  params.append('populate[quotation_groups][fields][0]', 'documentId');
  params.append('populate[quotation_groups][populate][quotation_recurring_events][fields][0]', 'documentId');
  params.append('populate[quotation_groups][populate][quotation_recurring_events][fields][1]', 'event_index');
  params.append('populate[quotation_groups][populate][quotation_recurring_events][fields][2]', 'scheduled_date');
  params.append(
    'populate[quotation_groups][populate][quotation_recurring_events][populate][sampling_schedule][fields][0]',
    'documentId',
  );
  params.append(
    'populate[quotation_groups][populate][quotation_recurring_events][populate][sampling_schedule][populate][work_order][fields][0]',
    'documentId',
  );
  params.append(
    'populate[quotation_groups][populate][quotation_recurring_events][populate][sampling_schedule][populate][work_order][fields][1]',
    'job_number',
  );
  const nextAnd = appendQuotationsListFilters(params, filters);
  appendQuotationsRoleVisibility(params, options.visibility, nextAnd, options.picAssignee);
  const res = await fetch(`${STRAPI_URL}/api/quotations?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows, meta } = parseListBody(res, body);
  const total = meta.pagination?.total ?? rows.length;
  return { rows: rows.map((r) => normalizeQuotationEntry(r)), total };
}

/** Nest `grouping` (incl. v1 `perencanaan_sampling`) under a `jenis_pengukuran` populate path. */
function jenisPengukuranGroupingPopulateQs(jenisPengukuranPath: string): string {
  const g = `${jenisPengukuranPath}[populate][grouping]`;
  return (
    `&${g}[fields][0]=documentId` +
    `&${g}[fields][1]=nama_grouping` +
    `&${g}[fields][2]=air` +
    `&${g}[fields][3]=perencanaan_sampling`
  );
}

const workOrderPopulateQs =
  'populate[quotation][populate][customer]=true' +
  '&populate[quotation][populate][sample_informations][sort][0]=createdAt:asc' +
  '&populate[quotation][populate][sample_informations][fields][0]=documentId' +
  '&populate[quotation][populate][sample_informations][fields][1]=sample_point_count' +
  '&populate[quotation][populate][sample_informations][fields][2]=harga' +
  '&populate[quotation][populate][sample_informations][populate][parameter_ujis][fields][0]=documentId' +
  '&populate[quotation][populate][sample_informations][populate][parameter_ujis][fields][1]=nama_parameter' +
  '&populate[quotation][populate][sample_informations][populate][parameter_ujis][fields][2]=simbol' +
  '&populate[quotation][populate][sample_informations][populate][jenis_pengukuran][fields][0]=documentId' +
  '&populate[quotation][populate][sample_informations][populate][jenis_pengukuran][fields][1]=nama_jenis' +
  '&populate[quotation][populate][sample_informations][populate][jenis_pengukuran][fields][2]=kode_jenis' +
  '&populate[quotation][populate][sample_informations][populate][jenis_pengukuran][sort][0]=nama_jenis:asc' +
  jenisPengukuranGroupingPopulateQs(
    'populate[quotation][populate][sample_informations][populate][jenis_pengukuran]',
  ) +
  '&populate[quotation][populate][sample_informations][populate][baku_mutu][fields][0]=documentId' +
  '&populate[quotation][populate][sample_informations][populate][baku_mutu][fields][1]=kode' +
  '&populate[quotation][populate][sample_informations][populate][baku_mutu][fields][2]=nama_baku_mutu' +
  '&populate[quotation][populate][sample_informations][populate][baku_mutu][fields][3]=judul' +
  '&populate[quotation][populate][sample_informations][populate][baku_mutu][sort][0]=nama_baku_mutu:asc' +
  '&populate[quotation][populate][quotation_sample_informations][sort][0]=createdAt:asc' +
  '&populate[quotation][populate][quotation_sample_informations][fields][0]=documentId' +
  '&populate[quotation][populate][quotation_sample_informations][fields][1]=sample_point_count' +
  '&populate[quotation][populate][quotation_sample_informations][fields][2]=harga' +
  '&populate[quotation][populate][quotation_sample_informations][populate][parameter_ujis][fields][0]=documentId' +
  '&populate[quotation][populate][quotation_sample_informations][populate][parameter_ujis][fields][1]=nama_parameter' +
  '&populate[quotation][populate][quotation_sample_informations][populate][parameter_ujis][fields][2]=simbol' +
  '&populate[quotation][populate][quotation_sample_informations][populate][jenis_pengukuran][fields][0]=documentId' +
  '&populate[quotation][populate][quotation_sample_informations][populate][jenis_pengukuran][fields][1]=nama_jenis' +
  '&populate[quotation][populate][quotation_sample_informations][populate][jenis_pengukuran][fields][2]=kode_jenis' +
  '&populate[quotation][populate][quotation_sample_informations][populate][jenis_pengukuran][sort][0]=nama_jenis:asc' +
  jenisPengukuranGroupingPopulateQs(
    'populate[quotation][populate][quotation_sample_informations][populate][jenis_pengukuran]',
  ) +
  '&populate[quotation][populate][quotation_sample_informations][populate][baku_mutu][fields][0]=documentId' +
  '&populate[quotation][populate][quotation_sample_informations][populate][baku_mutu][fields][1]=kode' +
  '&populate[quotation][populate][quotation_sample_informations][populate][baku_mutu][fields][2]=nama_baku_mutu' +
  '&populate[quotation][populate][quotation_sample_informations][populate][baku_mutu][fields][3]=judul' +
  '&populate[quotation][populate][quotation_sample_informations][populate][baku_mutu][sort][0]=nama_baku_mutu:asc' +
  '&populate[sampling_schedules][fields][0]=documentId' +
  '&populate[sampling_schedules][fields][1]=schedule_kind' +
  '&populate[sampling_schedules][fields][2]=progress_status' +
  '&populate[sampling_schedules][populate][quotation_group][fields][0]=documentId' +
  '&populate[sampling_schedules][populate][quotation_group][populate][customer][fields][0]=documentId' +
  '&populate[sampling_schedules][populate][quotation_group][populate][customer][fields][1]=name' +
  '&populate[sampling_schedules][populate][quotation_group][populate][customer][fields][2]=customer_number';

/** Deep populate: quotation sample lines → points → attachments / surveyors / test results (+ env rows). */
const workOrderDetailPopulateQs =
  'populate[quotation][populate][customer]=true' +
  '&populate[quotation][populate][sample_informations][populate][jenis_pengukuran][fields][0]=documentId' +
  '&populate[quotation][populate][sample_informations][populate][jenis_pengukuran][fields][1]=nama_jenis' +
  '&populate[quotation][populate][sample_informations][populate][jenis_pengukuran][fields][2]=kode_jenis' +
  '&populate[quotation][populate][sample_informations][populate][jenis_pengukuran][sort][0]=nama_jenis:asc' +
  jenisPengukuranGroupingPopulateQs(
    'populate[quotation][populate][sample_informations][populate][jenis_pengukuran]',
  ) +
  '&populate[quotation][populate][sample_informations][populate][baku_mutu][fields][0]=documentId' +
  '&populate[quotation][populate][sample_informations][populate][baku_mutu][fields][1]=kode' +
  '&populate[quotation][populate][sample_informations][populate][baku_mutu][fields][2]=nama_baku_mutu' +
  '&populate[quotation][populate][sample_informations][populate][baku_mutu][fields][3]=judul' +
  '&populate[quotation][populate][sample_informations][populate][baku_mutu][sort][0]=nama_baku_mutu:asc' +
  '&populate[quotation][populate][sample_informations][populate][parameter_ujis][fields][0]=documentId' +
  '&populate[quotation][populate][sample_informations][populate][parameter_ujis][fields][1]=nama_parameter' +
  '&populate[quotation][populate][sample_informations][populate][parameter_ujis][fields][2]=simbol' +
  '&populate[quotation][populate][sample_informations][populate][sample_information_points][populate][detail_attachments]=true' +
  '&populate[quotation][populate][sample_informations][populate][sample_information_points][populate][detail_surveyors][populate][0]=user' +
  '&populate[quotation][populate][sample_informations][populate][sample_information_points][populate][test_results][populate][environment_rows][populate][0]=environment_variable' +
  '&populate[quotation][populate][quotation_sample_informations][sort][0]=createdAt:asc' +
  '&populate[quotation][populate][quotation_sample_informations][populate][jenis_pengukuran][fields][0]=documentId' +
  '&populate[quotation][populate][quotation_sample_informations][populate][jenis_pengukuran][fields][1]=nama_jenis' +
  '&populate[quotation][populate][quotation_sample_informations][populate][jenis_pengukuran][fields][2]=kode_jenis' +
  '&populate[quotation][populate][quotation_sample_informations][populate][jenis_pengukuran][sort][0]=nama_jenis:asc' +
  jenisPengukuranGroupingPopulateQs(
    'populate[quotation][populate][quotation_sample_informations][populate][jenis_pengukuran]',
  ) +
  '&populate[quotation][populate][quotation_sample_informations][populate][baku_mutu][fields][0]=documentId' +
  '&populate[quotation][populate][quotation_sample_informations][populate][baku_mutu][fields][1]=kode' +
  '&populate[quotation][populate][quotation_sample_informations][populate][baku_mutu][fields][2]=nama_baku_mutu' +
  '&populate[quotation][populate][quotation_sample_informations][populate][baku_mutu][fields][3]=judul' +
  '&populate[quotation][populate][quotation_sample_informations][populate][baku_mutu][sort][0]=nama_baku_mutu:asc' +
  '&populate[quotation][populate][quotation_sample_informations][populate][parameter_ujis][fields][0]=documentId' +
  '&populate[quotation][populate][quotation_sample_informations][populate][parameter_ujis][fields][1]=nama_parameter' +
  '&populate[quotation][populate][quotation_sample_informations][populate][parameter_ujis][fields][2]=simbol' +
  '&populate[quotation][populate][quotation_sample_informations][populate][quotation_group][fields][0]=documentId' +
  '&populate[sampling_schedules][fields][0]=documentId' +
  '&populate[sampling_schedules][fields][1]=schedule_number' +
  '&populate[sampling_schedules][fields][2]=progress_status' +
  '&populate[sampling_schedules][fields][3]=schedule_kind' +
  '&populate[sampling_schedules][fields][4]=customer_agreed_start_date' +
  '&populate[sampling_schedules][fields][5]=customer_agreed_end_date' +
  '&populate[sampling_schedules][fields][6]=customer_agreed_dates' +
  '&populate[sampling_schedules][fields][7]=sampling_received_at' +
  '&populate[sampling_schedules][fields][8]=reschedule_point_index' +
  '&populate[sampling_schedules][populate][reschedule_sampling_plan][fields][0]=documentId' +
  '&populate[sampling_schedules][populate][quotation][populate][customer]=true' +
  '&populate[sampling_schedules][populate][quotation_group][fields][0]=documentId' +
  '&populate[sampling_schedules][populate][quotation_group][populate][customer]=true';

export type StrapiSampleInformationWorkOrderRef = {
  documentId: string;
  work_order_date?: string | null;
  /** Work order GL number when populated. */
  job_number?: string | null;
  quotation?: {
    documentId: string;
    quotation_number: string;
  } | null;
};

export type StrapiSampleDetailAttachmentRow = {
  documentId: string;
  picture?: string | null;
  is_main_picture?: boolean | null;
};

export type StrapiSampleDetailSurveyorRow = {
  documentId: string;
  user?: { username?: string; email?: string } | null;
};

export type StrapiJenisPengukuranGroupingMini = {
  documentId: string;
  nama_grouping?: string | null;
  air?: boolean | null;
  perencanaan_sampling?: number | null;
};

export type StrapiJenisPengukuranMini = {
  documentId: string;
  nama_jenis?: string | null;
  kode_jenis?: string | null;
  /** Master grouping (klasifikasi air / non-air) when populated. */
  grouping?: StrapiJenisPengukuranGroupingMini | null;
};

/** Label satu baris untuk UI (nama — kode, atau documentId jika nama/kode kosong). */
export function formatJenisPengukuranLine(
  jp: StrapiJenisPengukuranMini | string | null | undefined,
): string {
  if (jp == null) return '';
  if (typeof jp === 'string') return jp.trim();
  const nama = String(jp.nama_jenis ?? '').trim();
  const kode = String(jp.kode_jenis ?? '').trim();
  const joined = [nama, kode].filter(Boolean).join(' — ');
  return joined || jp.documentId.trim();
}

export type StrapiBakuMutuMini = {
  documentId: string;
  kode?: string | null;
  nama_baku_mutu?: string | null;
  judul?: string | null;
};

export type StrapiParameterUjiMini = {
  documentId: string;
  nama_parameter: string | null;
  simbol: string | null;
};

function normalizeParameterUjiMini(raw: Record<string, unknown>): StrapiParameterUjiMini {
  const src = flattenStrapiEntry(raw);
  return {
    documentId: String(src.documentId ?? raw.documentId ?? ''),
    nama_parameter: (src.nama_parameter as string) ?? null,
    simbol: (src.simbol as string) ?? null,
  };
}

function normalizeParameterUjisFromSampleSrc(src: Record<string, unknown>): StrapiParameterUjiMini[] {
  return unwrapPopulateMany(src.parameter_ujis)
    .map((r) => normalizeParameterUjiMini(r as Record<string, unknown>))
    .filter((r) => r.documentId.trim());
}

/** Tampilan ringkas (tabel, ringkasan): judul dulu, lalu nama, kode, atau documentId. */
export function formatBakuMutuLine(bm: StrapiBakuMutuMini | string | null | undefined): string {
  if (bm == null) return '';
  if (typeof bm === 'string') return bm.trim();
  const judul = String(bm.judul ?? '').trim();
  if (judul) return judul;
  const nama = String(bm.nama_baku_mutu ?? '').trim();
  if (nama) return nama;
  const kode = String(bm.kode ?? '').trim();
  if (kode) return kode;
  return String(bm.documentId ?? '').trim();
}

/** Baris `GET /api/parameter-ujis/grid-by-baku-mutu/:bakuMutuDocumentId` (mirror query lab × baku mutu). */
export type StrapiParameterUjiBakuMutuGridRow = {
  baku_mutu_parameter_uji_document_id: string;
  parameter_uji_document_id: string;
  nama_parameter: string | null;
  simbol: string | null;
  kode_sni: string | null;
  fokus_ilmu: string | null;
  satuan: string | null;
  status: string | null;
  analis_types: string[];
  beban_kerja: string | null;
  akomodasi: string | null;
  harga: number | null;
  parameter_type: number | null;
  larutan: string | null;
  cara_sampling: string | null;
  r_grouping_parent_document_id: string | null;
  k_grouping_parent_document_id: string | null;
  j_grouping_parent_document_id: string | null;
};

export async function strapiFetchParameterUjiGridByBakuMutu(
  jwt: string,
  bakuMutuDocumentId: string,
): Promise<StrapiParameterUjiBakuMutuGridRow[]> {
  const url = `${STRAPI_URL}/api/parameter-ujis/grid-by-baku-mutu/${encodeURIComponent(bakuMutuDocumentId)}`;
  const res = await fetch(url, { headers: strapiAuthHeaders(jwt) });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(parseErrorMessage(body, res.status));
  }
  const raw = (await res.json()) as { data?: unknown };
  const arr = raw.data;
  if (!Array.isArray(arr)) return [];
  return arr as StrapiParameterUjiBakuMutuGridRow[];
}

export type StrapiTestResultSampleRef = {
  documentId: string;
  jenis_pengukuran?: StrapiJenisPengukuranMini | null;
  baku_mutu?: StrapiBakuMutuMini | null;
  parameter_uji?: string | null;
  /** Populated when the result is nested under work-order context (optional). */
  work_order?: StrapiSampleInformationWorkOrderRef | null;
  quotation?: {
    documentId: string;
    quotation_number: string;
  } | null;
};

export type StrapiTestResultEnvironmentRow = {
  documentId: string;
  result_value?: string | null;
  result_type?: string | null;
  unit?: string | null;
  environment_variable?: { documentId?: string; name?: string | null } | null;
  test_result?: { documentId?: string; test_item?: string | null } | null;
};

export type StrapiTestResultRow = {
  documentId: string;
  test_item: string | null;
  test_method: string | null;
  unit: string | null;
  result: string | null;
  reporting_limit: string | null;
  standard_limit: string | null;
  sample_information?: StrapiTestResultSampleRef | null;
  environment_rows?: StrapiTestResultEnvironmentRow[];
  createdAt?: string;
  updatedAt?: string;
};

export type StrapiSampleInformationPointRow = {
  documentId: string;
  longitude?: number | null;
  latitude?: number | null;
  sampled_datetime?: string | null;
  sample_received_date?: string | null;
  tested_date?: string | null;
  customer_sample?: { documentId: string; location_name?: string | null } | null;
  detail_attachments?: StrapiSampleDetailAttachmentRow[];
  detail_surveyors?: StrapiSampleDetailSurveyorRow[];
  test_results?: StrapiTestResultRow[];
};

export type StrapiSampleInformationRow = {
  documentId: string;
  jenis_pengukuran?: StrapiJenisPengukuranMini | null;
  parameter_uji?: string | null;
  /** Populated `parameter_ujis` M2M (nama/simbol) when quotation/work-order detail is loaded. */
  parameter_ujis?: StrapiParameterUjiMini[];
  baku_mutu?: StrapiBakuMutuMini | null;
  sample_point_count?: number | null;
  harga?: number | null;
  quotation_group?: StrapiQuotationGroupRow | null;
  quotation?: {
    documentId: string;
    quotation_number: string;
  } | null;
  /** Present when this row was loaded in the context of a work order detail. */
  work_order?: StrapiSampleInformationWorkOrderRef | null;
  /** Per-titik detail (coordinates, attachments, surveyors, test rows). */
  sample_information_points?: StrapiSampleInformationPointRow[];
  /** Aggregated across all points (convenient for summaries). */
  detail_attachments?: StrapiSampleDetailAttachmentRow[];
  detail_surveyors?: StrapiSampleDetailSurveyorRow[];
  test_results?: StrapiTestResultRow[];
  createdAt?: string;
  updatedAt?: string;
};

export type StrapiWorkOrderRow = {
  documentId: string;
  /** Job number; assigned on create from quotation cabang job_code (`GL|GLJ|GLM-#####`). */
  job_number?: string | null;
  roman_quarter?:
    | 'I'
    | 'II'
    | 'III'
    | 'IV'
    | 'V'
    | 'VI'
    | 'VII'
    | 'VIII'
    | 'IX'
    | 'X'
    | 'XI'
    | 'XII'
    | string
    | null;
  calendar_year?: number | null;
  testing_start_date?: string | null;
  testing_end_date?: string | null;
  work_order_date: string | null;
  progress_status?: 'draft' | 'open' | 'in_progress' | 'completed' | 'cancelled' | null;
  quotation?: StrapiQuotationRow | null;
  /** All schedules linked to this WO (initial + point reschedules). */
  sampling_schedules?: StrapiSamplingScheduleRow[];
  /** Primary (initial) schedule for backward-compatible consumers. */
  sampling_schedule?: StrapiSamplingScheduleRow | null;
  /** Populated on detail fetch; list only sets `sampleCount`. */
  sample_informations?: StrapiSampleInformationRow[];
  sampleCount: number;
  createdAt?: string;
  updatedAt?: string;
};

export type WorkOrdersListResult = { rows: StrapiWorkOrderRow[]; total: number };

function normalizeSampleWorkOrderRef(raw: Record<string, unknown> | null): StrapiSampleInformationWorkOrderRef | null {
  if (!raw) return null;
  const w = flattenStrapiEntry(raw);
  const qRaw = unwrapPopulateOne(w.quotation);
  let quotation: StrapiSampleInformationWorkOrderRef['quotation'] = null;
  if (qRaw) {
    const q = flattenStrapiEntry(qRaw);
    quotation = {
      documentId: String(q.documentId ?? qRaw.documentId ?? ''),
      quotation_number: String(q.quotation_number ?? ''),
    };
  }
  return {
    documentId: String(w.documentId ?? raw.documentId ?? ''),
    work_order_date: (w.work_order_date as string) ?? null,
    job_number:
      w.job_number != null && String(w.job_number).trim() !== ''
        ? String(w.job_number)
        : null,
    quotation,
  };
}

function normalizeSampleDetailAttachmentEntry(raw: Record<string, unknown>): StrapiSampleDetailAttachmentRow {
  const s = flattenStrapiEntry(raw);
  return {
    documentId: String(s.documentId ?? raw.documentId ?? ''),
    picture: (s.picture as string) ?? null,
    is_main_picture: s.is_main_picture != null ? Boolean(s.is_main_picture) : null,
  };
}

function normalizeSampleDetailSurveyorEntry(raw: Record<string, unknown>): StrapiSampleDetailSurveyorRow {
  const s = flattenStrapiEntry(raw);
  const uRaw = unwrapPopulateOne(s.user);
  let user: StrapiSampleDetailSurveyorRow['user'] = null;
  if (uRaw) {
    const u = flattenStrapiEntry(uRaw);
    user = {
      username: String(u.username ?? ''),
      email: String(u.email ?? ''),
    };
  }
  return {
    documentId: String(s.documentId ?? raw.documentId ?? ''),
    user,
  };
}

function normalizeJenisPengukuranGroupingRelation(raw: unknown): StrapiJenisPengukuranGroupingMini | null {
  const one = unwrapPopulateOne(raw);
  if (!one) return null;
  const g = flattenStrapiEntry(one);
  const id = String(g.documentId ?? one.documentId ?? '').trim();
  if (!id) return null;
  const airRaw = g.air;
  let air: boolean | null = null;
  if (airRaw === true || airRaw === false) air = airRaw;
  else if (airRaw === 1 || airRaw === '1') air = true;
  else if (airRaw === 0 || airRaw === '0') air = false;
  const perencanaan =
    g.perencanaan_sampling != null && Number.isFinite(Number(g.perencanaan_sampling))
      ? Number(g.perencanaan_sampling)
      : null;
  return {
    documentId: id,
    nama_grouping: (g.nama_grouping as string) ?? null,
    air,
    perencanaan_sampling: perencanaan,
  };
}

function normalizeJenisPengukuranRelation(raw: unknown): StrapiJenisPengukuranMini | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t ? { documentId: t, nama_jenis: null, kode_jenis: null } : null;
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const jp = flattenStrapiEntry(raw as Record<string, unknown>);
    const id = String(jp.documentId ?? (raw as { documentId?: unknown }).documentId ?? '');
    if (!id) return null;
    const grouping = normalizeJenisPengukuranGroupingRelation(jp.grouping);
    return {
      documentId: id,
      nama_jenis: (jp.nama_jenis as string) ?? null,
      kode_jenis: (jp.kode_jenis as string) ?? null,
      ...(grouping ? { grouping } : {}),
    };
  }
  return null;
}

function normalizeBakuMutuRelation(raw: unknown): StrapiBakuMutuMini | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t ? { documentId: t, kode: null, nama_baku_mutu: null, judul: null } : null;
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const bm = flattenStrapiEntry(raw as Record<string, unknown>);
    const id = String(bm.documentId ?? (raw as { documentId?: unknown }).documentId ?? '');
    if (!id) return null;
    return {
      documentId: id,
      kode: (bm.kode as string) ?? null,
      nama_baku_mutu: (bm.nama_baku_mutu as string) ?? null,
      judul: (bm.judul as string) ?? null,
    };
  }
  return null;
}

function normalizeSampleInformationPointEntry(
  raw: Record<string, unknown>,
  ctx: {
    parentLine: Pick<StrapiSampleInformationRow, 'documentId' | 'jenis_pengukuran' | 'baku_mutu' | 'parameter_uji'>;
    parentWorkOrder: StrapiSampleInformationWorkOrderRef | null;
  },
): StrapiSampleInformationPointRow {
  const src = flattenStrapiEntry(raw);
  const pointDocId = String(src.documentId ?? raw.documentId ?? '');
  const csRaw = unwrapPopulateOne(src.customer_sample);
  let customer_sample: StrapiSampleInformationPointRow['customer_sample'] = null;
  if (csRaw) {
    const cs = flattenStrapiEntry(csRaw);
    customer_sample = {
      documentId: String(cs.documentId ?? csRaw.documentId ?? ''),
      location_name: (cs.location_name as string) ?? null,
    };
  }
  const detail_attachments = unwrapPopulateMany(src.detail_attachments).map((r) =>
    normalizeSampleDetailAttachmentEntry(r),
  );
  const detail_surveyors = unwrapPopulateMany(src.detail_surveyors).map((r) =>
    normalizeSampleDetailSurveyorEntry(r),
  );

  const sampleRefForResults: StrapiTestResultSampleRef = {
    documentId: ctx.parentLine.documentId,
    jenis_pengukuran: ctx.parentLine.jenis_pengukuran ?? null,
    baku_mutu: ctx.parentLine.baku_mutu ?? null,
    parameter_uji: ctx.parentLine.parameter_uji ?? null,
    work_order: ctx.parentWorkOrder,
    quotation: ctx.parentWorkOrder?.quotation ?? null,
  };
  const test_results = unwrapPopulateMany(src.test_results).map((tr) => {
    const row = normalizeTestResultEntry(tr as Record<string, unknown>);
    return { ...row, sample_information: sampleRefForResults };
  });

  return {
    documentId: pointDocId,
    longitude: src.longitude != null ? Number(src.longitude) : null,
    latitude: src.latitude != null ? Number(src.latitude) : null,
    sampled_datetime: (src.sampled_datetime as string) ?? null,
    sample_received_date: (src.sample_received_date as string) ?? null,
    tested_date: (src.tested_date as string) ?? null,
    customer_sample,
    detail_attachments: detail_attachments.length ? detail_attachments : undefined,
    detail_surveyors: detail_surveyors.length ? detail_surveyors : undefined,
    test_results: test_results.length ? test_results : undefined,
  };
}

/** Builds legacy `;`-separated `parameter-uji` documentIds from Strapi `parameter_ujis` M2M or old string column. */
function parameterUjiStoredStringFromSampleSrc(src: Record<string, unknown>): string | null {
  const normalized = normalizeParameterUjisFromSampleSrc(src);
  if (normalized.length) {
    return normalized.map((r) => r.documentId).join(';');
  }
  const rawPu = src.parameter_uji;
  if (Array.isArray(rawPu)) {
    const ids = parseStoredParameterUjiDocumentIds(rawPu);
    return ids.length ? ids.join(';') : null;
  }
  return scalarStringFromStrapiField(rawPu);
}

function normalizeSampleInformationEntry(
  raw: Record<string, unknown>,
  opts?: { parentWorkOrder?: StrapiSampleInformationWorkOrderRef | null },
): StrapiSampleInformationRow {
  const src = flattenStrapiEntry(raw);
  const sampleDocId = String(src.documentId ?? raw.documentId ?? '');
  let work_order = opts?.parentWorkOrder ?? null;
  if (!work_order) {
    const woRaw = unwrapPopulateOne(src.work_order);
    if (woRaw) work_order = normalizeSampleWorkOrderRef(woRaw);
  }

  const qRaw = unwrapPopulateOne(src.quotation);
  let quotation: StrapiSampleInformationRow['quotation'] = null;
  if (qRaw) {
    const q = flattenStrapiEntry(qRaw);
    quotation = {
      documentId: String(q.documentId ?? qRaw.documentId ?? ''),
      quotation_number: String(q.quotation_number ?? ''),
    };
  }

  const jenis_pengukuran = normalizeJenisPengukuranRelation(src.jenis_pengukuran);
  const parameter_ujis = normalizeParameterUjisFromSampleSrc(src);
  const parameter_uji = parameterUjiStoredStringFromSampleSrc(src);
  const baku_mutu = normalizeBakuMutuRelation(src.baku_mutu ?? src.regulasi);

  const pointsRaw = unwrapPopulateMany(src.sample_information_points);
  const sample_information_points =
    pointsRaw.length > 0
      ? pointsRaw.map((pr) =>
          normalizeSampleInformationPointEntry(pr as Record<string, unknown>, {
            parentLine: { documentId: sampleDocId, jenis_pengukuran, baku_mutu, parameter_uji },
            parentWorkOrder: work_order,
          }),
        )
      : [];

  const detail_attachments = sample_information_points.flatMap((p) => p.detail_attachments ?? []);
  const detail_surveyors = sample_information_points.flatMap((p) => p.detail_surveyors ?? []);
  const test_results = sample_information_points.flatMap((p) => p.test_results ?? []);

  const spc =
    coercePositiveIntField(src.sample_point_count) ?? coercePositiveIntField(src.jumlah_titik);

  const groupRaw = unwrapPopulateOne(src.quotation_group);
  const quotation_group = normalizeQuotationGroupEntry(groupRaw);

  return {
    documentId: sampleDocId,
    jenis_pengukuran,
    parameter_uji,
    parameter_ujis: parameter_ujis.length ? parameter_ujis : undefined,
    baku_mutu,
    sample_point_count: spc,
    harga: src.harga != null ? Number(src.harga) : null,
    quotation_group,
    quotation,
    work_order,
    sample_information_points: sample_information_points.length ? sample_information_points : undefined,
    detail_attachments: detail_attachments.length ? detail_attachments : undefined,
    detail_surveyors: detail_surveyors.length ? detail_surveyors : undefined,
    test_results: test_results.length ? test_results : undefined,
    createdAt: (src.createdAt as string) ?? (src.created_at as string) ?? undefined,
    updatedAt: (src.updatedAt as string) ?? (src.updated_at as string) ?? undefined,
  };
}

function normalizeTestResultSampleRef(raw: Record<string, unknown> | null): StrapiTestResultSampleRef | null {
  if (!raw) return null;
  const s = flattenStrapiEntry(raw);
  const qRaw = unwrapPopulateOne(s.quotation);
  let quotation: StrapiTestResultSampleRef['quotation'] = null;
  if (qRaw) {
    const q = flattenStrapiEntry(qRaw);
    quotation = {
      documentId: String(q.documentId ?? qRaw.documentId ?? ''),
      quotation_number: String(q.quotation_number ?? ''),
    };
  }
  return {
    documentId: String(s.documentId ?? raw.documentId ?? ''),
    jenis_pengukuran: normalizeJenisPengukuranRelation(s.jenis_pengukuran),
    baku_mutu: normalizeBakuMutuRelation(s.baku_mutu ?? s.regulasi),
    parameter_uji: parameterUjiStoredStringFromSampleSrc(s),
    work_order: null,
    quotation,
  };
}

function normalizeTestResultEnvironmentRow(raw: Record<string, unknown>): StrapiTestResultEnvironmentRow {
  const src = flattenStrapiEntry(raw);
  const vRaw = unwrapPopulateOne(src.environment_variable);
  let environment_variable: StrapiTestResultEnvironmentRow['environment_variable'] = null;
  if (vRaw) {
    const v = flattenStrapiEntry(vRaw);
    environment_variable = {
      documentId: String(v.documentId ?? vRaw.documentId ?? ''),
      name: (v.name as string) ?? null,
    };
  }
  const trRaw = unwrapPopulateOne(src.test_result);
  let test_result: StrapiTestResultEnvironmentRow['test_result'] = null;
  if (trRaw) {
    const tr = flattenStrapiEntry(trRaw);
    test_result = {
      documentId: String(tr.documentId ?? trRaw.documentId ?? ''),
      test_item: (tr.test_item as string) ?? null,
    };
  }
  return {
    documentId: String(src.documentId ?? raw.documentId ?? ''),
    result_value: (src.result_value as string) ?? null,
    result_type: (src.result_type as string) ?? null,
    unit: (src.unit as string) ?? null,
    environment_variable,
    test_result,
  };
}

function normalizeTestResultEntry(raw: Record<string, unknown>): StrapiTestResultRow {
  const src = flattenStrapiEntry(raw);
  const pointRaw = unwrapPopulateOne(src.sample_information_point);
  let sample_information: StrapiTestResultSampleRef | null = null;
  if (pointRaw) {
    const siRaw = unwrapPopulateOne(flattenStrapiEntry(pointRaw).sample_information);
    sample_information = normalizeTestResultSampleRef(siRaw ? (siRaw as Record<string, unknown>) : null);
  }
  const environment_rows = unwrapPopulateMany(src.environment_rows).map((r) =>
    normalizeTestResultEnvironmentRow(r),
  );
  return {
    documentId: String(src.documentId ?? raw.documentId ?? ''),
    test_item: (src.test_item as string) ?? null,
    test_method: (src.test_method as string) ?? null,
    unit: (src.unit as string) ?? null,
    result: (src.result as string) ?? null,
    reporting_limit: (src.reporting_limit as string) ?? null,
    standard_limit: (src.standard_limit as string) ?? null,
    sample_information,
    environment_rows: environment_rows.length ? environment_rows : undefined,
    createdAt: (src.createdAt as string) ?? (src.created_at as string) ?? undefined,
    updatedAt: (src.updatedAt as string) ?? (src.updated_at as string) ?? undefined,
  };
}

function normalizeWorkOrderEntry(raw: Record<string, unknown>): StrapiWorkOrderRow {
  const src = flattenStrapiEntry(raw);
  const qRaw = unwrapPopulateOne(src.quotation);
  let quotation: StrapiQuotationRow | null = null;
  if (qRaw) {
    quotation = normalizeQuotationEntry(qRaw);
  }
  const schedulesMany = unwrapPopulateMany(src.sampling_schedules).map((row) =>
    normalizeSamplingScheduleRow(row as Record<string, unknown>),
  );
  const legacyScheduleRaw = unwrapPopulateOne(src.sampling_schedule);
  const legacySchedule =
    legacyScheduleRaw && typeof legacyScheduleRaw === 'object'
      ? normalizeSamplingScheduleRow(legacyScheduleRaw as Record<string, unknown>)
      : null;
  const sampling_schedules =
    schedulesMany.length > 0
      ? schedulesMany
      : legacySchedule
        ? [legacySchedule]
        : [];
  const sampling_schedule = primarySamplingSchedule(sampling_schedules, legacySchedule);
  const woDocId = String(src.documentId ?? raw.documentId ?? '');
  const woJobNum =
    src.job_number != null && String(src.job_number).trim() !== '' ? String(src.job_number) : null;
  let quotationMini: StrapiSampleInformationWorkOrderRef['quotation'] = null;
  if (quotation) {
    quotationMini = {
      documentId: quotation.documentId,
      quotation_number: quotation.quotation_number,
    };
  }
  const parentWoRef: StrapiSampleInformationWorkOrderRef = {
    documentId: woDocId,
    work_order_date: (src.work_order_date as string) ?? null,
    job_number: woJobNum,
    quotation: quotationMini,
  };
  const qFlat = qRaw ? flattenStrapiEntry(qRaw) : null;
  const sampleInformationsNested = qFlat ? unwrapQuotationSampleLinesRaw(qFlat) : [];
  const sampleRows = sampleInformationsNested.map((sr) =>
    normalizeSampleInformationEntry(sr as Record<string, unknown>, { parentWorkOrder: parentWoRef }),
  );
  const samplesAreIdOnly =
    sampleRows.length > 0 &&
    sampleRows.every(
      (s) =>
        !s.jenis_pengukuran?.documentId?.trim() &&
        (s.parameter_uji == null || s.parameter_uji === '') &&
        !s.baku_mutu?.documentId?.trim() &&
        s.sample_point_count == null &&
        s.harga == null,
    );
  return {
    documentId: String(src.documentId ?? raw.documentId ?? ''),
    job_number:
      src.job_number != null && String(src.job_number).trim() !== '' ? String(src.job_number) : null,
    roman_quarter: (src.roman_quarter as string) ?? null,
    calendar_year: src.calendar_year != null ? Number(src.calendar_year) : null,
    testing_start_date: (src.testing_start_date as string) ?? null,
    testing_end_date: (src.testing_end_date as string) ?? null,
    work_order_date: (src.work_order_date as string) ?? null,
    progress_status: (src.progress_status as StrapiWorkOrderRow['progress_status']) ?? null,
    quotation,
    sampling_schedules,
    sampling_schedule,
    sampleCount: sampleRows.length,
    sample_informations: sampleRows.length && !samplesAreIdOnly ? sampleRows : undefined,
    createdAt: (src.createdAt as string) ?? (src.created_at as string) ?? undefined,
    updatedAt: (src.updatedAt as string) ?? (src.updated_at as string) ?? undefined,
  };
}

function parseWorkOrderOneResponse(res: Response, body: unknown): StrapiWorkOrderRow {
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  const b = asRecord(body);
  const data = b?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Invalid Strapi work order response');
  }
  return normalizeWorkOrderEntry(data as Record<string, unknown>);
}

export type WorkOrderRomanQuarter =
  | 'I'
  | 'II'
  | 'III'
  | 'IV'
  | 'V'
  | 'VI'
  | 'VII'
  | 'VIII'
  | 'IX'
  | 'X'
  | 'XI'
  | 'XII';

export type StrapiWorkOrderCreateData = {
  quotation: { connect: string[] };
  work_order_date: string;
  progress_status: NonNullable<StrapiWorkOrderRow['progress_status']>;
  roman_quarter: WorkOrderRomanQuarter;
  calendar_year: number;
  testing_start_date?: string | null;
  testing_end_date?: string | null;
};

export type StrapiWorkOrderBundleSurveyTeamInput = {
  user_idx: number | { connect: string[] };
  is_lead: boolean;
  scheduled_date?: string[];
};

export type StrapiWorkOrderBundleCreateData = {
  quotation_document_id: string;
  work_order: StrapiWorkOrderCreateData;
  sampling_plans: Array<Omit<StrapiSamplingPlanCreateData, 'work_order'> & { work_order?: { connect: string[] } }>;
  survey_teams?: StrapiWorkOrderBundleSurveyTeamInput[];
};

export type StrapiWorkOrderUpdateData = {
  work_order_date?: string;
  progress_status?: NonNullable<StrapiWorkOrderRow['progress_status']>;
  roman_quarter?: WorkOrderRomanQuarter;
  calendar_year?: number;
  testing_start_date?: string | null;
  testing_end_date?: string | null;
};

export type StrapiSampleInformationCreateData = {
  quotation: { connect: string[] };
  quotation_group?: { connect: string[] };
  jenis_pengukuran: string;
  parameter_uji: string;
  baku_mutu: string;
  sample_point_count: number;
  harga?: number | null;
};

export type StrapiQuotationGroupCreateData = {
  quotation?: { connect: string[] };
  customer?: { connect: string[] } | null;
  recurring_type?: 'weekly' | 'monthly' | 'yearly' | null;
  recurring_interval?: number | null;
  recurring_count_override?: number | null;
  recurring_start_date?: string | null;
};

export type StrapiQuotationGroupUpdateData = {
  customer?: { connect: string[] } | null;
  recurring_type?: 'weekly' | 'monthly' | 'yearly' | null;
  recurring_interval?: number | null;
  recurring_count_override?: number | null;
  recurring_start_date?: string | null;
};

export type StrapiSampleInformationUpdateData = {
  jenis_pengukuran?: string;
  parameter_uji?: string;
  baku_mutu?: string;
  sample_point_count?: number;
  harga?: number | null;
  quotation_group?: { connect: string[] } | null;
};

export async function strapiCreateWorkOrder(
  jwt: string,
  data: StrapiWorkOrderCreateData,
): Promise<StrapiWorkOrderRow> {
  const res = await fetch(`${STRAPI_URL}/api/work-orders?${workOrderPopulateQs}`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      data: {
        quotation: data.quotation,
        work_order_date: data.work_order_date,
        progress_status: data.progress_status,
        roman_quarter: data.roman_quarter,
        calendar_year: data.calendar_year,
        ...(data.testing_start_date ? { testing_start_date: data.testing_start_date } : {}),
        ...(data.testing_end_date ? { testing_end_date: data.testing_end_date } : {}),
      },
    }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseWorkOrderOneResponse(res, body);
}

export async function strapiCreateWorkOrderBundle(
  jwt: string,
  data: StrapiWorkOrderBundleCreateData,
): Promise<StrapiWorkOrderRow> {
  const hasFiles = data.sampling_plans.some((plan) => (plan.peta_sketsa_lokasi_files?.length ?? 0) > 0);
  const bodyData = {
    quotation_document_id: data.quotation_document_id,
    work_order: data.work_order,
    sampling_plans: data.sampling_plans.map(({ peta_sketsa_lokasi_files: _files, ...plan }) => plan),
  };
  const body = hasFiles
    ? (() => {
        const form = new FormData();
        form.append('data', JSON.stringify(bodyData));
        data.sampling_plans.forEach((plan, idx) => {
          for (const file of plan.peta_sketsa_lokasi_files ?? []) {
            form.append(`files.sampling_plans.${idx}.peta_sketsa_lokasi`, file);
          }
        });
        return form;
      })()
    : JSON.stringify({ data: bodyData });

  const res = await fetch(`${STRAPI_URL}/api/work-orders/bundle?${workOrderPopulateQs}`, {
    method: 'POST',
    headers: hasFiles
      ? strapiAuthHeaders(jwt)
      : {
          ...(strapiAuthHeaders(jwt) as Record<string, string>),
          'Content-Type': 'application/json',
        },
    body,
  });
  const parsed = (await res.json().catch(() => ({}))) as unknown;
  return parseWorkOrderOneResponse(res, parsed);
}

export async function strapiListWorkOrders(
  jwt: string,
  pageSize = 200,
  filters: WorkOrdersListFilters = emptyWorkOrdersListFilters,
): Promise<WorkOrdersListResult> {
  const params = new URLSearchParams({
    'pagination[pageSize]': String(pageSize),
    'pagination[page]': '1',
    sort: 'work_order_date:desc',
  });
  appendWorkOrdersListFilters(params, filters);
  const qs = `${params.toString()}&${workOrderPopulateQs}`;
  const res = await fetch(`${STRAPI_URL}/api/work-orders?${qs}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows, meta } = parseListBody(res, body);
  const total = meta.pagination?.total ?? rows.length;
  return { rows: rows.map((r) => normalizeWorkOrderEntry(r)), total };
}

export async function strapiGetWorkOrder(jwt: string, documentId: string): Promise<StrapiWorkOrderRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/work-orders/${encodeURIComponent(documentId)}?${workOrderDetailPopulateQs}`,
    {
      headers: strapiAuthHeaders(jwt),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseWorkOrderOneResponse(res, body);
}

export type SamplingChecklistFormAirGroup = 'air' | 'non_air';

export type SamplingChecklistFormChecklist = {
  tujuan_pengambilan: DynamicEntityRow[];
  pengawetan: DynamicEntityRow[];
  larutan_penjerap: DynamicEntityRow[];
  peralatan_pengambilan_contoh: DynamicEntityRow[];
  peralatan_pengukuran_lapangan: DynamicEntityRow[];
  peralatan_pendukung: DynamicEntityRow[];
  peralatan_k3: DynamicEntityRow[];
  cara_pengambilan_contoh: DynamicEntityRow[];
  pengendalian_mutu_lapangan: DynamicEntityRow[];
  pengamanan_transportasi_contoh: DynamicEntityRow[];
  administrasi_contoh: DynamicEntityRow[];
  metode_sampling: DynamicEntityRow[];
  sni_methods: DynamicEntityRow[];
  jumlah_wadah_contoh_uji_air: string;
  jumlah_wadah_contoh_uji_padatan: string;
  petugas_pengambil_contoh: StrapiManagedUserRow | null;
  disahkan_oleh_manajer_teknis: StrapiManagedUserRow | null;
};

export type SamplingChecklistFormOptions = {
  users: StrapiManagedUserRow[];
  tujuan_pengambilan: DynamicEntityRow[];
  pengawetan: DynamicEntityRow[];
  larutan_penjerap: DynamicEntityRow[];
  peralatan_pengambilan_contoh: DynamicEntityRow[];
  peralatan_pengukuran_lapangan: DynamicEntityRow[];
  peralatan_pendukung: DynamicEntityRow[];
  peralatan_k3: DynamicEntityRow[];
  cara_pengambilan_contoh: DynamicEntityRow[];
  pengendalian_mutu_lapangan: DynamicEntityRow[];
  pengamanan_transportasi_contoh: DynamicEntityRow[];
  administrasi_contoh: DynamicEntityRow[];
  metode_sampling: DynamicEntityRow[];
  sni_methods: DynamicEntityRow[];
};

export type SamplingChecklistFormGroup = {
  air_group: SamplingChecklistFormAirGroup;
  label: string;
  plan_document_id: string | null;
  jenis_labels: string[];
  checklist: SamplingChecklistFormChecklist;
};

export type SamplingChecklistForm = {
  work_order_document_id: string;
  quotation_document_id: string | null;
  quotation_uses_groups: boolean;
  groups: SamplingChecklistFormGroup[];
  options: SamplingChecklistFormOptions;
  warning: string | null;
};

const SAMPLING_CHECKLIST_RELATION_FIELDS = [
  'tujuan_pengambilan',
  'pengawetan',
  'larutan_penjerap',
  'peralatan_pengambilan_contoh',
  'peralatan_pengukuran_lapangan',
  'peralatan_pendukung',
  'peralatan_k3',
  'cara_pengambilan_contoh',
  'pengendalian_mutu_lapangan',
  'pengamanan_transportasi_contoh',
  'administrasi_contoh',
  'metode_sampling',
  'sni_methods',
] as const;

function samplingChecklistRows(raw: unknown): DynamicEntityRow[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => asRecord(item))
    .filter((row): row is Record<string, unknown> => Boolean(row))
    .map((row) => normalizeDynamicRow(row))
    .filter((row) => Boolean(row.documentId));
}

function samplingChecklistUser(raw: unknown): StrapiManagedUserRow | null {
  const row = asRecord(raw);
  if (!row) return null;
  const numericId = row.numericId != null && Number.isFinite(Number(row.numericId)) ? Number(row.numericId) : null;
  const documentId = String(row.documentId ?? '').trim() || null;
  const id = String(row.id ?? '').trim();
  if (!id && numericId == null && !documentId) return null;
  return {
    id: id || (numericId != null ? String(numericId) : documentId || 'u:unknown'),
    documentId,
    numericId,
    username: String(row.username ?? ''),
    email: String(row.email ?? ''),
    blocked: Boolean(row.blocked),
    userTypeCode: null,
    userTypeName: null,
  };
}

function samplingChecklistDraft(raw: unknown): SamplingChecklistFormChecklist {
  const row = asRecord(raw) ?? {};
  const checklist = {} as SamplingChecklistFormChecklist;
  for (const field of SAMPLING_CHECKLIST_RELATION_FIELDS) {
    checklist[field] = samplingChecklistRows(row[field]);
  }
  checklist.jumlah_wadah_contoh_uji_air = String(row.jumlah_wadah_contoh_uji_air ?? '');
  checklist.jumlah_wadah_contoh_uji_padatan = String(row.jumlah_wadah_contoh_uji_padatan ?? '');
  checklist.petugas_pengambil_contoh = samplingChecklistUser(row.petugas_pengambil_contoh);
  checklist.disahkan_oleh_manajer_teknis = samplingChecklistUser(row.disahkan_oleh_manajer_teknis);
  return checklist;
}

function samplingChecklistOptions(raw: unknown): SamplingChecklistFormOptions {
  const row = asRecord(raw) ?? {};
  const users = Array.isArray(row.users)
    ? row.users.map(samplingChecklistUser).filter((user): user is StrapiManagedUserRow => Boolean(user))
    : [];
  const options = { users } as SamplingChecklistFormOptions;
  for (const field of SAMPLING_CHECKLIST_RELATION_FIELDS) {
    options[field] = samplingChecklistRows(row[field]);
  }
  return options;
}

/** Satu request untuk modal checklist kesiapan alat petugas sampling. */
export async function strapiGetSamplingChecklistForm(
  jwt: string,
  workOrderDocumentId: string,
): Promise<SamplingChecklistForm> {
  const res = await fetch(
    `${STRAPI_URL}/api/work-orders/${encodeURIComponent(workOrderDocumentId)}/sampling-checklist-form`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(asRecord(body)?.data);
  if (!data) throw new Error('Respons checklist kesiapan alat tidak valid.');
  const groups = Array.isArray(data.groups) ? data.groups : [];
  return {
    work_order_document_id: String(data.work_order_document_id ?? workOrderDocumentId),
    quotation_document_id: String(data.quotation_document_id ?? '').trim() || null,
    quotation_uses_groups: Boolean(data.quotation_uses_groups),
    groups: groups.map((item) => {
      const group = asRecord(item) ?? {};
      const airGroup = group.air_group === 'non_air' ? 'non_air' : 'air';
      return {
        air_group: airGroup,
        label: String(group.label ?? ''),
        plan_document_id: String(group.plan_document_id ?? '').trim() || null,
        jenis_labels: Array.isArray(group.jenis_labels) ? group.jenis_labels.map((label) => String(label ?? '')).filter(Boolean) : [],
        checklist: samplingChecklistDraft(group.checklist),
      };
    }),
    options: samplingChecklistOptions(data.options),
    warning: String(data.warning ?? '').trim() || null,
  };
}

export async function strapiUpdateWorkOrder(
  jwt: string,
  documentId: string,
  data: StrapiWorkOrderUpdateData,
): Promise<StrapiWorkOrderRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/work-orders/${encodeURIComponent(documentId)}?${workOrderDetailPopulateQs}`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseWorkOrderOneResponse(res, body);
}

export async function strapiDeleteWorkOrder(jwt: string, documentId: string): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/work-orders/${encodeURIComponent(documentId)}`, {
    method: 'DELETE',
    headers: strapiAuthHeaders(jwt),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(body, res.status));
  }
}

export type SampleInformationsListResult = { rows: StrapiSampleInformationRow[]; total: number };

/** Populate quotation for flat list UI (samples are quotation-scoped; work order may be absent). */
const sampleInformationFlatListPopulateQs =
  'populate[quotation][fields][0]=documentId&populate[quotation][fields][1]=quotation_number' +
  '&populate[jenis_pengukuran][fields][0]=documentId&populate[jenis_pengukuran][fields][1]=nama_jenis&populate[jenis_pengukuran][fields][2]=kode_jenis' +
  '&populate[jenis_pengukuran][sort][0]=nama_jenis:asc' +
  '&populate[baku_mutu][fields][0]=documentId&populate[baku_mutu][fields][1]=kode&populate[baku_mutu][fields][2]=nama_baku_mutu&populate[baku_mutu][fields][3]=judul' +
  '&populate[baku_mutu][sort][0]=nama_baku_mutu:asc' +
  '&populate[parameter_ujis][fields][0]=documentId&populate[parameter_ujis][fields][1]=nama_parameter&populate[parameter_ujis][fields][2]=simbol' +
  '&populate[work_order][fields][0]=documentId&populate[work_order][fields][1]=job_number';

const sampleInformationOneResponsePopulateQs =
  'populate[jenis_pengukuran][fields][0]=documentId&populate[jenis_pengukuran][fields][1]=nama_jenis&populate[jenis_pengukuran][fields][2]=kode_jenis' +
  '&populate[jenis_pengukuran][sort][0]=nama_jenis:asc' +
  '&populate[baku_mutu][fields][0]=documentId&populate[baku_mutu][fields][1]=kode&populate[baku_mutu][fields][2]=nama_baku_mutu&populate[baku_mutu][fields][3]=judul' +
  '&populate[baku_mutu][sort][0]=nama_baku_mutu:asc' +
  '&populate[parameter_ujis][fields][0]=documentId&populate[parameter_ujis][fields][1]=nama_parameter&populate[parameter_ujis][fields][2]=simbol' +
  '&populate[quotation][fields][0]=documentId&populate[quotation][fields][1]=quotation_number';

export async function strapiListSampleInformations(
  jwt: string,
  opts?: { quotationDocumentId?: string; pageSize?: number },
): Promise<SampleInformationsListResult> {
  const params = new URLSearchParams({
    'pagination[pageSize]': String(opts?.pageSize ?? 200),
    'pagination[page]': '1',
    sort: 'createdAt:asc',
  });
  if (opts?.quotationDocumentId) {
    params.set('filters[quotation][documentId][$eq]', opts.quotationDocumentId);
  }
  const res = await fetch(
    `${STRAPI_URL}/api/sample-informations?${params}&${sampleInformationFlatListPopulateQs}`,
    {
      headers: strapiAuthHeaders(jwt),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows, meta } = parseListBody(res, body);
  const total = meta.pagination?.total ?? rows.length;
  return { rows: rows.map((r) => normalizeSampleInformationEntry(r)), total };
}

/** All samples (flat), newest first, with work order / quotation context. */
export async function strapiListAllSampleInformations(
  jwt: string,
  pageSize = 200,
): Promise<SampleInformationsListResult> {
  const params = new URLSearchParams({
    'pagination[pageSize]': String(pageSize),
    'pagination[page]': '1',
    sort: 'createdAt:desc',
  });
  const qs = `${params.toString()}&${sampleInformationFlatListPopulateQs}`;
  const res = await fetch(`${STRAPI_URL}/api/sample-informations?${qs}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows, meta } = parseListBody(res, body);
  const total = meta.pagination?.total ?? rows.length;
  return { rows: rows.map((r) => normalizeSampleInformationEntry(r)), total };
}

function parseSampleInformationOneResponse(res: Response, body: unknown): StrapiSampleInformationRow {
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  const b = asRecord(body);
  const data = b?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Invalid Strapi sample-information response');
  }
  return normalizeSampleInformationEntry(data as Record<string, unknown>);
}

export async function strapiCreateSampleInformation(
  jwt: string,
  data: StrapiSampleInformationCreateData,
): Promise<StrapiSampleInformationRow> {
  const { jenis_pengukuran: jpId, baku_mutu: bmId, parameter_uji: puStored, ...rest } = data;
  const jp = String(jpId ?? '').trim();
  const bm = String(bmId ?? '').trim();
  const puIds = parseStoredParameterUjiDocumentIds(puStored);
  if (!jp) throw new Error('jenis_pengukuran (documentId master) wajib diisi.');
  if (!bm) throw new Error('baku_mutu (documentId master) wajib diisi.');
  if (!puIds.length) {
    throw new Error('parameter_uji (minimal satu parameter uji / documentId) wajib diisi.');
  }
  const payload = {
    ...rest,
    jenis_pengukuran: { connect: [jp] },
    baku_mutu: { connect: [bm] },
    parameter_ujis: { connect: puIds },
  };
  const res = await fetch(
    `${STRAPI_URL}/api/sample-informations?${sampleInformationOneResponsePopulateQs}`,
    {
      method: 'POST',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: payload }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  const row = parseSampleInformationOneResponse(res, body);
  const n = data.sample_point_count;
  if (Number.isFinite(n) && n > 0) {
    for (let i = 0; i < n; i += 1) {
      await strapiCreateSampleInformationPoint(jwt, row.documentId);
    }
  }
  return row;
}

export async function strapiUpdateSampleInformation(
  jwt: string,
  documentId: string,
  data: StrapiSampleInformationUpdateData,
): Promise<StrapiSampleInformationRow> {
  const { jenis_pengukuran: jpField, baku_mutu: bmField, parameter_uji: puField, ...rest } = data;
  const payload: Record<string, unknown> = { ...rest };
  if (puField !== undefined) {
    payload.parameter_ujis = { set: parseStoredParameterUjiDocumentIds(puField) };
  }
  if (jpField !== undefined) {
    const jp = String(jpField).trim();
    payload.jenis_pengukuran = jp ? { connect: [jp] } : null;
  }
  if (bmField !== undefined) {
    const bm = String(bmField).trim();
    payload.baku_mutu = bm ? { connect: [bm] } : null;
  }
  const res = await fetch(
    `${STRAPI_URL}/api/sample-informations/${encodeURIComponent(documentId)}?${sampleInformationOneResponsePopulateQs}`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: payload }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseSampleInformationOneResponse(res, body);
}

/** Empty titik row under a sample line (coords / relations filled later). */
export async function strapiCreateSampleInformationPoint(
  jwt: string,
  sampleInformationDocumentId: string,
): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/sample-information-points`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      data: {
        sample_information: { connect: [sampleInformationDocumentId] },
      },
    }),
  });
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
}

export async function strapiDeleteSampleInformation(jwt: string, documentId: string): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/sample-informations/${encodeURIComponent(documentId)}`, {
    method: 'DELETE',
    headers: strapiAuthHeaders(jwt),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(body, res.status));
  }
}

/** Quotation-only sample lines (`quotation-sample-information`); no titik rows (unlike `sample-informations`). */
export async function strapiCreateQuotationSampleInformation(
  jwt: string,
  data: StrapiSampleInformationCreateData,
): Promise<StrapiSampleInformationRow> {
  const { jenis_pengukuran: jpId, baku_mutu: bmId, parameter_uji: puStored, ...rest } = data;
  const jp = String(jpId ?? '').trim();
  const bm = String(bmId ?? '').trim();
  const puIds = parseStoredParameterUjiDocumentIds(puStored);
  if (!jp) throw new Error('jenis_pengukuran (documentId master) wajib diisi.');
  if (!bm) throw new Error('baku_mutu (documentId master) wajib diisi.');
  if (!puIds.length) {
    throw new Error('parameter_uji (minimal satu parameter uji / documentId) wajib diisi.');
  }
  const payload = {
    ...rest,
    jenis_pengukuran: { connect: [jp] },
    baku_mutu: { connect: [bm] },
    parameter_ujis: { connect: puIds },
  };
  const res = await fetch(
    `${STRAPI_URL}/api/quotation-sample-informations?${sampleInformationOneResponsePopulateQs}`,
    {
      method: 'POST',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: payload }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseSampleInformationOneResponse(res, body);
}

export async function strapiUpdateQuotationSampleInformation(
  jwt: string,
  documentId: string,
  data: StrapiSampleInformationUpdateData,
): Promise<StrapiSampleInformationRow> {
  const { jenis_pengukuran: jpField, baku_mutu: bmField, parameter_uji: puField, ...rest } = data;
  const payload: Record<string, unknown> = { ...rest };
  if (puField !== undefined) {
    payload.parameter_ujis = { set: parseStoredParameterUjiDocumentIds(puField) };
  }
  if (jpField !== undefined) {
    const jp = String(jpField).trim();
    payload.jenis_pengukuran = jp ? { connect: [jp] } : null;
  }
  if (bmField !== undefined) {
    const bm = String(bmField).trim();
    payload.baku_mutu = bm ? { connect: [bm] } : null;
  }
  const res = await fetch(
    `${STRAPI_URL}/api/quotation-sample-informations/${encodeURIComponent(documentId)}?${sampleInformationOneResponsePopulateQs}`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: payload }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  return parseSampleInformationOneResponse(res, body);
}

export async function strapiDeleteQuotationSampleInformation(jwt: string, documentId: string): Promise<void> {
  const res = await fetch(
    `${STRAPI_URL}/api/quotation-sample-informations/${encodeURIComponent(documentId)}`,
    {
      method: 'DELETE',
      headers: strapiAuthHeaders(jwt),
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(body, res.status));
  }
}

export async function strapiCreateQuotationGroup(
  jwt: string,
  data: StrapiQuotationGroupCreateData,
): Promise<StrapiQuotationGroupRow> {
  const res = await fetch(`${STRAPI_URL}/api/quotation-groups`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const raw = asRecord(body)?.data ?? body;
  const row = normalizeQuotationGroupEntry(asRecord(raw));
  if (!row) throw new Error('Quotation group dibuat tanpa documentId.');
  return row;
}

export async function strapiUpdateQuotationGroup(
  jwt: string,
  documentId: string,
  data: StrapiQuotationGroupUpdateData,
): Promise<StrapiQuotationGroupRow> {
  const res = await fetch(`${STRAPI_URL}/api/quotation-groups/${encodeURIComponent(documentId)}`, {
    method: 'PUT',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ data }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const raw = asRecord(body)?.data ?? body;
  const row = normalizeQuotationGroupEntry(asRecord(raw));
  if (!row) throw new Error('Quotation group diperbarui tanpa documentId.');
  return row;
}

export async function strapiDeleteQuotationGroup(jwt: string, documentId: string): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/quotation-groups/${encodeURIComponent(documentId)}`, {
    method: 'DELETE',
    headers: strapiAuthHeaders(jwt),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(body, res.status));
  }
}

export async function strapiSyncQuotationGroupRecurringEvents(
  jwt: string,
  documentId: string,
  opts?: { quotationDocumentId?: string },
): Promise<{ event_count: number }> {
  const quotationDocumentId = opts?.quotationDocumentId?.trim() ?? '';
  const res = await fetch(
    `${STRAPI_URL}/api/quotation-groups/${encodeURIComponent(documentId)}/sync-recurring-events`,
    {
      method: 'POST',
      headers: {
        ...strapiAuthHeaders(jwt),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(quotationDocumentId ? { quotation: quotationDocumentId } : {}),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data ?? body;
  const event_count = Number(asRecord(data)?.event_count);
  return { event_count: Number.isFinite(event_count) ? event_count : 0 };
}

export type TestResultsListResult = { rows: StrapiTestResultRow[]; total: number };

const testResultFlatListPopulateQs =
  'populate[sample_information_point][populate][sample_information][fields][0]=documentId' +
  '&populate[sample_information_point][populate][sample_information][populate][parameter_ujis][fields][0]=documentId' +
  '&populate[sample_information_point][populate][sample_information][populate][parameter_ujis][fields][1]=nama_parameter' +
  '&populate[sample_information_point][populate][sample_information][populate][parameter_ujis][fields][2]=simbol' +
  '&populate[sample_information_point][populate][sample_information][populate][jenis_pengukuran][fields][0]=documentId' +
  '&populate[sample_information_point][populate][sample_information][populate][jenis_pengukuran][fields][1]=nama_jenis' +
  '&populate[sample_information_point][populate][sample_information][populate][jenis_pengukuran][fields][2]=kode_jenis' +
  '&populate[sample_information_point][populate][sample_information][populate][jenis_pengukuran][sort][0]=nama_jenis:asc' +
  '&populate[sample_information_point][populate][sample_information][populate][baku_mutu][fields][0]=documentId' +
  '&populate[sample_information_point][populate][sample_information][populate][baku_mutu][fields][1]=kode' +
  '&populate[sample_information_point][populate][sample_information][populate][baku_mutu][fields][2]=nama_baku_mutu' +
  '&populate[sample_information_point][populate][sample_information][populate][baku_mutu][fields][3]=judul' +
  '&populate[sample_information_point][populate][sample_information][populate][baku_mutu][sort][0]=nama_baku_mutu:asc' +
  '&populate[sample_information_point][populate][sample_information][populate][quotation][fields][0]=quotation_number' +
  '&populate[sample_information_point][populate][sample_information][populate][quotation][fields][1]=documentId';

export async function strapiListTestResults(
  jwt: string,
  opts?: { sampleDocumentId?: string; pageSize?: number },
): Promise<TestResultsListResult> {
  const params = new URLSearchParams({
    'pagination[pageSize]': String(opts?.pageSize ?? 200),
    'pagination[page]': '1',
    sort: 'createdAt:asc',
  });
  if (opts?.sampleDocumentId) {
    params.set(
      'filters[sample_information_point][sample_information][documentId][$eq]',
      opts.sampleDocumentId,
    );
  }
  const res = await fetch(`${STRAPI_URL}/api/test-results?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows, meta } = parseListBody(res, body);
  const total = meta.pagination?.total ?? rows.length;
  return { rows: rows.map((r) => normalizeTestResultEntry(r)), total };
}

/** All test result rows (flat), newest first, with sample + work order / quotation context. */
export async function strapiListAllTestResults(jwt: string, pageSize = 200): Promise<TestResultsListResult> {
  const params = new URLSearchParams({
    'pagination[pageSize]': String(pageSize),
    'pagination[page]': '1',
    sort: 'createdAt:desc',
  });
  const qs = `${params.toString()}&${testResultFlatListPopulateQs}`;
  const res = await fetch(`${STRAPI_URL}/api/test-results?${qs}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows, meta } = parseListBody(res, body);
  const total = meta.pagination?.total ?? rows.length;
  return { rows: rows.map((r) => normalizeTestResultEntry(r)), total };
}

export async function strapiDeleteTestResult(jwt: string, documentId: string): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/test-results/${encodeURIComponent(documentId)}`, {
    method: 'DELETE',
    headers: strapiAuthHeaders(jwt),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(body, res.status));
  }
}

/** Deletes the work order only (sample lines stay on the quotation). */
export async function strapiDeleteWorkOrderCascade(jwt: string, workOrderDocumentId: string): Promise<void> {
  await strapiDeleteWorkOrder(jwt, workOrderDocumentId);
}

export type SurveyRowStatus =
  | 'not_started'
  | 'in_progress'
  | 'finished'
  | 'customer_signed'
  | 'completed'
  | 'cancelled';

export type SurveyRescheduleStatus = 'pending_staff' | 'ready';

export type StrapiSurveyGridRow = {
  row_key: string;
  point_index: number;
  point_label: string;
  sampling_plan_document_id: string;
  work_order_document_id: string;
  survey_team_document_id: string;
  survey_user_id: number | null;
  survey_user_label: string;
  work_order_label: string;
  job_number: string;
  bidang_pengujian_label: string;
  /** true = v1 perencanaan_sampling 1 (air), false = 2/3 (udara/LB3). */
  is_air: boolean;
  sampling_plan_status: string;
  berita_acara_document_id: string | null;
  list_order: number | null;
  status: SurveyRowStatus;
  start_time: string | null;
  end_time: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  customer_approved_sign: string | null;
  surveyor_sign: string | null;
  reschedule_status?: SurveyRescheduleStatus | null;
  reschedule_date?: string | null;
  reschedule_schedule_document_id?: string | null;
  scheduled_date?: string | null;
};

export type SurveyRowsListOptions = {
  /** Filter tim survey by users-permissions numeric user id; omit for all rows. */
  userId?: number | null;
  /** Scope + auto-sync sampling plan ke berita acara. Default: aktif kecuali syncSamplingPlans === false. */
  workOrderDocumentId?: string | null;
  syncSamplingPlans?: boolean;
};

export type StrapiStartSurveyPresenceRow = {
  survey_team_document_id: string;
  is_presence: boolean;
  picture?: string;
};

export const LHU_GROUPED_NAME_OPTIONS = [
  { value: 'air_udara', label: 'Air & Udara' },
  { value: 'emisi_isokinetik', label: 'Emisi Isokinetik' },
  { value: 'emisi_non_isokinetik', label: 'Emisi Non Isokinetik' },
  { value: 'emisi_kendaraan', label: 'Emisi Kendaraan' },
  { value: 'plankton', label: 'Plankton' },
  { value: 'bentos', label: 'Bentos' },
] as const;

export type LhuGroupedName = (typeof LHU_GROUPED_NAME_OPTIONS)[number]['value'];

export type StrapiStartSurveyPayload = {
  sampling_plan_document_id: string;
  survey_team_document_id: string;
  point_index: number;
  longitude?: number;
  latitude?: number;
  /** Kehadiran per anggota tim survey work order. */
  presences: StrapiStartSurveyPresenceRow[];
};

export type StrapiFinishSurveyPayload = {
  end_time?: string;
  location_name: string;
  location_address?: string;
  location_sketsa_picture_file?: File;
  weather_condition: string;
  wind_direction_condition: string;
  water_temperature?: string;
  dhl?: string;
  water_debit?: string;
  air_temperature?: string;
  salinity?: string;
  water_clarity?: string;
  ph?: string;
  tds?: string;
  dissolved_oxygen?: string;
  jumlah_sampel?: string;
  is_having_blanko_wadah_penyerap?: boolean;
  abnormalitas_note?: string;
  keterangan_foto?: string;
  sample_point_collection: string;
  sample_collection_datetime?: string;
  lhu_grouped_name?: LhuGroupedName;
  longitude?: number;
  latitude?: number;
  form_answers?: Array<{
    form_question_document_id: string;
    form_question_option_document_id?: string;
    value: string;
  }>;
  form_answer_files?: Record<string, File>;
  jenis_pengukuran_document_id?: string;
  /** Parameter lapangan, kondisi lingkungan, dan detail kendaraan untuk titik ini. */
  dynamic_main_parameter?: {
    parameter_utama_lapangan: Record<string, string>;
    environmental_condition: Array<{ nama_parameter: string; unit: string; result: string }>;
    /** Hanya dikirim bila grup LHU adalah emisi kendaraan. */
    vehicle_information?: {
      fuel_type: string;
      hour_meter: string;
      production_year: string;
      vehicle_id: string;
      vehicle_type: string;
    };
  };
  /** false = simpan draft tanpa menandai titik selesai. */
  complete?: boolean;
};

function mapSurveyRowFromApi(row: unknown): StrapiSurveyGridRow {
  const r = asRecord(row) ?? {};
  const rescheduleStatusRaw = String(r.reschedule_status ?? '').trim();
  const reschedule_status =
    rescheduleStatusRaw === 'pending_staff' || rescheduleStatusRaw === 'ready'
      ? (rescheduleStatusRaw as SurveyRescheduleStatus)
      : null;
  return {
    row_key: String(r.row_key ?? ''),
    point_index: Number(r.point_index) || 1,
    point_label: String(r.point_label ?? ''),
    sampling_plan_document_id: String(r.sampling_plan_document_id ?? ''),
    work_order_document_id: String(r.work_order_document_id ?? ''),
    survey_team_document_id: String(r.survey_team_document_id ?? ''),
    survey_user_id:
      r.survey_user_id != null && Number.isFinite(Number(r.survey_user_id))
        ? Number(r.survey_user_id)
        : null,
    survey_user_label: String(r.survey_user_label ?? ''),
    work_order_label: String(r.work_order_label ?? ''),
    job_number: String(r.job_number ?? '').trim(),
    bidang_pengujian_label: String(r.bidang_pengujian_label ?? ''),
    is_air: r.is_air === true || r.is_air === 'true',
    sampling_plan_status: String(r.sampling_plan_status ?? ''),
    berita_acara_document_id: r.berita_acara_document_id
      ? String(r.berita_acara_document_id)
      : null,
    list_order: (() => {
      const n = Number(r.list_order);
      return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
    })(),
    status: (String(r.status ?? 'not_started') as SurveyRowStatus) || 'not_started',
    start_time: r.start_time ? String(r.start_time) : null,
    end_time: r.end_time ? String(r.end_time) : null,
    cancelled_at: r.cancelled_at ? String(r.cancelled_at) : null,
    cancel_reason: r.cancel_reason ? String(r.cancel_reason) : null,
    customer_approved_sign: r.customer_approved_sign ? String(r.customer_approved_sign) : null,
    surveyor_sign: r.surveyor_sign ? String(r.surveyor_sign) : null,
    reschedule_status,
    reschedule_date: r.reschedule_date ? String(r.reschedule_date) : null,
    reschedule_schedule_document_id: r.reschedule_schedule_document_id
      ? String(r.reschedule_schedule_document_id)
      : null,
    scheduled_date: r.scheduled_date ? String(r.scheduled_date) : null,
  };
}

function parseSurveyRowsFromBody(body: unknown): StrapiSurveyGridRow[] {
  const b = asRecord(body);
  const data = b?.data;
  if (!Array.isArray(data)) return [];
  return data.map((row) => mapSurveyRowFromApi(row));
}

function surveyLetterStatusFromRow(letter: DynamicEntityRow | null): SurveyRowStatus {
  if (!letter) return 'not_started';
  if (String(letter.cancelled_at ?? '').trim()) return 'cancelled';
  const end = letter.end_time;
  const cust = String(letter.customer_approved_sign ?? '').trim();
  const surv = String(letter.surveyor_sign ?? '').trim();
  if (cust && surv) return 'completed';
  if (cust) return 'customer_signed';
  if (end) return 'finished';
  return 'in_progress';
}

export function bidangLabelFromSamplingPlan(
  plan: DynamicEntityRow,
  allPlans: DynamicEntityRow[] = [],
  sampleLines: StrapiSampleInformationRow[] = [],
): string {
  const planScope = allPlans.length ? allPlans : [plan];
  const jenisByQsi = mergeJenisIndexes(
    buildJenisByQsiFromSampleLines(sampleLines),
    buildJenisByQsiFromPlans(planScope),
  );
  const qsiUpdatedAtByQsi = mergeUpdatedAtIndexes(
    buildQsiUpdatedAtByDocIdFromSampleLines(sampleLines),
    buildQsiUpdatedAtByDocIdFromPlans(planScope),
  );
  return effectiveBidangLabelFromSamplingPlan(plan, jenisByQsi, qsiUpdatedAtByQsi);
}

async function strapiListBeritaAcaraLettersForWorkOrder(
  jwt: string,
  workOrderDocumentId: string,
): Promise<DynamicEntityRow[]> {
  const params = new URLSearchParams({
    'pagination[pageSize]': '500',
    'pagination[page]': '1',
    populate: '*',
  });
  params.append('filters[work_order][documentId][$eq]', workOrderDocumentId);
  const res = await fetch(`${STRAPI_URL}/api/berita-acara-letters?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  const { rows } = parseListBody(res, body);
  return rows.map((row) => normalizeDynamicRow(row));
}

function surveyUserLabelFromTeam(team: DynamicEntityRow): string {
  const label = surveyTeamMemberDisplayLabel(team);
  return !label || label === '—' ? '' : label;
}

function surveyTeamGroupLabelFromTeams(woTeams: DynamicEntityRow[]): string {
  const sorted = [...woTeams].sort((a, b) => {
    const leadA = Boolean(a.is_lead);
    const leadB = Boolean(b.is_lead);
    if (leadA !== leadB) return leadA ? -1 : 1;
    return surveyTeamMemberDisplayLabel(a).localeCompare(surveyTeamMemberDisplayLabel(b), 'id');
  });
  return sorted
    .map((team) => {
      const name = surveyTeamMemberDisplayLabel(team);
      if (!name || name === '—') return '';
      return Boolean(team.is_lead) ? `${name} (lead)` : name;
    })
    .filter(Boolean)
    .join(', ');
}

function surveyUserIdFromTeam(team: DynamicEntityRow): number | null {
  const u = team.user_idx;
  if (!u || typeof u !== 'object' || Array.isArray(u)) return null;
  const n = Number((u as Record<string, unknown>).id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function pickSurveyTeamRowForWorkOrder(
  woTeams: DynamicEntityRow[],
  filterUserId: number | null,
): DynamicEntityRow | null {
  if (!woTeams.length) return null;
  if (filterUserId != null) {
    return woTeams.find((t) => surveyUserIdFromTeam(t) === filterUserId) ?? null;
  }
  const lead = woTeams.find((t) => Boolean(t.is_lead));
  return lead ?? woTeams[0];
}

/** Susun baris survey dari Content API (fallback jika custom survey-rows gagal). */
async function buildSurveyRowsFromContentApi(
  jwt: string,
  options: SurveyRowsListOptions = {},
): Promise<StrapiSurveyGridRow[]> {
  const filterId =
    options.userId != null && Number.isFinite(options.userId) && options.userId > 0
      ? Math.floor(options.userId)
      : null;
  const woFilter = String(options.workOrderDocumentId ?? '').trim();
  const rows: StrapiSurveyGridRow[] = [];
  const teamsByWo = new Map<string, DynamicEntityRow[]>();

  if (filterId != null) {
    const userTeams = await strapiListSurveyTeamsForSchedule(jwt, { userNumericId: filterId });
    const woIds = new Set<string>();
    for (const team of userTeams) {
      const teamDocId = String(team.documentId ?? '').trim();
      if (!teamDocId) continue;
      let woRec: Record<string, unknown> | null = null;
      const wo = team.work_order;
      if (wo && typeof wo === 'object' && !Array.isArray(wo)) {
        woRec = wo as Record<string, unknown>;
      }
      if (!woRec?.documentId) {
        const teamRes = await fetch(
          `${STRAPI_URL}/api/survey-teams/${encodeURIComponent(teamDocId)}?populate[work_order][populate][0]=quotation`,
          { headers: strapiAuthHeaders(jwt) },
        );
        const teamBody = (await teamRes.json().catch(() => ({}))) as unknown;
        if (teamRes.ok) {
          const raw = asRecord(teamBody)?.data as Record<string, unknown> | undefined;
          if (raw) {
            const normalized = normalizeDynamicRow(raw);
            const wo2 = normalized.work_order;
            if (wo2 && typeof wo2 === 'object' && !Array.isArray(wo2)) {
              woRec = wo2 as Record<string, unknown>;
            }
          }
        }
      }
      const woDocId = String(woRec?.documentId ?? '').trim();
      if (woDocId) woIds.add(woDocId);
    }
    for (const woDocId of woIds) {
      const woTeams = await strapiResolveSurveyTeamsForWorkOrder(jwt, woDocId);
      if (woTeams.length) teamsByWo.set(woDocId, woTeams);
    }
  } else {
    const teams = await strapiListSurveyTeamsForSchedule(jwt, {});
    for (const team of teams) {
      const teamDocId = String(team.documentId ?? '').trim();
      if (!teamDocId) continue;

      let woRec: Record<string, unknown> | null = null;
      const wo = team.work_order;
      if (wo && typeof wo === 'object' && !Array.isArray(wo)) {
        woRec = wo as Record<string, unknown>;
      }
      if (!woRec?.documentId) {
        const teamRes = await fetch(
          `${STRAPI_URL}/api/survey-teams/${encodeURIComponent(teamDocId)}?populate[work_order][populate][0]=quotation`,
          { headers: strapiAuthHeaders(jwt) },
        );
        const teamBody = (await teamRes.json().catch(() => ({}))) as unknown;
        if (teamRes.ok) {
          const raw = asRecord(teamBody)?.data as Record<string, unknown> | undefined;
          if (raw) {
            const normalized = normalizeDynamicRow(raw);
            const wo2 = normalized.work_order;
            if (wo2 && typeof wo2 === 'object' && !Array.isArray(wo2)) {
              woRec = wo2 as Record<string, unknown>;
            }
          }
        }
      }
      const woDocId = String(woRec?.documentId ?? '').trim();
      if (!woDocId) continue;
      const list = teamsByWo.get(woDocId) ?? [];
      list.push(team);
      teamsByWo.set(woDocId, list);
    }
  }

  if (woFilter && !teamsByWo.has(woFilter)) {
    const woTeams = await strapiResolveSurveyTeamsForWorkOrder(jwt, woFilter);
    if (woTeams.length) teamsByWo.set(woFilter, woTeams);
  }

  for (const [woDocId, woTeams] of teamsByWo) {
    if (woFilter && woDocId !== woFilter) continue;
    const team = pickSurveyTeamRowForWorkOrder(woTeams, filterId);
    if (!team) continue;

    const teamDocId = String(team.documentId ?? '').trim();
    const wo = team.work_order;
    const woRec =
      wo && typeof wo === 'object' && !Array.isArray(wo) ? (wo as Record<string, unknown>) : null;
    if (!teamDocId || !woRec) continue;

    const job = String(woRec.job_number ?? '').trim();
    const q = woRec.quotation;
    const qn =
      q && typeof q === 'object' && !Array.isArray(q)
        ? String((q as Record<string, unknown>).quotation_number ?? '').trim()
        : '';
    const woLabel = [job, qn].filter(Boolean).join(' · ');

    const rawPlans = await strapiListSamplingPlansForWorkOrder(jwt, woDocId);
    const letters = await strapiListBeritaAcaraLettersForWorkOrder(jwt, woDocId);
    const letterCountByPlanId = letterCountBySamplingPlanFromLetters(letters);
    const plans = pickCanonicalSamplingPlans(rawPlans, letterCountByPlanId);
    const jenisByQsi = buildJenisByQsiFromPlans(rawPlans);
    const qsiUpdatedAtByQsi = buildQsiUpdatedAtByDocIdFromPlans(rawPlans);

    const letterByKey = new Map<string, DynamicEntityRow>();
    for (const letter of letters) {
      const sp = letter.sampling_plan;
      const planId =
        sp && typeof sp === 'object' && !Array.isArray(sp)
          ? String((sp as Record<string, unknown>).documentId ?? '').trim()
          : '';
      const pi = Number(letter.point_index);
      if (planId && pi >= 1) letterByKey.set(`${planId}:${pi}`, letter);
    }

    for (const plan of plans) {
      const planDocId = String(plan.documentId ?? '').trim();
      if (!planDocId) continue;
      const count = Math.max(1, Math.floor(Number(plan.sample_point_count) || 1));
      const bidang = effectiveBidangLabelFromSamplingPlan(plan, jenisByQsi, qsiUpdatedAtByQsi);
      for (let pi = 1; pi <= count; pi++) {
        const letter = letterByKey.get(`${planDocId}:${pi}`) ?? null;
        const letterDocId = letter ? String(letter.documentId ?? '').trim() : '';
        rows.push({
          row_key: `${planDocId}:${pi}`,
          point_index: pi,
          point_label: `Titik ${pi}`,
          sampling_plan_document_id: planDocId,
          work_order_document_id: woDocId,
          survey_team_document_id: teamDocId,
          survey_user_id: surveyUserIdFromTeam(team),
          survey_user_label:
            woTeams.length > 1 ? surveyTeamGroupLabelFromTeams(woTeams) : surveyUserLabelFromTeam(team),
          work_order_label: woLabel,
          job_number: job,
          bidang_pengujian_label: bidang,
          is_air: airGroupFromSamplingPlan(plan) === 'air',
          sampling_plan_status: String(plan.progress_status ?? 'draft').trim() || 'draft',
          berita_acara_document_id: letterDocId || null,
          list_order: (() => {
            const n = Number(letter?.list_order);
            return letterDocId && Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
          })(),
          status: surveyLetterStatusFromRow(letter),
          start_time: letter?.start_time ? String(letter.start_time) : null,
          end_time: letter?.end_time ? String(letter.end_time) : null,
          customer_approved_sign: letter?.customer_approved_sign
            ? String(letter.customer_approved_sign)
            : null,
          surveyor_sign: letter?.surveyor_sign ? String(letter.surveyor_sign) : null,
        });
      }
    }
  }

  return rows;
}

/** Sinkronkan sampling plan kanonik + bidang pengujian untuk satu work order (tanpa muat grid survey). */
export async function strapiSyncSamplingPlansForWorkOrder(
  jwt: string,
  workOrderDocumentId: string,
): Promise<{ canonical_plan_count: number }> {
  const woDocId = workOrderDocumentId.trim();
  if (!woDocId) throw new Error('work_order_document_id wajib.');

  const params = new URLSearchParams({ work_order_document_id: woDocId });
  const res = await fetch(`${STRAPI_URL}/api/berita-acara-letters/sync-sampling-plans?${params}`, {
    method: 'POST',
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data as Record<string, unknown> | undefined;
  return {
    canonical_plan_count: Number(data?.canonical_plan_count ?? 0),
  };
}

export type QuotationSampleSyncPendingItem =
  | {
      kind: 'new_sample';
      quotation_sample_information_document_id: string;
      sampling_plan_document_id: string | null;
      bidang_pengujian_label: string;
      point_count: number;
    }
  | {
      kind: 'extended_points';
      quotation_sample_information_document_id: string;
      sampling_plan_document_id: string;
      bidang_pengujian_label: string;
      previous_point_count: number;
      point_count: number;
      added_point_count: number;
    };

export type QuotationSampleSyncPreview = {
  work_order_document_id: string;
  quotation_document_id: string;
  has_pending: boolean;
  new_sample_count: number;
  extended_plan_count: number;
  added_point_total: number;
  items: QuotationSampleSyncPendingItem[];
};

function normalizeQuotationSampleSyncPreview(raw: unknown): QuotationSampleSyncPreview {
  const data = asRecord(raw) ?? {};
  const itemsRaw = Array.isArray(data.items) ? data.items : [];
  const items: QuotationSampleSyncPendingItem[] = [];
  for (const itemRaw of itemsRaw) {
    const item = asRecord(itemRaw);
    if (!item) continue;
    const kind = String(item.kind ?? '').trim();
    const bidang = String(item.bidang_pengujian_label ?? '').trim() || 'Jenis pengukuran';
    const qsiId = String(item.quotation_sample_information_document_id ?? '').trim();
    if (!qsiId) continue;
    if (kind === 'extended_points') {
      const prev = Math.max(1, Math.floor(Number(item.previous_point_count) || 1));
      const next = Math.max(prev, Math.floor(Number(item.point_count) || prev));
      items.push({
        kind: 'extended_points',
        quotation_sample_information_document_id: qsiId,
        sampling_plan_document_id: String(item.sampling_plan_document_id ?? '').trim(),
        bidang_pengujian_label: bidang,
        previous_point_count: prev,
        point_count: next,
        added_point_count: Math.max(0, next - prev),
      });
      continue;
    }
    items.push({
      kind: 'new_sample',
      quotation_sample_information_document_id: qsiId,
      sampling_plan_document_id: item.sampling_plan_document_id
        ? String(item.sampling_plan_document_id).trim()
        : null,
      bidang_pengujian_label: bidang,
      point_count: Math.max(1, Math.floor(Number(item.point_count) || 1)),
    });
  }
  return {
    work_order_document_id: String(data.work_order_document_id ?? '').trim(),
    quotation_document_id: String(data.quotation_document_id ?? '').trim(),
    has_pending: Boolean(data.has_pending) || items.length > 0,
    new_sample_count: Number(data.new_sample_count ?? items.filter((i) => i.kind === 'new_sample').length),
    extended_plan_count: Number(
      data.extended_plan_count ?? items.filter((i) => i.kind === 'extended_points').length,
    ),
    added_point_total: Number(data.added_point_total ?? 0),
    items,
  };
}

/** Preview sample quotation yang belum tersinkron ke plan / berita acara WO. */
export async function strapiPreviewQuotationSampleSync(
  jwt: string,
  workOrderDocumentId: string,
): Promise<QuotationSampleSyncPreview> {
  const woDocId = workOrderDocumentId.trim();
  if (!woDocId) throw new Error('work_order_document_id wajib.');
  const params = new URLSearchParams({ work_order_document_id: woDocId });
  const res = await fetch(
    `${STRAPI_URL}/api/berita-acara-letters/quotation-sample-sync-preview?${params}`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  return normalizeQuotationSampleSyncPreview(asRecord(body)?.data);
}

/** Sinkronkan sample baru / titik tambahan quotation ke sampling plan WO (untuk berita acara). */
export async function strapiSyncQuotationSamplesForWorkOrder(
  jwt: string,
  workOrderDocumentId: string,
): Promise<{
  attached_plan_count: number;
  updated_point_count_plans: number;
  canonical_plan_count: number;
}> {
  const woDocId = workOrderDocumentId.trim();
  if (!woDocId) throw new Error('work_order_document_id wajib.');

  const res = await fetch(`${STRAPI_URL}/api/berita-acara-letters/sync-quotation-samples`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ work_order_document_id: woDocId }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data as Record<string, unknown> | undefined;
  return {
    attached_plan_count: Number(data?.attached_plan_count ?? 0),
    updated_point_count_plans: Number(data?.updated_point_count_plans ?? 0),
    canonical_plan_count: Number(data?.canonical_plan_count ?? 0),
  };
}

export type AddSamplingPointsResult = {
  work_order_document_id: string;
  quotation_document_id: string;
  sampling_plan_document_id: string;
  bidang_pengujian_label: string;
  previous_point_count: number;
  point_count: number;
  added_point_count: number;
  quotation_status: string;
  remark_edit: string;
  canonical_plan_count: number;
};

export type AddQuotationSampleFromSamplingResult = {
  work_order_document_id: string;
  quotation_document_id: string;
  quotation_sample_information_document_id: string;
  sample_information_document_id: string;
  sample_point_count: number;
  quotation_status: string;
  remark_edit: string;
  attached_plan_count: number;
  canonical_plan_count: number;
};

/** Petugas sampling menambah satu sampel lengkap ke quotation (Need Attention). */
export async function strapiAddQuotationSampleFromSampling(
  jwt: string,
  input: {
    workOrderDocumentId: string;
    jenisPengukuranDocumentId: string;
    bakuMutuDocumentId: string;
    parameterUji: string;
    samplePointCount: number;
    harga?: number | null;
  },
): Promise<AddQuotationSampleFromSamplingResult> {
  const woDocId = input.workOrderDocumentId.trim();
  if (!woDocId) throw new Error('work_order_document_id wajib.');

  const res = await fetch(`${STRAPI_URL}/api/berita-acara-letters/add-quotation-sample`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      work_order_document_id: woDocId,
      jenis_pengukuran: input.jenisPengukuranDocumentId.trim(),
      baku_mutu: input.bakuMutuDocumentId.trim(),
      parameter_uji: input.parameterUji,
      sample_point_count: input.samplePointCount,
      harga: input.harga ?? null,
    }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data as Record<string, unknown> | undefined;
  return {
    work_order_document_id: String(data?.work_order_document_id ?? woDocId),
    quotation_document_id: String(data?.quotation_document_id ?? ''),
    quotation_sample_information_document_id: String(
      data?.quotation_sample_information_document_id ?? '',
    ),
    sample_information_document_id: String(data?.sample_information_document_id ?? ''),
    sample_point_count: Number(data?.sample_point_count ?? input.samplePointCount),
    quotation_status: String(data?.quotation_status ?? ''),
    remark_edit: String(data?.remark_edit ?? ''),
    attached_plan_count: Number(data?.attached_plan_count ?? 0),
    canonical_plan_count: Number(data?.canonical_plan_count ?? 0),
  };
}

/** Petugas sampling menambah titik pada bidang pengujian; BA / WO / quotation ikut bertambah. */
export async function strapiAddSamplingPointsForWorkOrder(
  jwt: string,
  input: {
    workOrderDocumentId: string;
    samplingPlanDocumentId: string;
    addedPointCount: number;
  },
): Promise<AddSamplingPointsResult> {
  const woDocId = input.workOrderDocumentId.trim();
  const planDocId = input.samplingPlanDocumentId.trim();
  if (!woDocId) throw new Error('work_order_document_id wajib.');
  if (!planDocId) throw new Error('sampling_plan_document_id wajib.');

  const res = await fetch(`${STRAPI_URL}/api/berita-acara-letters/add-sampling-points`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      work_order_document_id: woDocId,
      sampling_plan_document_id: planDocId,
      added_point_count: input.addedPointCount,
    }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data as Record<string, unknown> | undefined;
  return {
    work_order_document_id: String(data?.work_order_document_id ?? woDocId),
    quotation_document_id: String(data?.quotation_document_id ?? ''),
    sampling_plan_document_id: String(data?.sampling_plan_document_id ?? planDocId),
    bidang_pengujian_label: String(data?.bidang_pengujian_label ?? ''),
    previous_point_count: Number(data?.previous_point_count ?? 0),
    point_count: Number(data?.point_count ?? 0),
    added_point_count: Number(data?.added_point_count ?? input.addedPointCount),
    quotation_status: String(data?.quotation_status ?? ''),
    remark_edit: String(data?.remark_edit ?? ''),
    canonical_plan_count: Number(data?.canonical_plan_count ?? 0),
  };
}

export async function strapiListSurveyRows(
  jwt: string,
  options: SurveyRowsListOptions = {},
): Promise<StrapiSurveyGridRow[]> {
  const params = new URLSearchParams();
  const filterId =
    options.userId != null && Number.isFinite(options.userId) && options.userId > 0
      ? Math.floor(options.userId)
      : null;
  if (filterId != null) params.set('user_id', String(filterId));

  const woDocId = String(options.workOrderDocumentId ?? '').trim();
  if (woDocId) params.set('work_order_document_id', woDocId);
  if (options.syncSamplingPlans !== false) params.set('sync_sampling_plans', '1');

  const qs = params.toString();
  const res = await fetch(
    `${STRAPI_URL}/api/berita-acara-letters/survey-rows${qs ? `?${qs}` : ''}`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (res.ok) return parseSurveyRowsFromBody(body);
  return buildSurveyRowsFromContentApi(jwt, options);
}

export type StrapiStartSurveyResult = {
  letter: DynamicEntityRow;
  surveyRowPatch: {
    row_key: string;
    berita_acara_document_id: string;
    status: 'in_progress';
    start_time: string;
    list_order: number | null;
  } | null;
};

export type StrapiCancelSurveyPointPayload = {
  sampling_plan_document_id: string;
  survey_team_document_id: string;
  point_index: number;
  cancel_reason: string;
};

export type StrapiCancelSurveyPointResult = {
  letter: DynamicEntityRow;
  surveyRowPatch: {
    row_key: string;
    berita_acara_document_id: string;
    status: 'cancelled';
    list_order: null;
    cancelled_at: string;
    cancel_reason: string;
  } | null;
};

export type StrapiRescheduleProposePayload = {
  sampling_plan_document_id: string;
  survey_team_document_id: string;
  point_index: number;
  proposed_date: string;
  reason?: string;
};

export type StrapiRescheduleProposeResult = {
  reschedule_schedule_document_id: string;
  proposed_date: string;
  surveyRowPatch: {
    row_key: string;
    reschedule_status: 'pending_staff';
    reschedule_date: string;
    reschedule_schedule_document_id: string;
    scheduled_date: string;
  } | null;
};

export async function strapiRescheduleProposeSurveyPoint(
  jwt: string,
  payload: StrapiRescheduleProposePayload,
): Promise<StrapiRescheduleProposeResult> {
  const res = await fetch(`${STRAPI_URL}/api/berita-acara-letters/reschedule-propose`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const b = asRecord(body);
  const data = asRecord(b?.data);
  const meta = asRecord(b?.meta);
  const patchRaw = asRecord(meta?.survey_row);
  if (!data) throw new Error('Invalid reschedule propose response');
  return {
    reschedule_schedule_document_id: String(data.reschedule_schedule_document_id ?? ''),
    proposed_date: String(data.proposed_date ?? payload.proposed_date),
    surveyRowPatch: patchRaw
      ? {
          row_key: String(patchRaw.row_key ?? ''),
          reschedule_status: 'pending_staff',
          reschedule_date: String(patchRaw.reschedule_date ?? payload.proposed_date),
          reschedule_schedule_document_id: String(
            patchRaw.reschedule_schedule_document_id ?? data.reschedule_schedule_document_id ?? '',
          ),
          scheduled_date: String(patchRaw.scheduled_date ?? payload.proposed_date),
        }
      : null,
  };
}

export async function strapiCancelSurveyPoint(
  jwt: string,
  payload: StrapiCancelSurveyPointPayload,
): Promise<StrapiCancelSurveyPointResult> {
  const res = await fetch(`${STRAPI_URL}/api/berita-acara-letters/cancel`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const b = asRecord(body);
  const row = b?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid cancel survey response');
  const letter = normalizeDynamicRow(row);
  const meta = asRecord(b?.meta);
  const patchRaw = asRecord(meta?.survey_row);
  const rowKey = String(patchRaw?.row_key ?? '').trim();
  const letterDocId = String(
    patchRaw?.berita_acara_document_id ?? letter.documentId ?? '',
  ).trim();
  const cancelledAt = patchRaw?.cancelled_at
    ? String(patchRaw.cancelled_at)
    : letter.cancelled_at
      ? String(letter.cancelled_at)
      : new Date().toISOString();
  const cancelReason = String(patchRaw?.cancel_reason ?? letter.cancel_reason ?? '').trim();
  const surveyRowPatch =
    rowKey && letterDocId
      ? {
          row_key: rowKey,
          berita_acara_document_id: letterDocId,
          status: 'cancelled' as const,
          list_order: null,
          cancelled_at: cancelledAt,
          cancel_reason: cancelReason,
        }
      : null;
  return { letter, surveyRowPatch };
}

export async function strapiStartSurvey(
  jwt: string,
  payload: StrapiStartSurveyPayload,
): Promise<StrapiStartSurveyResult> {
  const res = await fetch(`${STRAPI_URL}/api/berita-acara-letters/start`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const b = asRecord(body);
  const row = b?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid start survey response');
  const letter = normalizeDynamicRow(row);
  const meta = asRecord(b?.meta);
  const patchRaw = asRecord(meta?.survey_row);
  const rowKey = String(patchRaw?.row_key ?? '').trim();
  const letterDocId = String(
    patchRaw?.berita_acara_document_id ?? letter.documentId ?? '',
  ).trim();
  const startTime = patchRaw?.start_time
    ? String(patchRaw.start_time)
    : letter.start_time
      ? String(letter.start_time)
      : new Date().toISOString();
  const listOrderRaw = patchRaw?.list_order ?? letter.list_order;
  const listOrderN = Number(listOrderRaw);
  const list_order = Number.isFinite(listOrderN) && listOrderN >= 1 ? Math.floor(listOrderN) : null;
  const surveyRowPatch =
    rowKey && letterDocId
      ? {
          row_key: rowKey,
          berita_acara_document_id: letterDocId,
          status: 'in_progress' as const,
          start_time: startTime,
          list_order,
        }
      : null;
  return { letter, surveyRowPatch };
}

export async function strapiFinishSurvey(
  jwt: string,
  beritaAcaraDocumentId: string,
  payload: StrapiFinishSurveyPayload,
): Promise<DynamicEntityRow> {
  const { location_sketsa_picture_file, form_answer_files, ...jsonPayload } = payload;
  const answerFiles = Object.entries(form_answer_files ?? {}).filter((entry): entry is [string, File] => {
    return Boolean(entry[0].trim()) && entry[1] instanceof File;
  });
  const hasFile = location_sketsa_picture_file instanceof File || answerFiles.length > 0;
  const res = await fetch(
    `${STRAPI_URL}/api/berita-acara-letters/${encodeURIComponent(beritaAcaraDocumentId)}/finish`,
    {
      method: 'PUT',
      headers: hasFile
        ? strapiAuthHeaders(jwt)
        : {
            ...(strapiAuthHeaders(jwt) as Record<string, string>),
            'Content-Type': 'application/json',
          },
      body: hasFile
        ? (() => {
            const form = new FormData();
            form.append('data', JSON.stringify(jsonPayload));
            if (location_sketsa_picture_file instanceof File) {
              form.append('files.location_sketsa_picture', location_sketsa_picture_file);
            }
            for (const [key, file] of answerFiles) {
              form.append(`files.form_answer.${key}`, file);
            }
            return form;
          })()
        : JSON.stringify(jsonPayload),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid finish survey response');
  return normalizeDynamicRow(row);
}

export async function strapiVerifyBeritaAcaraFormSampling(
  jwt: string,
  beritaAcaraDocumentId: string,
  verified: boolean,
): Promise<{ documentId: string; verified: boolean }> {
  const res = await fetch(
    `${STRAPI_URL}/api/berita-acara-letters/${encodeURIComponent(beritaAcaraDocumentId)}/verify-form-sampling`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ verified }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(asRecord(body)?.data) ?? {};
  return {
    documentId: String(data.documentId ?? beritaAcaraDocumentId),
    verified: data.verified === true,
  };
}

export async function strapiBulkSignBeritaAcara(
  jwt: string,
  kind: 'customer' | 'surveyor',
  documentIds: string[],
  sign: string,
): Promise<void> {
  const path =
    kind === 'customer'
      ? '/api/berita-acara-letters/bulk-customer-sign'
      : '/api/berita-acara-letters/bulk-surveyor-sign';
  const res = await fetch(`${STRAPI_URL}${path}`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ document_ids: documentIds, sign }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
}

export async function strapiReorderBeritaAcaraLetters(
  jwt: string,
  workOrderDocumentId: string,
  orderedDocumentIds: string[],
): Promise<void> {
  const woDocId = workOrderDocumentId.trim();
  if (!woDocId) throw new Error('work_order_document_id wajib.');
  const ids = orderedDocumentIds.map((id) => String(id ?? '').trim()).filter(Boolean);
  if (!ids.length) throw new Error('ordered_document_ids wajib.');
  const res = await fetch(
    `${STRAPI_URL}/api/berita-acara-letters/work-order/${encodeURIComponent(woDocId)}/list-order`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ordered_document_ids: ids }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
}

export async function strapiDownloadWorkOrderBeritaAcaraPdf(
  jwt: string,
  workOrderDocumentId: string,
  opts?: { cacheBust?: boolean },
): Promise<Blob> {
  const qs = opts?.cacheBust ? `?t=${Date.now()}` : '';
  const res = await fetch(
    `${STRAPI_URL}/api/berita-acara-letters/work-order/${encodeURIComponent(workOrderDocumentId)}/pdf${qs}`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
  return res.blob();
}

/** Kirim PDF berita acara yang sudah ditandatangani ke satu atau lebih alamat email. */
export async function strapiSendWorkOrderBeritaAcaraPdf(
  jwt: string,
  workOrderDocumentId: string,
  emails: string[],
): Promise<void> {
  const recipients = [...new Set(emails.map((email) => email.trim()).filter(Boolean))];
  if (!recipients.length) throw new Error('Alamat email wajib.');
  const res = await fetch(
    `${STRAPI_URL}/api/berita-acara-letters/work-order/${encodeURIComponent(workOrderDocumentId)}/pdf/send`,
    {
      method: 'POST',
      headers: { ...strapiAuthHeaders(jwt), 'Content-Type': 'application/json' },
      body: JSON.stringify({ emails: recipients }),
    },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
}

/** Buka preview HTML berita acara di tab browser baru (untuk development styling). */
export async function strapiOpenWorkOrderBeritaAcaraPdfHtmlPreview(
  jwt: string,
  workOrderDocumentId: string,
): Promise<void> {
  const res = await fetch(
    `${STRAPI_URL}/api/berita-acara-letters/work-order/${encodeURIComponent(workOrderDocumentId)}/pdf/preview-html`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
  const html = await res.text();
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const tab = window.open(url, '_blank', 'noopener,noreferrer');
  if (!tab) {
    URL.revokeObjectURL(url);
    throw new Error('Pop-up diblokir browser. Izinkan pop-up untuk preview HTML.');
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Buka preview HTML RPS di tab browser baru (untuk development styling). */
export async function strapiOpenWorkOrderRpsPdfHtmlPreview(
  jwt: string,
  workOrderDocumentId: string,
): Promise<void> {
  const res = await fetch(
    `${STRAPI_URL}/api/sample-preservation-chains/work-order/${encodeURIComponent(workOrderDocumentId)}/rps-pdf/preview-html`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
  const html = await res.text();
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const tab = window.open(url, '_blank', 'noopener,noreferrer');
  if (!tab) {
    URL.revokeObjectURL(url);
    throw new Error('Pop-up diblokir browser. Izinkan pop-up untuk preview HTML.');
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function strapiDownloadWorkOrderRpsPdf(
  jwt: string,
  workOrderDocumentId: string,
  opts?: { cacheBust?: boolean },
): Promise<Blob> {
  const qs = opts?.cacheBust ? `?t=${Date.now()}` : '';
  const res = await fetch(
    `${STRAPI_URL}/api/sample-preservation-chains/work-order/${encodeURIComponent(workOrderDocumentId)}/rps-pdf${qs}`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
  return res.blob();
}

export async function strapiDownloadWorkOrderPermohonanPengujianPdf(
  jwt: string,
  workOrderDocumentId: string,
  opts?: { cacheBust?: boolean },
): Promise<Blob> {
  const qs = opts?.cacheBust ? `?t=${Date.now()}` : '';
  const res = await fetch(
    `${STRAPI_URL}/api/sample-preservation-chains/work-order/${encodeURIComponent(workOrderDocumentId)}/permohonan-pengujian-pdf${qs}`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
  return res.blob();
}

/** Buka preview HTML Permohonan Pengujian di tab browser baru (untuk development styling). */
export async function strapiOpenWorkOrderPermohonanPengujianPdfHtmlPreview(
  jwt: string,
  workOrderDocumentId: string,
): Promise<void> {
  const res = await fetch(
    `${STRAPI_URL}/api/sample-preservation-chains/work-order/${encodeURIComponent(workOrderDocumentId)}/permohonan-pengujian-pdf/preview-html`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
  const html = await res.text();
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const tab = window.open(url, '_blank', 'noopener,noreferrer');
  if (!tab) {
    URL.revokeObjectURL(url);
    throw new Error('Pop-up diblokir browser. Izinkan pop-up untuk preview HTML.');
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function strapiWorkOrderPdfStatus(
  jwt: string,
  workOrderDocumentId: string,
): Promise<boolean> {
  const woDocId = workOrderDocumentId.trim();
  if (!woDocId) throw new Error('work_order_document_id wajib.');
  const res = await fetch(`${STRAPI_URL}/api/work-orders/${encodeURIComponent(woDocId)}/pdf/status`, {
    headers: strapiAuthHeaders(jwt),
    cache: 'no-store',
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const rec = body && typeof body === 'object' ? (body as { data?: { signed?: unknown } }) : null;
  return Boolean(rec?.data?.signed);
}

export async function strapiSignWorkOrderPdf(
  jwt: string,
  workOrderDocumentId: string,
  sign: string,
): Promise<void> {
  const woDocId = workOrderDocumentId.trim();
  if (!woDocId) throw new Error('work_order_document_id wajib.');
  const signValue = sign.trim();
  if (!signValue) throw new Error('Tanda tangan PIC wajib.');
  const res = await fetch(`${STRAPI_URL}/api/work-orders/${encodeURIComponent(woDocId)}/pdf/sign`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ sign: signValue }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
}

export async function strapiDownloadWorkOrderPdf(
  jwt: string,
  workOrderDocumentId: string,
  opts?: { cacheBust?: boolean },
): Promise<Blob> {
  const qs = opts?.cacheBust ? `?t=${Date.now()}` : '';
  const res = await fetch(
    `${STRAPI_URL}/api/work-orders/${encodeURIComponent(workOrderDocumentId)}/pdf${qs}`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
  return res.blob();
}

/** Buka preview HTML PDF work order di tab browser baru (untuk development styling). */
export async function strapiOpenWorkOrderPdfHtmlPreview(
  jwt: string,
  workOrderDocumentId: string,
): Promise<void> {
  const res = await fetch(
    `${STRAPI_URL}/api/work-orders/${encodeURIComponent(workOrderDocumentId)}/pdf/preview-html`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
  const html = await res.text();
  const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const tab = window.open(url, '_blank', 'noopener,noreferrer');
  if (!tab) {
    URL.revokeObjectURL(url);
    throw new Error('Pop-up diblokir browser. Izinkan pop-up untuk preview HTML.');
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export type PreservationCompletionStatus = 'not_ready' | 'partial_completed' | 'all_completed';

export type PreservationWorkflowStep =
  | 'awaiting_signatures'
  | 'survey_in_progress'
  | 'all_completed'
  | 'received'
  | 'verified'
  | 'preserved';

export type PreservationRpsStatus = 'pending' | 'approved' | 'rejected';

export type StrapiPreservationGridRow = {
  row_key: string;
  work_order_document_id: string;
  work_order_job_number: string;
  work_order_label: string;
  /** ISO datetime BA terbaru (createdAt) untuk work order ini. */
  berita_acara_created_at: string | null;
  total_points: number;
  signed_points: number;
  /** Titik BA dibatalkan — tidak masuk denominator total_points. */
  cancelled_points: number;
  handed_over_points: number;
  /** Titik BA signed yang sudah diverifikasi admin form sampling. */
  verified_points?: number;
  completion_status: PreservationCompletionStatus;
  preservation_document_id: string | null;
  workflow_step: PreservationWorkflowStep;
  sample_received_datetime: string | null;
  rps_sampling_date: string | null;
  rps_sample_received_date: string | null;
  sample_verified_datetime: string | null;
  rps_status: PreservationRpsStatus;
  rps_reviewed_datetime: string | null;
  rps_reject_note: string;
  /** Server-computed: admin penerima sample may open Sample received for this row. */
  can_sample_received?: boolean;
  quotation_status?: string | null;
  sample_pickup_method?: string | null;
  sample_receive_blocked_reason?: string | null;
  /** Email customer quotation untuk pengiriman PDF berita acara. */
  customer_email?: string;
};

export type PreservationGridSortField =
  | 'berita_acara_created_at'
  | 'work_order_job_number'
  | 'work_order_label'
  | 'signed_points'
  | 'total_points'
  | 'handed_over_points'
  | 'completion_status'
  | 'workflow_step'
  | 'sample_received_datetime'
  | 'sample_verified_datetime'
  | 'rps_status'
  | 'rps_reviewed_datetime';

export type PreservationGridSort = {
  field: PreservationGridSortField;
  direction: 'asc' | 'desc';
};

export const defaultPreservationGridSort: PreservationGridSort = {
  field: 'berita_acara_created_at',
  direction: 'desc',
};

export type BeritaAcaraPreservationFormRow = {
  berita_acara_document_id: string;
  sampling_plan_document_id: string;
  point_index: number;
  point_label: string;
  list_order: number | null;
  /** Kode sampel `${job_number}-${list_order}`; kosong bila belum ada list_order. */
  sample_code: string;
  jenis_pengukuran_label: string;
  location_name: string;
  is_fully_signed: boolean;
  handed_over_to_office_at: string | null;
  verified: boolean;
  parameters: { document_id: string; name: string }[];
  preservation_suggestions: string[];
};

export type SampleConditionValue =
  | 'tidak_diawetkan_di_lapangan'
  | 'wadah_sampel_tidak_sesuai'
  | 'sampel_kadaluarsa'
  | 'lainnya';

export const SAMPLE_CONDITION_OPTIONS: { value: SampleConditionValue; label: string }[] = [
  { value: 'tidak_diawetkan_di_lapangan', label: 'Tidak diawetkan di lapangan' },
  { value: 'wadah_sampel_tidak_sesuai', label: 'Wadah sampel tidak sesuai' },
  { value: 'sampel_kadaluarsa', label: 'Sampel kadaluarsa' },
  { value: 'lainnya', label: 'Lainnya (sebutkan)' },
];

function mapPreservationGridRow(row: unknown): StrapiPreservationGridRow {
  const r = asRecord(row) ?? {};
  return {
    row_key: String(r.row_key ?? ''),
    work_order_document_id: String(r.work_order_document_id ?? ''),
    work_order_job_number: String(r.work_order_job_number ?? ''),
    work_order_label: String(r.work_order_label ?? ''),
    berita_acara_created_at: r.berita_acara_created_at ? String(r.berita_acara_created_at) : null,
    total_points: Number(r.total_points) || 0,
    signed_points: Number(r.signed_points) || 0,
    cancelled_points: Number(r.cancelled_points) || 0,
    handed_over_points: Number(r.handed_over_points) || 0,
    verified_points: Number(r.verified_points) || 0,
    completion_status:
      (String(r.completion_status ?? 'not_ready') as PreservationCompletionStatus) || 'not_ready',
    preservation_document_id: r.preservation_document_id ? String(r.preservation_document_id) : null,
    workflow_step: (() => {
      if (String(r.completion_status ?? '') === 'partial_completed') return 'survey_in_progress';
      const step = String(r.workflow_step ?? 'awaiting_signatures');
      if (step === 'partial_completed') return 'survey_in_progress';
      return (step as PreservationWorkflowStep) || 'awaiting_signatures';
    })(),
    sample_received_datetime: r.sample_received_datetime ? String(r.sample_received_datetime) : null,
    rps_sampling_date: r.rps_sampling_date ? String(r.rps_sampling_date).slice(0, 10) : null,
    rps_sample_received_date: r.rps_sample_received_date
      ? String(r.rps_sample_received_date).slice(0, 10)
      : null,
    sample_verified_datetime: r.sample_verified_datetime ? String(r.sample_verified_datetime) : null,
    rps_status:
      String(r.rps_status ?? '') === 'approved' || String(r.rps_status ?? '') === 'rejected'
        ? (String(r.rps_status) as PreservationRpsStatus)
        : 'pending',
    rps_reviewed_datetime: r.rps_reviewed_datetime ? String(r.rps_reviewed_datetime) : null,
    rps_reject_note: String(r.rps_reject_note ?? '').trim(),
    can_sample_received:
      typeof r.can_sample_received === 'boolean' ? r.can_sample_received : undefined,
    quotation_status: r.quotation_status ? String(r.quotation_status) : null,
    sample_pickup_method: r.sample_pickup_method ? String(r.sample_pickup_method) : null,
    sample_receive_blocked_reason: r.sample_receive_blocked_reason
      ? String(r.sample_receive_blocked_reason)
      : null,
    customer_email: String(r.customer_email ?? '').trim(),
  };
}

export async function strapiListPreservationGridRows(
  jwt: string,
  sort: PreservationGridSort = defaultPreservationGridSort,
): Promise<StrapiPreservationGridRow[]> {
  const qs = new URLSearchParams({
    sort: sort.field,
    order: sort.direction,
  });
  const res = await fetch(`${STRAPI_URL}/api/sample-preservation-chains/grid-rows?${qs}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data;
  if (!Array.isArray(data)) return [];
  return data.map((row) => mapPreservationGridRow(row));
}

export type SampleReceiveLogItem = {
  berita_acara_document_id: string;
  point_index: number;
  point_label: string;
  jenis_pengukuran_label: string;
  location_name: string;
};

export type SampleReceiveLogRow = {
  document_id: string;
  received_at: string | null;
  received_by_name: string;
  received_items: SampleReceiveLogItem[];
  preservation_by_customer: boolean;
  preservation_by_laboratory: boolean;
  received_condition_tidak_diawetkan_di_lapangan: boolean;
  received_condition_wadah_tidak_sesuai: boolean;
  received_condition_kadaluarsa: boolean;
  received_condition_lainnya: boolean;
  received_condition_note: string;
};

function mapSampleReceiveLogRow(raw: unknown): SampleReceiveLogRow {
  const r = asRecord(raw) ?? {};
  const items = Array.isArray(r.received_items) ? r.received_items : [];
  return {
    document_id: String(r.document_id ?? ''),
    received_at: r.received_at ? String(r.received_at) : null,
    received_by_name: String(r.received_by_name ?? '').trim(),
    received_items: items
      .map((item) => {
        const it = asRecord(item);
        if (!it) return null;
        return {
          berita_acara_document_id: String(it.berita_acara_document_id ?? ''),
          point_index: Number(it.point_index) || 0,
          point_label: String(it.point_label ?? ''),
          jenis_pengukuran_label: String(it.jenis_pengukuran_label ?? ''),
          location_name: String(it.location_name ?? ''),
        };
      })
      .filter((x): x is SampleReceiveLogItem => x != null),
    preservation_by_customer: Boolean(r.preservation_by_customer),
    preservation_by_laboratory: Boolean(r.preservation_by_laboratory),
    received_condition_tidak_diawetkan_di_lapangan: Boolean(
      r.received_condition_tidak_diawetkan_di_lapangan,
    ),
    received_condition_wadah_tidak_sesuai: Boolean(r.received_condition_wadah_tidak_sesuai),
    received_condition_kadaluarsa: Boolean(r.received_condition_kadaluarsa),
    received_condition_lainnya: Boolean(r.received_condition_lainnya),
    received_condition_note: String(r.received_condition_note ?? '').trim(),
  };
}

export async function strapiListSampleReceiveLogs(
  jwt: string,
  workOrderDocumentId: string,
): Promise<SampleReceiveLogRow[]> {
  const res = await fetch(
    `${STRAPI_URL}/api/sample-preservation-chains/work-order/${encodeURIComponent(workOrderDocumentId)}/receive-logs`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data;
  if (!Array.isArray(data)) return [];
  return data.map((row) => mapSampleReceiveLogRow(row));
}

export async function strapiListBeritaAcaraPreservationForms(
  jwt: string,
  workOrderDocumentId: string,
): Promise<BeritaAcaraPreservationFormRow[]> {
  const res = await fetch(
    `${STRAPI_URL}/api/sample-preservation-chains/work-order/${encodeURIComponent(workOrderDocumentId)}/berita-acara-forms`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data;
  if (!Array.isArray(data)) return [];
  return data.map((raw) => {
    const r = asRecord(raw) ?? {};
    const params = Array.isArray(r.parameters) ? r.parameters : [];
    const suggestions = Array.isArray(r.preservation_suggestions) ? r.preservation_suggestions : [];
    return {
      berita_acara_document_id: String(r.berita_acara_document_id ?? ''),
      sampling_plan_document_id: String(r.sampling_plan_document_id ?? ''),
      point_index: Number(r.point_index) || 1,
      point_label: String(r.point_label ?? ''),
      list_order: Number(r.list_order) >= 1 ? Math.floor(Number(r.list_order)) : null,
      sample_code: String(r.sample_code ?? '').trim(),
      jenis_pengukuran_label: String(r.jenis_pengukuran_label ?? ''),
      location_name: String(r.location_name ?? ''),
      is_fully_signed: r.is_fully_signed !== false,
      handed_over_to_office_at: r.handed_over_to_office_at
        ? String(r.handed_over_to_office_at)
        : null,
      verified: r.verified === true,
      parameters: params
        .map((p) => {
          const pr = asRecord(p);
          if (!pr) return null;
          return {
            document_id: String(pr.document_id ?? ''),
            name: String(pr.name ?? ''),
          };
        })
        .filter((x): x is { document_id: string; name: string } => Boolean(x?.document_id && x?.name)),
      preservation_suggestions: suggestions.map((s) => String(s)).filter(Boolean),
    };
  });
}

export async function strapiSamplePreservationReceived(
  jwt: string,
  payload: {
    work_order_document_id: string;
    sample_received_datetime: string;
    sample_received_by_user_id: number;
    rps_sampling_date: string;
    rps_sample_received_date: string;
    berita_acara_document_ids: string[];
    sign: string;
    preservation_by_customer?: boolean;
    preservation_by_laboratory?: boolean;
    received_condition_tidak_diawetkan_di_lapangan?: boolean;
    received_condition_wadah_tidak_sesuai?: boolean;
    received_condition_kadaluarsa?: boolean;
    received_condition_lainnya?: boolean;
    received_condition_note?: string;
  },
): Promise<DynamicEntityRow> {
  const res = await fetch(`${STRAPI_URL}/api/sample-preservation-chains/sample-received`, {
    method: 'POST',
    headers: {
      ...(strapiAuthHeaders(jwt) as Record<string, string>),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid sample received response');
  return normalizeDynamicRow(row);
}

export async function strapiVerifySamplePreservation(
  jwt: string,
  preservationDocumentId: string,
  payload: {
    sample_verified_datetime: string;
    sample_verified_by_user_id: number;
    sign: string;
    sample_preservation_by_user_id: number;
  },
): Promise<DynamicEntityRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/sample-preservation-chains/${encodeURIComponent(preservationDocumentId)}/verify`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid verify sample response');
  return normalizeDynamicRow(row);
}

export type PreservationLocationInput = {
  berita_acara_document_id: string;
  jenis_pengukuran_name?: string;
  parameter_name?: string;
  preservation_material_name: string;
};

export async function strapiPreserveSample(
  jwt: string,
  preservationDocumentId: string,
  payload: {
    sample_condition: SampleConditionValue;
    sample_condition_note?: string;
    preservation_locations: PreservationLocationInput[];
  },
): Promise<DynamicEntityRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/sample-preservation-chains/${encodeURIComponent(preservationDocumentId)}/preserve`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid preserve sample response');
  return normalizeDynamicRow(row);
}

export async function strapiApproveWorkOrderRps(
  jwt: string,
  workOrderDocumentId: string,
  sign: string,
): Promise<DynamicEntityRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/sample-preservation-chains/work-order/${encodeURIComponent(workOrderDocumentId)}/approve-rps`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sign }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid approve RPS response');
  return normalizeDynamicRow(row);
}

export async function strapiRejectWorkOrderRps(
  jwt: string,
  workOrderDocumentId: string,
  note: string,
): Promise<DynamicEntityRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/sample-preservation-chains/work-order/${encodeURIComponent(workOrderDocumentId)}/reject-rps`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ note }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid reject RPS response');
  return normalizeDynamicRow(row);
}

export type LaboratoryResultGridStatus = 'pending' | 'draft' | 'ready';

export type StrapiLaboratoryResultGridRow = {
  row_key: string;
  work_order_document_id: string;
  work_order_job_number: string;
  work_order_label: string;
  berita_acara_document_id: string;
  point_index: number;
  /** Kode sampel berita acara. Kosong bila letter belum punya list_order. */
  list_order?: number | null;
  point_label: string;
  location_name: string;
  parameter_uji_document_id: string;
  parameter_name: string;
  parameter_simbol: string;
  /** Hasil uji pada titik ini. Kosong bila belum diisi. */
  result?: string;
  rps_approved_datetime: string | null;
  testing_done_datetime: string | null;
  baku_mutu: string;
  jenis_sampel: string;
  laboratory_result_document_id: string | null;
  status: LaboratoryResultGridStatus;
  analis_types?: string[];
  sampling?: LaboratoryResultSamplingSnapshot;
};

export type LaboratoryResultSamplingOption = {
  document_id: string;
  title: string;
  sort_order: number;
  answer_type?: string;
  start?: number | null;
  end?: number | null;
};

export type LaboratoryResultSamplingOptionValue = {
  option_document_id: string;
  option_title: string;
  value: string;
};

export type LaboratoryResultSamplingQuestion = {
  document_id: string;
  title: string;
  answer_type: string;
  has_option: boolean;
  sort_order: number;
  options: LaboratoryResultSamplingOption[];
  value: string;
  option_values: LaboratoryResultSamplingOptionValue[];
};

export type LaboratoryResultSamplingGroup = {
  form_question_name: string;
  questions: LaboratoryResultSamplingQuestion[];
};

export type LaboratoryResultSamplingSnapshot = {
  start_time: string;
  end_time: string;
  sample_collection_datetime: string;
  jumlah_sampel: string;
  abnormalitas_note: string;
  foto_url: string;
  air_group: 'air' | 'non_air';
  field_measurements: { label: string; value: string }[];
  form_answer_groups: LaboratoryResultSamplingGroup[];
};

export type LaboratoryResultParameterReviewStatus = 'pending' | 'approved' | 'rejected';

export type LaboratoryResultBeritaAcaraEntry = {
  berita_acara_document_id: string;
  point_index: number;
  point_label: string;
  row_label: string;
  location_name: string;
  location_address: string;
  bidang_label: string;
  jenis_sampel?: string;
  /** Regulasi sample cocok dengan faktor koreksi emisi. Input corrected result hanya tampil bila true. */
  has_emission_correction?: boolean;
  /** Grup LHU berita acara (`emisi_isokinetik`, `plankton`, `bentos`, dan seterusnya). */
  lhu_grouped_name?: string | null;
  /** HTML secondary data yang diisi analis lab lewat editor. */
  secondary_data_html?: string;
  status: LaboratoryResultGridStatus;
  sampling?: LaboratoryResultSamplingSnapshot;
  parameters: {
    parameter_uji_document_id: string;
    parameter_name: string;
    parameter_simbol?: string;
    /** `parameter_uji_analises.analis_type` matching the sample's jenis pengukuran. */
    analis_types?: string[];
    /** Satuan dari baku mutu parameter atau metoda default. */
    parameter_unit?: string;
    /** Master `parameter_uji.data` adalah "Parameter Lapangan". */
    is_parameter_lapangan?: boolean;
    /** Nilai `dynamic_main_parameter.parameter_utama_lapangan` untuk parameter ini. */
    parameter_lapangan_value?: string;
    result: string;
    result_corrected?: string;
    review_status?: LaboratoryResultParameterReviewStatus;
    review_note?: string;
    reviewed_at?: string | null;
  }[];
};

export type LaboratoryResultWorkOrderFormData = {
  work_order_document_id: string;
  work_order_job_number: string;
  work_order_label: string;
  laboratory_result_document_id: string | null;
  status: LaboratoryResultGridStatus;
  approved_by_user_id?: number | null;
  berita_acara: LaboratoryResultBeritaAcaraEntry[];
};

export type LaboratoryResultWorkOrderPreviewData = {
  work_order: { document_id: string; job_number: string; label: string } | null;
  quotation: { document_id: string; quotation_number: string; customer_name: string } | null;
  berita_acara_sections: {
    berita_acara_document_id: string;
    point_label: string;
    row_label: string;
    location_name: string;
    location_address: string;
    bidang_label: string;
    preservation_locations: { preservation_material_name: string; parameter_name: string }[];
    laboratory_result_items: {
      parameter_name: string;
      parameter_uji_document_id?: string;
      result: string;
      review_status?: LaboratoryResultParameterReviewStatus;
      review_note?: string;
      reviewed_at?: string | null;
    }[];
  }[];
};

export type LaboratoryResultFormData = {
  berita_acara_document_id: string;
  laboratory_result_document_id: string | null;
  status: LaboratoryResultGridStatus;
  approved_by_user_id?: number | null;
  detail: {
    row_label: string;
    location_name: string;
    location_address: string;
  };
  sampling_plan: {
    document_id: string;
    bidang_label: string;
    quotation_sample_information: {
      document_id: string;
      jenis_pengukuran_label: string;
      parameter_names: string[];
    } | null;
  } | null;
  parameters: {
    parameter_uji_document_id: string;
    parameter_name: string;
    parameter_simbol?: string;
    result: string;
  }[];
};

export type LaboratoryResultPreviewData = {
  laboratory_result: { document_id: string; result_status: string; approved_by_user_id: number | null };
  quotation: { document_id: string; quotation_number: string; customer_name: string } | null;
  work_order: { document_id: string; job_number: string; label: string } | null;
  berita_acara: {
    document_id: string;
    point_index: number;
    row_label: string;
    location_name: string;
    location_address: string;
    weather_condition: string;
    sample_point_collection: string;
  } | null;
  sampling_plan: LaboratoryResultFormData['sampling_plan'];
  preservation_locations: {
    preservation_material_name: string;
    parameter_name: string;
    jenis_pengukuran_name: string;
  }[];
  laboratory_result_items: {
    parameter_name: string;
    parameter_uji_document_id: string;
    result: string;
  }[];
};

function mapLaboratorySamplingSnapshot(raw: unknown): LaboratoryResultSamplingSnapshot | undefined {
  const r = asRecord(raw);
  if (!r) return undefined;
  const groupsRaw = Array.isArray(r.form_answer_groups) ? r.form_answer_groups : [];
  const measurementsRaw = Array.isArray(r.field_measurements) ? r.field_measurements : [];
  return {
    start_time: String(r.start_time ?? ''),
    end_time: String(r.end_time ?? ''),
    sample_collection_datetime: String(r.sample_collection_datetime ?? ''),
    jumlah_sampel: String(r.jumlah_sampel ?? ''),
    abnormalitas_note: String(r.abnormalitas_note ?? ''),
    foto_url: String(r.foto_url ?? ''),
    air_group: String(r.air_group ?? '') === 'non_air' ? 'non_air' : 'air',
    field_measurements: measurementsRaw
      .map((item) => {
        const rec = asRecord(item);
        if (!rec) return null;
        const label = String(rec.label ?? '').trim();
        const value = String(rec.value ?? '').trim();
        if (!label && !value) return null;
        return { label, value };
      })
      .filter((x): x is { label: string; value: string } => x != null),
    form_answer_groups: groupsRaw
      .map((group) => {
        const g = asRecord(group);
        if (!g) return null;
        const questionsRaw = Array.isArray(g.questions) ? g.questions : [];
        return {
          form_question_name: String(g.form_question_name ?? '').trim() || 'Lainnya',
          questions: questionsRaw
            .map((qRaw) => {
              const q = asRecord(qRaw);
              if (!q) return null;
              const optionsRaw = Array.isArray(q.options) ? q.options : [];
              const optionValuesRaw = Array.isArray(q.option_values) ? q.option_values : [];
              return {
                document_id: String(q.document_id ?? ''),
                title: String(q.title ?? ''),
                answer_type: String(q.answer_type ?? 'text'),
                has_option: Boolean(q.has_option),
                sort_order: Number(q.sort_order) || 0,
                options: optionsRaw
                  .map((oRaw): LaboratoryResultSamplingOption | null => {
                    const o = asRecord(oRaw);
                    if (!o) return null;
                    const startN = Number(o.start);
                    const endN = Number(o.end);
                    return {
                      document_id: String(o.document_id ?? ''),
                      title: String(o.title ?? ''),
                      sort_order: Number(o.sort_order) || 0,
                      answer_type: String(o.answer_type ?? '').trim(),
                      start: o.start == null || o.start === '' || !Number.isInteger(startN) ? null : startN,
                      end: o.end == null || o.end === '' || !Number.isInteger(endN) ? null : endN,
                    };
                  })
                  .filter((x): x is LaboratoryResultSamplingOption => x != null),
                value: String(q.value ?? ''),
                option_values: optionValuesRaw
                  .map((vRaw) => {
                    const v = asRecord(vRaw);
                    if (!v) return null;
                    return {
                      option_document_id: String(v.option_document_id ?? ''),
                      option_title: String(v.option_title ?? ''),
                      value: String(v.value ?? ''),
                    };
                  })
                  .filter((x): x is LaboratoryResultSamplingOptionValue => x != null),
              };
            })
            .filter((x): x is LaboratoryResultSamplingQuestion => x != null),
        };
      })
      .filter((x): x is LaboratoryResultSamplingGroup => x != null),
  };
}

function parseGridListOrder(value: unknown): number | null {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.floor(n);
}

function mapLaboratoryGridRow(
  row: unknown,
  samplingByBa: Record<string, LaboratoryResultSamplingSnapshot | undefined>,
): StrapiLaboratoryResultGridRow {
  const r = asRecord(row) ?? {};
  const baId = String(r.berita_acara_document_id ?? '');
  return {
    row_key: String(r.row_key ?? `${r.work_order_document_id ?? ''}:${baId}:${r.parameter_uji_document_id ?? ''}`),
    work_order_document_id: String(r.work_order_document_id ?? ''),
    work_order_job_number: String(r.work_order_job_number ?? ''),
    work_order_label: String(r.work_order_label ?? ''),
    berita_acara_document_id: baId,
    point_index: Number(r.point_index) || 0,
    list_order: parseGridListOrder(r.list_order),
    point_label: String(r.point_label ?? ''),
    location_name: String(r.location_name ?? ''),
    parameter_uji_document_id: String(r.parameter_uji_document_id ?? ''),
    parameter_name: String(r.parameter_name ?? ''),
    parameter_simbol: String(r.parameter_simbol ?? ''),
    result: String(r.result ?? '').trim(),
    rps_approved_datetime: r.rps_approved_datetime ? String(r.rps_approved_datetime) : null,
    testing_done_datetime: r.testing_done_datetime ? String(r.testing_done_datetime) : null,
    baku_mutu: String(r.baku_mutu ?? ''),
    jenis_sampel: String(r.jenis_sampel ?? ''),
    laboratory_result_document_id: r.laboratory_result_document_id
      ? String(r.laboratory_result_document_id)
      : null,
    status: (String(r.status ?? 'pending') as LaboratoryResultGridStatus) || 'pending',
    analis_types: Array.isArray(r.analis_types)
      ? r.analis_types.map((type) => String(type ?? '').trim()).filter(Boolean)
      : [],
    sampling: samplingByBa[baId],
  };
}

export type LaboratoryResultListFilters = {
  jobNumber: string;
  parameterName: string;
  rpsApprovedDate: string;
  testingDoneDate: string;
  bakuMutu: string;
  jenisSampel: string;
  /** Exact analis type titles. Admin analis lab sends every type assigned to them. */
  analisTypes: readonly string[];
};

export const emptyLaboratoryResultListFilters = Object.freeze<LaboratoryResultListFilters>({
  jobNumber: '',
  parameterName: '',
  rpsApprovedDate: '',
  testingDoneDate: '',
  bakuMutu: '',
  jenisSampel: '',
  analisTypes: [],
});

function appendLaboratoryResultListFilters(
  params: URLSearchParams,
  f: LaboratoryResultListFilters,
): void {
  const nz = (v: string) => v.trim();
  if (nz(f.jobNumber)) params.set('jobNumber', nz(f.jobNumber));
  if (nz(f.parameterName)) params.set('parameterName', nz(f.parameterName));
  if (nz(f.rpsApprovedDate)) params.set('rpsApprovedDate', nz(f.rpsApprovedDate));
  if (nz(f.testingDoneDate)) params.set('testingDoneDate', nz(f.testingDoneDate));
  if (nz(f.bakuMutu)) params.set('bakuMutu', nz(f.bakuMutu));
  if (nz(f.jenisSampel)) params.set('jenisSampel', nz(f.jenisSampel));
  for (const type of f.analisTypes) {
    const value = type.trim();
    if (value) params.append('analisTypes', value);
  }
}

export type StrapiLaboratoryResultGridRowsResult = {
  rows: StrapiLaboratoryResultGridRow[];
  rowCount: number;
  unfilteredRowCount: number;
};

export async function strapiListLaboratoryResultGridRows(
  jwt: string,
  filters: LaboratoryResultListFilters = emptyLaboratoryResultListFilters,
): Promise<StrapiLaboratoryResultGridRowsResult> {
  const params = new URLSearchParams();
  appendLaboratoryResultListFilters(params, filters);
  const qs = params.toString();
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/grid-rows${qs ? `?${qs}` : ''}`,
    {
      headers: strapiAuthHeaders(jwt),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const rec = asRecord(body);
  const data = rec?.data;
  const meta = asRecord(rec?.meta);
  const samplingRaw = asRecord(meta?.sampling_by_berita_acara) ?? {};
  const samplingByBa: Record<string, LaboratoryResultSamplingSnapshot | undefined> = {};
  for (const [baId, snapshot] of Object.entries(samplingRaw)) {
    samplingByBa[baId] = mapLaboratorySamplingSnapshot(snapshot);
  }
  const rows = Array.isArray(data)
    ? data.map((row) => mapLaboratoryGridRow(row, samplingByBa))
    : [];
  const rowCount =
    meta?.row_count != null && Number.isFinite(Number(meta.row_count))
      ? Number(meta.row_count)
      : rows.length;
  const unfilteredRowCount =
    meta?.unfiltered_row_count != null && Number.isFinite(Number(meta.unfiltered_row_count))
      ? Number(meta.unfiltered_row_count)
      : rows.length;
  return { rows, rowCount, unfilteredRowCount };
}

export async function strapiGetLaboratoryWorkOrderForm(
  jwt: string,
  workOrderDocumentId: string,
): Promise<LaboratoryResultWorkOrderFormData> {
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/work-order/${encodeURIComponent(workOrderDocumentId)}/form`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data;
  if (!data || typeof data !== 'object') throw new Error('Invalid laboratory work order form response');
  return data as LaboratoryResultWorkOrderFormData;
}

export async function strapiSaveLaboratoryWorkOrder(
  jwt: string,
  workOrderDocumentId: string,
  payload: {
    status: 'draft' | 'ready';
    approved_by_user_id?: number;
    berita_acara: {
      berita_acara_document_id: string;
      items: { parameter_uji_document_id: string; result: string; result_corrected?: string }[];
    }[];
  },
): Promise<{
  work_order_document_id: string;
  status: 'draft' | 'ready';
  laboratory_result_document_id: string;
}> {
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/work-order/${encodeURIComponent(workOrderDocumentId)}/save`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!data) throw new Error('Invalid save laboratory work order response');
  const statusRaw = String(data.status ?? payload.status);
  return {
    work_order_document_id: String(data.work_order_document_id ?? workOrderDocumentId),
    status: statusRaw === 'ready' ? 'ready' : 'draft',
    laboratory_result_document_id: String(
      data.laboratory_result_document_id ?? '',
    ),
  };
}

/** HTML template secondary data yang disusun di backend (bukan HTML yang sudah disimpan). */
export async function strapiFetchBeritaAcaraSecondaryDataTemplate(
  jwt: string,
  beritaAcaraDocumentId: string,
): Promise<string> {
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/berita-acara/${encodeURIComponent(beritaAcaraDocumentId)}/secondary-data-template`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
  return res.text();
}

export type BiologySecondaryDataTemplates = {
  plankton: string;
  bentos: string;
};

/** HTML plankton dan bentos untuk disisipkan di bawah isi editor, tanpa mengganti konten yang ada. */
export async function strapiFetchSecondaryDataBiologyTemplates(
  jwt: string,
): Promise<BiologySecondaryDataTemplates> {
  const res = await fetch(`${STRAPI_URL}/api/laboratory-results/secondary-data-biology-templates`, {
    headers: strapiAuthHeaders(jwt),
    cache: 'no-store',
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(asRecord(body)?.data);
  return {
    plankton: String(data?.plankton ?? ''),
    bentos: String(data?.bentos ?? ''),
  };
}

export async function strapiSaveBeritaAcaraSecondaryDataHtml(
  jwt: string,
  beritaAcaraDocumentId: string,
  html: string,
): Promise<string> {
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/berita-acara/${encodeURIComponent(beritaAcaraDocumentId)}/secondary-data-html`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ html }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data as Record<string, unknown> | undefined;
  return String(data?.html ?? '');
}

export async function strapiGetLaboratoryWorkOrderPreview(
  jwt: string,
  workOrderDocumentId: string,
): Promise<LaboratoryResultWorkOrderPreviewData> {
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/work-order/${encodeURIComponent(workOrderDocumentId)}/preview`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data;
  if (!data || typeof data !== 'object') throw new Error('Invalid work order preview response');
  return data as LaboratoryResultWorkOrderPreviewData;
}

export async function strapiReviewLaboratoryWorkOrderParameter(
  jwt: string,
  workOrderDocumentId: string,
  payload: {
    berita_acara_document_id: string;
    parameter_uji_document_id: string;
    action: 'update_result' | 'approve' | 'reject';
    result?: string;
    result_corrected?: string;
    review_note?: string;
  },
): Promise<{
  work_order_document_id: string;
  berita_acara_document_id: string;
  parameter: LaboratoryResultBeritaAcaraEntry['parameters'][number] | null;
  request_edit_triggered: boolean;
  testing_status: string | null;
  laboratory_result_status: string | null;
}> {
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/work-order/${encodeURIComponent(workOrderDocumentId)}/parameter-review`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!data) throw new Error('Invalid parameter review response');
  return {
    work_order_document_id: String(data.work_order_document_id ?? workOrderDocumentId),
    berita_acara_document_id: String(data.berita_acara_document_id ?? payload.berita_acara_document_id),
    parameter: (data.parameter as LaboratoryResultBeritaAcaraEntry['parameters'][number] | null) ?? null,
    request_edit_triggered: data.request_edit_triggered === true,
    testing_status: data.testing_status != null ? String(data.testing_status) : null,
    laboratory_result_status:
      data.laboratory_result_status != null ? String(data.laboratory_result_status) : null,
  };
}

export async function strapiGetLaboratoryResultForm(
  jwt: string,
  beritaAcaraDocumentId: string,
): Promise<LaboratoryResultFormData> {
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/berita-acara/${encodeURIComponent(beritaAcaraDocumentId)}/form`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data;
  if (!data || typeof data !== 'object') throw new Error('Invalid laboratory form response');
  return data as LaboratoryResultFormData;
}

export async function strapiSaveLaboratoryResult(
  jwt: string,
  beritaAcaraDocumentId: string,
  payload: {
    approved_by_user_id: number;
    items: { parameter_uji_document_id: string; result: string }[];
  },
): Promise<DynamicEntityRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/berita-acara/${encodeURIComponent(beritaAcaraDocumentId)}/save`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const row = asRecord(body)?.data as Record<string, unknown> | undefined;
  if (!row) throw new Error('Invalid save laboratory result response');
  return normalizeDynamicRow(row);
}

export async function strapiGetLaboratoryResultPreview(
  jwt: string,
  laboratoryResultDocumentId: string,
): Promise<LaboratoryResultPreviewData> {
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/${encodeURIComponent(laboratoryResultDocumentId)}/preview`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data;
  if (!data || typeof data !== 'object') throw new Error('Invalid preview response');
  return data as LaboratoryResultPreviewData;
}

export async function strapiDownloadWorkOrderLhuPdf(
  jwt: string,
  workOrderDocumentId: string,
  opts?: { cacheBust?: boolean },
): Promise<Blob> {
  const qs = opts?.cacheBust ? `?t=${Date.now()}` : '';
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/work-order/${encodeURIComponent(workOrderDocumentId)}/lhu-pdf${qs}`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
  return res.blob();
}

/** Kirim PDF LHU ke satu atau lebih alamat email customer (Customer Service). */
export async function strapiSendWorkOrderLhuPdf(
  jwt: string,
  workOrderDocumentId: string,
  emails: string[],
): Promise<void> {
  const recipients = [...new Set(emails.map((email) => email.trim()).filter(Boolean))];
  if (!recipients.length) throw new Error('Alamat email wajib.');
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/work-order/${encodeURIComponent(workOrderDocumentId)}/lhu-pdf/send`,
    {
      method: 'POST',
      headers: { ...strapiAuthHeaders(jwt), 'Content-Type': 'application/json' },
      body: JSON.stringify({ emails: recipients }),
    },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
}

export async function strapiFetchWorkOrderLhuPdfHtml(
  jwt: string,
  workOrderDocumentId: string,
): Promise<string> {
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/work-order/${encodeURIComponent(workOrderDocumentId)}/lhu-pdf/preview-html`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
  return res.text();
}

/** Buka preview HTML LHU di tab browser baru (untuk development styling). */
export async function strapiOpenWorkOrderLhuPdfHtmlPreview(
  jwt: string,
  workOrderDocumentId: string,
): Promise<void> {
  const tab = window.open('about:blank', '_blank');
  if (!tab) {
    throw new Error('Pop-up diblokir browser. Izinkan pop-up untuk preview HTML.');
  }
  try {
    const html = await strapiFetchWorkOrderLhuPdfHtml(jwt, workOrderDocumentId);
    tab.document.open();
    tab.document.write(html);
    tab.document.close();
  } catch (err) {
    tab.close();
    throw err;
  }
}

export async function strapiGetWorkOrderLhuCustomHtml(
  jwt: string,
  workOrderDocumentId: string,
): Promise<string> {
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/work-order/${encodeURIComponent(workOrderDocumentId)}/lhu-custom-html`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const html = asRecord(asRecord(body)?.data)?.html;
  return typeof html === 'string' ? html : '';
}

export async function strapiSaveWorkOrderLhuCustomHtml(
  jwt: string,
  workOrderDocumentId: string,
  html: string,
): Promise<string> {
  const res = await fetch(
    `${STRAPI_URL}/api/laboratory-results/work-order/${encodeURIComponent(workOrderDocumentId)}/lhu-custom-html`,
    {
      method: 'PUT',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ html }),
      cache: 'no-store',
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const saved = asRecord(asRecord(body)?.data)?.html;
  return typeof saved === 'string' ? saved : '';
}

export const INVOICE_STATUSES = ['created', 'down_payment', 'full_payment'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_TESTING_STATUSES = [
  'pengujian',
  'verifikasi',
  'draft_lhu',
  'verifikasi_lhu',
  'cetak',
  'revisi_lhu',
  'lhu_rilis',
] as const;
export type InvoiceTestingStatus = (typeof INVOICE_TESTING_STATUSES)[number];

export const INVOICE_WORKFLOW_ACTIONS = [
  'approve_gl',
  'reject_gl',
  'down_payment',
  'full_payment',
  'verify_lhu',
  'approve_manajer_teknis',
  'customer_approve_lhu',
  'revisi_lhu',
  'release_lhu',
  'preview_lhu',
  'preview_invoice',
  'preview_proforma',
] as const;
export type InvoiceWorkflowAction = (typeof INVOICE_WORKFLOW_ACTIONS)[number];

export type InvoiceRowActions = Record<InvoiceWorkflowAction, boolean>;

export type StrapiInvoiceGridRow = {
  row_key: string;
  work_order_document_id: string;
  quotation_document_id: string | null;
  invoice_document_id: string | null;
  company_name: string;
  /** Email customer quotation. Kosong berarti customer tidak punya email. */
  customer_email: string;
  /** NPWP customer. Kosong jika tidak ada. */
  customer_npwp: string;
  /** PPN quotation. */
  is_ppn: boolean;
  quotation_number: string;
  job_number: string;
  quotation_request_date: string;
  grand_total: number;
  /** Grand total PDF invoice (setelah PPH). Batas jumlah bayar proforma. */
  pdf_grand_total: number;
  paid_amount: number;
  /** Jumlah bayar proforma. Null bila belum diisi. */
  proforma_amount: number | null;
  invoice_status: InvoiceStatus;
  testing_status: InvoiceTestingStatus;
  is_contract: boolean;
  approved_by_spv_lab: boolean;
  approved_by_spv_admin_sampling: boolean;
  approved_by_spv_admin_lhu: boolean;
  approved_by_manajer_teknis: boolean;
  lhu_revision_note: string | null;
  lhu_released_at: string | null;
  parameters_all_approved: boolean;
  parameters_review: {
    expected_count: number;
    approved_count: number;
    pending_count: number;
    rejected_count: number;
    missing_count: number;
  };
  actions: InvoiceRowActions;
};

function emptyInvoiceRowActions(): InvoiceRowActions {
  return {
    approve_gl: false,
    reject_gl: false,
    down_payment: false,
    full_payment: false,
    verify_lhu: false,
    approve_manajer_teknis: false,
    customer_approve_lhu: false,
    revisi_lhu: false,
    release_lhu: false,
    preview_lhu: false,
    preview_invoice: false,
    preview_proforma: false,
  };
}

function parseInvoiceStatus(v: unknown): InvoiceStatus {
  const s = String(v ?? '').trim();
  if (s === 'down_payment' || s === 'full_payment' || s === 'created') return s;
  return 'created';
}

function parseTestingStatus(v: unknown): InvoiceTestingStatus {
  const s = String(v ?? '').trim();
  if (s === 'lhu_rilis') return 'cetak';
  if ((INVOICE_TESTING_STATUSES as readonly string[]).includes(s)) {
    return s as InvoiceTestingStatus;
  }
  return 'pengujian';
}

function parseInvoiceRowActions(raw: unknown): InvoiceRowActions {
  const rec = asRecord(raw) ?? {};
  const out = emptyInvoiceRowActions();
  for (const key of INVOICE_WORKFLOW_ACTIONS) {
    out[key] = rec[key] === true;
  }
  return out;
}

function mapInvoiceGridRow(row: unknown): StrapiInvoiceGridRow {
  const r = asRecord(row) ?? {};
  const review = asRecord(r.parameters_review) ?? {};
  return {
    row_key: String(r.row_key ?? r.work_order_document_id ?? ''),
    work_order_document_id: String(r.work_order_document_id ?? ''),
    quotation_document_id: r.quotation_document_id ? String(r.quotation_document_id) : null,
    invoice_document_id: r.invoice_document_id ? String(r.invoice_document_id) : null,
    company_name: String(r.company_name ?? ''),
    customer_email: String(r.customer_email ?? '').trim(),
    customer_npwp: String(r.customer_npwp ?? '').trim(),
    is_ppn: r.is_ppn === true,
    quotation_number: String(r.quotation_number ?? ''),
    job_number: String(r.job_number ?? ''),
    quotation_request_date: String(r.quotation_request_date ?? ''),
    grand_total: Number(r.grand_total) || 0,
    pdf_grand_total: Number(r.pdf_grand_total) || Number(r.grand_total) || 0,
    paid_amount: Number(r.paid_amount) || 0,
    proforma_amount: (() => {
      const n = Number(r.proforma_amount);
      return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
    })(),
    invoice_status: parseInvoiceStatus(r.invoice_status),
    testing_status: parseTestingStatus(r.testing_status),
    is_contract: r.is_contract === true,
    approved_by_spv_lab:
      r.approved_by_spv_lab === true || r.parameters_all_approved === true,
    approved_by_spv_admin_sampling: r.approved_by_spv_admin_sampling === true,
    approved_by_spv_admin_lhu: r.approved_by_spv_admin_lhu === true,
    approved_by_manajer_teknis: r.approved_by_manajer_teknis === true,
    lhu_revision_note:
      r.lhu_revision_note != null && String(r.lhu_revision_note).trim()
        ? String(r.lhu_revision_note).trim()
        : null,
    lhu_released_at:
      r.lhu_released_at != null && String(r.lhu_released_at).trim()
        ? String(r.lhu_released_at).trim()
        : null,
    parameters_all_approved: r.parameters_all_approved === true || r.approved_by_spv_lab === true,
    parameters_review: {
      expected_count: Number(review.expected_count) || 0,
      approved_count: Number(review.approved_count) || 0,
      pending_count: Number(review.pending_count) || 0,
      rejected_count: Number(review.rejected_count) || 0,
      missing_count: Number(review.missing_count) || 0,
    },
    actions: parseInvoiceRowActions(r.actions),
  };
}

export type InvoiceGridSort = 'job_number:asc' | 'quotation_request_date:desc';

export async function strapiListInvoiceGridRows(
  jwt: string,
  sort?: InvoiceGridSort,
): Promise<StrapiInvoiceGridRow[]> {
  const qs = sort ? `?${new URLSearchParams({ sort }).toString()}` : '';
  const res = await fetch(`${STRAPI_URL}/api/invoices/grid-rows${qs}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data;
  if (!Array.isArray(data)) return [];
  return data.map((row) => mapInvoiceGridRow(row));
}

const INVOICE_ACTION_PATH: Record<
  Exclude<InvoiceWorkflowAction, 'preview_lhu' | 'preview_invoice' | 'preview_proforma'>,
  string
> = {
  approve_gl: 'approve-gl',
  reject_gl: 'reject-gl',
  down_payment: 'down-payment',
  full_payment: 'full-payment',
  verify_lhu: 'verify-lhu',
  approve_manajer_teknis: 'approve-manajer-teknis',
  customer_approve_lhu: 'customer-approve-lhu',
  revisi_lhu: 'revisi-lhu',
  release_lhu: 'release-lhu',
};

export async function strapiRunInvoiceWorkOrderAction(
  jwt: string,
  workOrderDocumentId: string,
  action: Exclude<InvoiceWorkflowAction, 'preview_lhu' | 'preview_invoice' | 'preview_proforma'>,
  payload?: { paid_amount?: number; revision_note?: string; payment_note?: string },
): Promise<StrapiInvoiceGridRow> {
  const res = await fetch(
    `${STRAPI_URL}/api/invoices/work-order/${encodeURIComponent(workOrderDocumentId)}/${INVOICE_ACTION_PATH[action]}`,
    {
      method: 'POST',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload ?? {}),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(body)?.data;
  if (!data) throw new Error('Invalid invoice action response');
  return mapInvoiceGridRow(data);
}

export type StrapiInvoicePaymentEntry = {
  document_id: string;
  amount: number;
  paid_at: string;
  note: string;
  job_number: string;
  recorded_by_name: string;
};

export type StrapiInvoicePaymentHistory = {
  quotation_document_id: string | null;
  grand_total: number;
  paid_amount: number;
  remaining_amount: number;
  invoice_status: InvoiceStatus;
  payments: StrapiInvoicePaymentEntry[];
};

function mapInvoicePaymentEntry(row: unknown): StrapiInvoicePaymentEntry {
  const r = asRecord(row) ?? {};
  return {
    document_id: String(r.document_id ?? ''),
    amount: Number(r.amount) || 0,
    paid_at: String(r.paid_at ?? ''),
    note: String(r.note ?? ''),
    job_number: String(r.job_number ?? ''),
    recorded_by_name: String(r.recorded_by_name ?? ''),
  };
}

export async function strapiListInvoicePayments(
  jwt: string,
  workOrderDocumentId: string,
): Promise<StrapiInvoicePaymentHistory> {
  const res = await fetch(
    `${STRAPI_URL}/api/invoices/work-order/${encodeURIComponent(workOrderDocumentId)}/payments`,
    { headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
  const data = asRecord(asRecord(body)?.data) ?? {};
  const payments = Array.isArray(data.payments) ? data.payments.map(mapInvoicePaymentEntry) : [];
  return {
    quotation_document_id: data.quotation_document_id ? String(data.quotation_document_id) : null,
    grand_total: Number(data.grand_total) || 0,
    paid_amount: Number(data.paid_amount) || 0,
    remaining_amount: Number(data.remaining_amount) || 0,
    invoice_status: parseInvoiceStatus(data.invoice_status),
    payments,
  };
}

function readErrorGrandTotal(body: unknown): number | null {
  const details = asRecord(asRecord(body)?.error)?.details;
  const n = Number(asRecord(details)?.grand_total);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export class ProformaAmountRejectedError extends Error {
  readonly grandTotal: number | null;

  constructor(message: string, grandTotal: number | null) {
    super(message);
    this.name = 'ProformaAmountRejectedError';
    this.grandTotal = grandTotal;
  }
}

export class InvoicePdfRequestError extends Error {
  readonly grandTotal: number | null;

  constructor(message: string, grandTotal: number | null) {
    super(message);
    this.name = 'InvoicePdfRequestError';
    this.grandTotal = grandTotal;
  }
}

export async function strapiDownloadWorkOrderInvoicePdf(
  jwt: string,
  workOrderDocumentId: string,
  opts?: { cacheBust?: boolean; variant?: 'invoice' | 'proforma' },
): Promise<Blob> {
  const params = new URLSearchParams();
  if (opts?.variant === 'proforma') params.set('variant', 'proforma');
  if (opts?.cacheBust) params.set('t', String(Date.now()));
  const query = params.toString();
  const qs = query ? `?${query}` : '';
  const res = await fetch(
    `${STRAPI_URL}/api/invoices/work-order/${encodeURIComponent(workOrderDocumentId)}/invoice-pdf${qs}`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new InvoicePdfRequestError(parseErrorMessage(errBody, res.status), readErrorGrandTotal(errBody));
  }
  return res.blob();
}

export async function strapiOpenWorkOrderInvoicePdfHtmlPreview(
  jwt: string,
  workOrderDocumentId: string,
  opts?: { variant?: 'invoice' | 'proforma' },
): Promise<void> {
  const qs = opts?.variant === 'proforma' ? '?variant=proforma' : '';
  const res = await fetch(
    `${STRAPI_URL}/api/invoices/work-order/${encodeURIComponent(workOrderDocumentId)}/invoice-pdf/preview-html${qs}`,
    { headers: strapiAuthHeaders(jwt), cache: 'no-store' },
  );
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as unknown;
    throw new Error(parseErrorMessage(errBody, res.status));
  }
  const html = await res.text();
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const tab = window.open(url, '_blank', 'noopener,noreferrer');
  if (!tab) {
    URL.revokeObjectURL(url);
    throw new Error('Pop-up diblokir browser. Izinkan pop-up untuk preview HTML.');
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function strapiSaveWorkOrderProformaAmount(
  jwt: string,
  workOrderDocumentId: string,
  paidAmount: number,
): Promise<number> {
  const res = await fetch(
    `${STRAPI_URL}/api/invoices/work-order/${encodeURIComponent(workOrderDocumentId)}/proforma-amount`,
    {
      method: 'POST',
      headers: {
        ...(strapiAuthHeaders(jwt) as Record<string, string>),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ paid_amount: paidAmount }),
    },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new ProformaAmountRejectedError(parseErrorMessage(body, res.status), readErrorGrandTotal(body));
  }
  const amount = Number(asRecord(asRecord(body)?.data)?.proforma_amount);
  return Number.isFinite(amount) ? amount : paidAmount;
}

export async function strapiClearWorkOrderProformaAmount(
  jwt: string,
  workOrderDocumentId: string,
): Promise<void> {
  const res = await fetch(
    `${STRAPI_URL}/api/invoices/work-order/${encodeURIComponent(workOrderDocumentId)}/proforma-amount`,
    { method: 'DELETE', headers: strapiAuthHeaders(jwt) },
  );
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
}

